import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
// Using simple text icons instead of react-icons
import {
  fetchQuotationDetail,
  updateQuotation,
  downloadQuotationPDF,
  downloadQuotationExcel,
  quotationDownloadErrorMessage,
  updateQuotationItemVendor,
  previewQuotationItemVendorPick,
  confirmLineMatch,
  type SupplierPickPricingPayload,
  sendQuotation,
  sendQuotationErrorMessage,
  rejectedRecipientsMessage,
  notSendableMessage,
  lookupItemPrice,
  lookupItemPriceErrorMessage,
  fetchQuotationCatalogueRows,
  removeQuotationCatalogueRow,
  resolveQuotationCatalogueReprice,
  markClientAmendmentReviewed,
  spreadQuotationCharges,
  undoQuotationChargeSpread,
  type ClientAmendmentDto,
  type QuotationConfirmedOrderDto,
  type QuotationDetailDto,
  type HarvestedCatalogueRow,
  type QuotationItemDto
} from '../lib/quotationApi';
// D45.1 / D45.2: the changes a client asked for by email, and the compare view for a version.
import { ClientAmendmentBanner } from '../features/quotations/ClientAmendmentBanner';
import { isPackAmbiguousLine, PACK_AMBIGUOUS_TIP } from '../features/quotations/packAmbiguity';
import { OrderedQuotationNote } from '../features/quotations/OrderedQuotationNote';
import { RevisionCompareModal } from '../features/quotations/RevisionCompareModal';
import { getExchangeRate, getVatRate, getCompetitorMarkupWarn } from '../lib/settingsApi';
import { ceilPrice, currencyPrefix, positiveNumber } from '../utils/priceUtils';
// The quotation money rules, from the MIRRORED twin of src/utils/pricing.js — not a
// private copy of them. This page owned that copy until 2026-08-27: `resolveMarkupPct`
// + `resolveLineSelling`, whose own comment claimed to mirror the server owner. They
// did not. Both sides divided a line's markup back out of its stored selling price,
// and the two divisions disagreed — the grid to 1dp against a `ceilPrice`d cost, the
// export to 2dp against the raw one — so one line read 30.2% on screen and 30.05% in
// the PDF, and neither was the rate anybody had set.
//
// `resolveItemPricing` answers what a line IS quoted at; `markupToApply` answers what
// rate to re-price it by. Different questions — see the twin's headers before using
// either in the other's place.
import {
  DEFAULT_MARKUP_PERCENT,
  resolveItemPricing,
  exactCostText,
  isPricedAtStatedRate,
  markupCostUsd,
  resolveMarkupPercent,
  markupToApply,
  priceLineAt,
  lockedUnitSellingPrice,
  sellingPriceAt,
  resolveQuotationTotals,
  chargesFromDoc,
  unitCostTtd,
  totalCostTtd,
  sumCostTtd,
  resolveProfitTtd,
  QuotationLineTotals,
  ProfitTtd,
} from '../utils/pricing';
import { shownProfitPercents } from '../features/quotations/shownProfitPercents';
import { statedSellingAfterEdit, lockedUnitRefusal, isLockedAtStatedPrice, isLockedLineError, LOCKED_LINE_UNLOCK_MESSAGE } from '../features/quotations/statedSelling';
// TT$<->US$ display conversion at this screen's pinned rate
// (BUSINESS_RULES#usd-cost-and-ttd-display) — client-only, not part of the
// mirrored pricing.ts (see that file's own header for why it lives here).
import { costToUsd, usdToTtd } from '../utils/fxDisplay';
import { LineNoteCell } from '../features/quotations/LineNoteCell';
import { perLbOrInner } from '../features/quotations/perLbOrInner';
import { orderedActions } from '../features/quotations/orderedActions';
import { quotationRfqFactRows, quotationTabTitle, quotationVesselName, type HeaderRfq } from '../features/quotations/quotationHeader';
// The Competitor's Total box is a client-only rule and lives beside pricing.ts, not in it —
// that file is mirrored against the server's and this half has no server twin.
import { competitorPriceOf, resolveCompetitorSummary, competitorMarkupPercent, isBelowCompetitorWarn } from '../utils/competitorSummary';
// The words for "Spread charges into prices" (the spread itself is the server's `utils/chargeSpread`, D170.5).
import { spreadLockedReason, spreadRefusalMessage } from '../features/quotations/chargeAbsorption';
// The TT$ half of a Pricing Summary row, and the rows that say what the spread
// built into the prices.
import { SummaryTtdCell } from '../features/quotations/SummaryTtdCell';
import { AbsorbedChargesRows } from '../features/quotations/AbsorbedChargesRows';
import { customExpensesAfterEdit, customExpensesAfterInsert, shownCustomExpenses } from '../features/quotations/shownCustomExpenses';
// `convertQuantity` and `roundQuantity` moved out with `restateQuantity` — this
// file now asks that function the whole question instead of doing the
// arithmetic itself.
import { LITRE_PER_UNIT, applyPackagingRemark, convertUnitPrice, convertibleUnits } from '../utils/packConversion';
import { canonicalUnit, isSameUnit } from '../utils/unitVocabulary';
import { mapCatalogueSourceToPricingSource } from '../utils/catalogueSource';
import {
  barredByWater,
  establishedUnits,
  getAlternateQuotationPricing,
  isEquivalentUnitFamily,
  isFractionalWholeCount,
  parsePackSizeFromDescription,
  pricingDetailsAfterFieldEdit,
  restateQuantity,
  restateQuantityThroughPack,
  restateSellingPrice,
} from '../utils/quotationUnitConversions';
import { readConversionAnchor, readPackStructure, targetUnitPriceFor } from '../features/quotations/unitChangeTarget';
import { markupEditProblem, quantityEditProblem, unitCostEditProblem } from '../features/quotations/lineEditChecks';
import { lookupNoPriceReason } from '../features/quotations/lookupNoPriceReason';
import ExportDropdown from '../components/ExportDropdown';
import LoadingState from '../components/LoadingState';
import ClientViewPopup from '../components/ClientViewPopup';
import SendQuotationModal, { SendQuotationData } from '../components/SendQuotationModal';
import { EMAIL_WENT_NOTICE_MS, emailAlreadyWent } from '../lib/emailRefusal';
import type { SupplierDropdownItem } from '../lib/vendorInvoiceApi';
// The SAME fetch `ItemLookupPanel` itself uses — re-run once a pending
// supplier-dropdown pick's "Does this item have VAT?" is answered
// (`handlePendingSupplierVatPickAnswer`'s own comment has why).
import { fetchSupplierDropdownDataFromAllTables } from '../lib/vendorInvoiceApi';
import { SupplierCell } from '../features/quotations/SupplierCell';
import { storedPriceDate } from '../features/quotations/SupplierDetailsCard';
// The link on every line to the catalogue rows it could be priced from, with the
// row that priced it highlighted (BUSINESS_RULES#quotation-line-catalogue-options).
import { CatalogueOptionsLink } from '../features/quotations/CatalogueOptionsLink';
// What a catalogue search pick replaces on a line beside the money — the same
// lists the server's supplier pick retires (utils/quotationLinePricingFacts) —
// and what it leaves the line's own NAME alone (2026-09-22 client call, JP /
// Deborah: the vessel's wording and requested quantity are always kept).
import {
  pricingDetailsForCataloguePick,
  itemNameAfterCataloguePick,
  catalogueFlagsAfterCataloguePick,
  costVatAgreesWithLoadedRate,
  VAT_RATE_CHANGED,
  VAT_RATE_CHANGED_MESSAGE,
  catalogueSearchPickRowName,
} from '../features/quotations/cataloguePickDetails';
// The ONE owner of "apply a resolved price to a line" — the catalogue pick's
// own tail, extracted so the catalogue-addition lookup panel's typed-price
// path prices a line exactly the same way instead of growing a second copy.
// `ConversionAnchor` / `anchorFromItem` / `normalizeUnit` / `positiveNumber`
// moved out with it; see that file's header for why.
import {
  applyLinePriceFacts,
  anchorFromItem,
  normalizeUnit,
  ttdCostEdit,
  costFieldsAfterEdit,
  catalogueCostFacts,
  type CostEdit,
} from '../features/quotations/linePriceApplication';
import { formatPricingSource } from '../features/quotations/pricingSourceLabel';
import { supplierOptionRefusal, supplierPickErrorMessage } from '../features/quotations/supplierOptionPrice';
// The grid's editable cell, lifted out of this file so its save path has a test
// (ClickUp 14yj85ny3x6). Same component, same props. See the file for why it is
// not components/EditableCell.tsx.
import { EditableCell } from '../features/quotations/QuotationEditableCell';
// The merged catalogue+supplier lookup panel (2026-09-23) — replaces the old
// magnifier popup (`components/MasterCatalogueSearchPopup.tsx`) and the
// Supplier cell's own dropdown (`components/SupplierDropdown.tsx`), both
// deleted. `CataloguePriceSelection` moved with the merge to `itemLookupRows`,
// the panel's pure owner — see that file's header for why.
import { ItemLookupPanel } from '../features/quotations/ItemLookupPanel';
// D107: a line with no real vendor gets no hand-typed cost - its cost cells open this popup.
import { AddLineToCatalogueModal, lineCatalogueName } from '../features/quotations/AddLineToCatalogueModal';
import { CostNeedsVendorCell } from '../features/quotations/CostNeedsVendorCell';
import { isLineCostNeedsVendorError, lineHasRealVendor, LINE_COST_NEEDS_VENDOR_MESSAGE } from '../features/quotations/lineVendor';
import type { CataloguePriceSelection } from '../features/quotations/itemLookupRows';
// The one owner of "what does this catalogue row cost per unit X" — the same
// lookup the panel itself used to price the option just clicked. Reused by
// the whole-number conversion rule's fallback (owner, 2026-09-24): when a
// picked unit is refused, the line is repriced at the unit it was actually
// asked in, off the SAME row, never guessed.
import { priceAtUnit } from '../features/quotations/itemLookupRows';
// The before -> after table shown inside the in-app confirm for a catalogue
// pick (owner decisions 2026-09-24) — reads the exact line the pick is about
// to write, never a second price computation. See that file's header.
import { LinePickPreview, pickChangesNothing } from '../features/quotations/LinePickPreview';
// The SAME re-derivation the lookup panel itself used to build a
// `priceSelection` in the first place, re-run for the row once "Does this
// item have VAT?" answers it — `handlePendingCataloguePickAnswer`'s own
// comment (below) has the bug this closes.
import { repriceApplyConfirm } from '../features/quotations/repriceConfirm';
import { priceCataloguePickForVatAnswer } from '../features/quotations/pendingCataloguePickVat';
// The supplier-dropdown pick's own version of the above — re-fetches rather
// than re-deriving a price, since there is no `MasterCatalogueItem` row in
// memory for a `SupplierDropdownItem` pick. See its own header for the bug
// this closes.
import { resolvePendingSupplierVatPick } from '../features/quotations/pendingSupplierVatPick';
// "Is this catalogue row's price stated in TT$?" — one owner, shared with the
// Master Catalogue screen and the search popup, because the three answered it
// differently and the difference is a factor of 6.5 on a line's cost.
import {
  catalogueRowCostPrice,
  catalogueRowIsTtd,
  catalogueRowNeedsUnsetVatRate,
} from '../features/catalogue/costPriceTiers';
import { setCatalogueVat, getMasterCatalogueItem } from '../lib/masterCatalogueApi';
import type { MasterCatalogueItem, CreateCatalogueItemPayload } from '../lib/masterCatalogueApi';
// "Does this item have VAT?" (owner decision 2026-09-24, "one Trinidad VAT
// rate") — a blank catalogue row is priced without VAT until this answers it,
// from a catalogue pick or the line's own "VAT?" badge. The orchestration
// (`answerCatalogueVatPrompt`) is dependency-injected and tested on its own;
// this page supplies the real API calls and its own state.
import { CatalogueVatPrompt } from '../features/quotations/CatalogueVatPrompt';
import { answerCatalogueVatPrompt } from '../features/quotations/answerCatalogueVatPrompt';
// The catalogue-addition review queue's submit call, and the marks a pending
// or rejected submission leaves on the line it was raised from — the lookup
// panel's "+ Add this item to Master Catalogue"
// (BUSINESS_RULES#catalogue-addition-review). Nothing about a submission is
// denormalized onto the quotation line itself; see
// `useCatalogueSubmissionMarkers`'s own header.
import { submitCatalogueAddition } from '../lib/catalogueSubmissionsApi';
import { useCatalogueSubmissionMarkers } from '../features/quotations/useCatalogueSubmissionMarkers';
import { addCatalogueItemFromLine } from '../features/quotations/addCatalogueItemFromLine';
// Owner decision 2026-09-24: flag a line quoted in a different unit than the
// RFQ asked for, and nudge (never block) on Send. `findRfqUnitMismatch` /
// `collectRfqUnitMismatches` are the one owner of that comparison — see the
// module header for why it reads `originalRequest` directly rather than
// through `readOriginalRequest` above.
import { collectRfqUnitMismatches, findRfqUnitMismatch, describeQuotedAmount, nextMismatchLine } from '../features/quotations/rfqUnitMismatch';
import { RfqUnitMismatchDialog } from '../features/quotations/RfqUnitMismatchDialog';
// D99 item 4: warn, never block, about catalogue matches nobody has confirmed.
import { LineMatchActions } from '../features/quotations/LineMatchActions';
import { MatchReviewBanner } from '../features/quotations/MatchReviewBanner';
import { confirmRefusalMessage, linesToReview, matchTooltip, mergeLookupMatch, pinnedCatalogueRowId, pricingDetailsAfterRefresh, type ReviewEntry } from '../features/quotations/lineMatchReview';
import { needsConfirmation, canConfirmMatch, lineIsUnpriced } from '../utils/matchReasons';
import { isFrozenStatus } from '../utils/quotationFrozen';
import { manualConfirmOrder, confirmOrderErrorMessage } from '../lib/confirmedOrderApi';
// What stops this quotation becoming a confirmed order. A convenience mirror of
// the server's gate (`confirmedOrder.service`), shared through a mirrored twin
// so the button can say WHY before it is pressed — never the only gate
// (ClickUp 86d478wf8).
import { confirmationBlockers, describeConfirmationBlockers, sendBlockers } from '../utils/quotationConfirmationRules';
// D51 — does each line still state what the vessel asked for, in its own unit
// (QUO-00054). The server refuses Finalize / Send / Confirm on the same rule.
import {
  LINE_AMOUNT_STATUS,
  blocksQuotation,
  describeLineAmount,
  // F5 (2026-09-25 audit): may the "use the ask" chip write `expected`
  // straight in, or must it offer the other two D51 remedies instead — see
  // handleUseAskedAmount and the D51 chip rendering below.
  expectedIsWholeCount,
  lineAmountBlockers,
  lineAmountCheck,
} from '../utils/quotationLineAmount';
import { useAuth } from '../context/AuthContext';
// Column order and widths for the items grid — draggable headers and resize
// grips (ClickUp 86d478wd1). The cells below stay in canonical source order;
// these apply the user's layout to them.
import {
  QuotationGridHeaderCells,
  QuotationGridRowCells,
} from '../features/quotations/QuotationGridCells';
import { templateColumnsFor } from '../features/quotations/gridLayout';
// What is on screen, what this user saved, and Reset / Save between them.
import { useQuotationGridLayout } from '../features/quotations/useQuotationGridLayout';
import { QuotationGridLayoutButtons } from '../features/quotations/QuotationGridLayoutButtons';
import ErrorModal from '../components/ErrorModal';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import Button from '../components/ui/Button';
import BackArrow from '../components/ui/BackArrow';
import { GlobalMarkupBar } from '../features/quotations/GlobalMarkupBar';
import { Maximize2 } from 'lucide-react';
// The cell-highlight vocabulary, shared with the vendor-invoice grid. This page
// used to hard-code its own Tailwind strings at six sites in five colours; two
// of them existed nowhere else, and its sky read as the invoice grid's blue
// while meaning the opposite. See `CellHighlightLegend` for the whole story.
import { flagCellClass } from '../utils/lineItemFlags';
import { CellHighlightLegend } from '../components/CellHighlightLegend';
import { ChainRail } from '../features/traceability/ChainRail';
import { AddQuotationItemModal } from '../components/AddQuotationItemModal';
import { formatDateTime as fmtDateTime } from "../utils/dateFormat";

// Sticky scrollbar styles — keep both axes scrollable and show a slim, themed scrollbar
// (was previously zero-sized, which hid the bar so users couldn't tell horizontal scroll
// existed). The footer renders the scrollbar so it stays visible while scrolling vertically.
const stickyScrollbarStyles = `
  .sticky-scrollbar {
    position: relative;
    overflow-x: auto;
    overflow-y: auto;
    scrollbar-color: #cbd5e1 #f1f5f9;
    scrollbar-width: thin;
  }
  .sticky-scrollbar::-webkit-scrollbar {
    width: 10px;
    height: 12px;
  }
  .sticky-scrollbar::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }
  .sticky-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;
    border: 2px solid #f1f5f9;
  }
  .sticky-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  .sticky-scrollbar::-webkit-scrollbar-corner {
    background: #f1f5f9;
  }

  /* Borderless cell-integrated unit select — looks flat in the cell, hover/focus reveal affordance */
  .quotation-cell-select {
    width: 100%;
    height: 28px;
    border: 1px solid transparent;
    border-radius: 6px;
    background-color: transparent;
    padding: 0 24px 0 8px;
    font-size: 12px;
    line-height: 16px;
    color: #2B2B2B;
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
    text-align: center;
    text-align-last: center;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23667085' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 6px center;
    transition: background-color 120ms ease, border-color 120ms ease;
  }
  .quotation-cell-select:hover {
    background-color: #F4F6F5;
    border-color: #E4E6E5;
  }
  .quotation-cell-select:focus {
    outline: none;
    border-color: #0A6AAC;
    box-shadow: 0 0 0 2px rgba(26, 181, 111, 0.15);
  }
`;

const QUOTATION_DETAIL_INFO_CONTENT = (
  <div className="flex flex-col gap-3">
    <p>This is the detail view of a quotation. You can edit items here, and export or send to the client.</p>
    <p className="font-medium">You can:</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li>Edit item descriptions, quantities, unit prices, and markup</li>
      <li>Equiv. Cost Price per KG and per lb / inner columns show derived cost prices from pack text (e.g. 20×500g) or when the unit is already per kg</li>
      <li>Drag and resize columns, then Save columns to see that layout on every quotation you open</li>
      {/* This line used to name the four statuses and imply you could pick one
          here. There has never been a status control on this page — it is on
          the quotations list, and it is the only route into freezing a
          quotation. TECH_DEBT #260. */}
      <li>
        Change the status from the <span className="font-medium">Quotations list</span> — a quotation
        can only be marked Finalized or Sent once every line has a cost
      </li>
      <li>Export to PDF or Excel, view Client View, or Send Quote</li>
      <li>Confirm order when the client has accepted (Sent/Finalized)</li>
    </ul>
    {/* The grid highlighted cells in five colours and explained none of them:
        the only way to find out why a cell was coloured was to hover it, and
        the only way to know hovering would tell you was to already know. Same
        three colours, same meanings and the same swatches as the vendor-invoice
        grid, so what is learned on one screen holds on the other. */}
    <p className="font-medium">Highlighted cells</p>
    <p>A cell is highlighted only when it needs a look. Everything else is what we quoted.</p>
    <CellHighlightLegend
      items={[
        {
          level: 'error',
          meaning: (
            <>
              missing — this item is not in the Master Catalogue, so nothing here could price it on
              its own. Nothing to do: when this quotation's status becomes{' '}
              <span className="font-semibold">Finalized</span>,{' '}
              <span className="font-semibold">Sent</span> or{' '}
              <span className="font-semibold">Rejected</span> (from the status dropdown on the
              Quotations list), every pink line that has a Unit Cost is added to the Master
              Catalogue automatically, so the next quotation can price it. A line still showing no
              cost is skipped rather than stored at zero, and what was added is listed above the
              grid where you can remove any of it.
            </>
          ),
        },
        {
          level: 'warn',
          meaning: (
            <>
              check it — the vessel asked for 0, or we quoted in a different unit than was asked
              because no pack size is on file, or the markup no longer matches the price. The
              quoted figure stands; it just is not the one the column claims.
              On a line name it means the catalogue match is a guess nobody has confirmed (a
              substitute, a different pack or size, or a loose name match): hover the name for why,
              then use Confirm or Change match. You can still send.
            </>
          ),
        },
        {
          level: 'manual',
          meaning: (
            <>
              a person set this — the line was restated into another unit, or what we quoted
              differs from what the RFQ asked for. Correct, not a warning.
            </>
          ),
        },
      ]}
      footer="Hover any highlighted cell to see exactly why."
    />
  </div>
);

/** Appended to unit cost prices, e.g. ` / pack`, ` / kg` (matches line UOM). */
const unitCostPriceSuffix = (uom?: string | null): string => {
  const u = String(uom ?? '').trim();
  return ` / ${u || 'unit'}`;
};

/**
 * The units a quotation line can be re-expressed in.
 *
 * Deliberately short. The old list offered EA, UNIT, PACK, BOX, CARTON, BAG and
 * TIN beside CASE, which are not different questions — they are seven names for
 * "the outer thing this ships in", and every one of them made the dropdown
 * longer without making a single conversion possible that CASE did not already
 * cover. A line whose reference row is sold by one of those names still gets it
 * offered, by that name, from `outerUomLabel` below, and a line already quoted
 * in one keeps it: nothing that exists today loses its own unit.
 *
 * G and ML are NOT here, and it is not an oversight. Every price in this system
 * is quantised to the cent (`ceilPrice`), so a true US$0.001/g becomes US$0.01/g
 * — a tenfold overcharge that compounds over a 50,000 g line. Per-kg and per-lb
 * carry the same information at a precision the money model can actually hold.
 *
 * GAL IS here, since the owner fixed a gallon at four litres (2026-09-14): the
 * pack conversion holds that ratio now, and a per-gallon price is larger than a
 * per-litre one, so the cent loses nothing.
 */
const INDUSTRY_UNIT_OPTIONS = ['CASE', 'PCS', 'KG', 'LB', 'OZ', 'L', 'GAL'] as const;
// `normalizeUnit` moved to `features/quotations/linePriceApplication.ts`
// (imported above) — it is a unit normalizer, which CLAUDE.md names by name
// as the pattern never to duplicate, and `anchorFromItem` there needs the
// exact same one this page uses everywhere else.

// `costToUsd`/`usdToTtd` (BUSINESS_RULES#usd-cost-and-ttd-display) — the
// TT$<->US$ display conversion at this screen's pinned rate. Extracted to
// `utils/fxDisplay.ts` 2026-09-22 (one-owner audit, defect #4) so
// `ClientInvoicePage.tsx` could call the owner instead of growing a second
// private copy; behaviour is unchanged, see that file for the full contract.

// Every money label on this screen, from the owner:
// BUSINESS_RULES#quotation-screen-money-labels. Each figure keeps its own
// number formatting (`toFixed(2)`, ceiled or not as it was) — only the label
// is shared, so a change to the label rule reaches every cell here at once.
const USD_LABEL = currencyPrefix('USD');
const TTD_LABEL = currencyPrefix('TTD');

/**
 * One Item Profit cell: the line's profit in US$ and in TT$, both beside the
 * ONE percentage `shownProfitPercents` picks. The grid has two of these —
 * `basis` picks profit ÷ cost (markup realised) or profit ÷ final selling price
 * (margin) — and both read the same owners as the Pricing Summary:
 * `resolveQuotationTotals` for US$, `resolveProfitTtd` for TT$. A percentage
 * with no positive denominator reads "—", never 0% or Infinity.
 */
const ItemProfitCell: React.FC<{
  usd: QuotationLineTotals | undefined;
  ttd: ProfitTtd | null;
  basis: 'cost' | 'selling';
}> = ({ usd, ttd, basis }) => {
  if (!usd || (usd.cost <= 0 && usd.netSellingPrice <= 0)) {
    return <span className="text-gray-400">-</span>;
  }
  const shown = shownProfitPercents(usd, ttd);
  const [percent, base] = basis === 'cost' ? [shown.ofCost, shown.costBase] : [shown.ofSelling, shown.sellingBase];
  const pct = base > 0 ? `${percent.toFixed(1)}%` : '—';
  const row = (currency: string, profit: number) => (
    <span className={profit >= 0 ? 'text-primary-500' : 'text-[#CC4B13]'}>
      {profit < 0 ? '-' : ''}
      {currency}
      {Math.abs(profit).toFixed(2)} ({pct})
    </span>
  );
  return (
    <div
      className="flex flex-col items-center leading-[16px]"
      title={`${basis === 'cost' ? 'Profit against cost price (profit ÷ cost)' : 'Profit against selling price (profit ÷ final selling price)'}, after the discount — the % is worked out in TT$`}
    >
      {row(USD_LABEL, usd.profit)}
      {!ttd ? (
        // Null for a missing rate OR a line with no quantity — not only the rate.
        <span className="text-gray-400" title="TT$ figure unavailable — needs an exchange rate and a quantity">{TTD_LABEL} —</span>
      ) : (
        row(TTD_LABEL, ttd.profit)
      )}
    </div>
  );
};

/**
 * One option per unit, however many names that unit goes by.
 *
 * The Unit dropdown's list is a union of two vocabularies: `normalizeUnit` here
 * only upper-cases, so a line stored as LB stays LB, while `convertibleUnits`
 * answers in `canonicalUnit`'s spelling, which folds LB → LBS. A pack-bearing
 * line quoted in pounds was therefore offered both — the same unit twice, one
 * of them the unit the line is already in, and picking either converted nothing
 * (`targetUnitPriceFor` returns the current price for an equivalent family).
 *
 * Folded on that same predicate rather than on string identity, so the list and
 * the click agree about what counts as a different unit — the same property the
 * rest of this list construction exists to hold. The FIRST spelling wins: the
 * line's own unit leads every list, so the survivor is the one
 * `<select value={currentUomNormalized}>` is matched against, and a row can
 * never be left with its own unit missing from its own dropdown.
 *
 * The stored `uom` is never canonicalised to fix this. That would restate every
 * row's unit for a display reason. (TECH_DEBT #154.)
 */
const dedupeUnitOptions = (units: string[]): string[] => {
  const kept: string[] = [];
  for (const unit of units) {
    if (!unit) continue;
    if (kept.some((already) => isEquivalentUnitFamily(already, unit))) continue;
    kept.push(unit);
  }
  return kept;
};

// `ConversionAnchor` / `positiveNumber` / `anchorFromItem` moved to
// `features/quotations/linePriceApplication.ts` (imported above), WITH the
// price-application tail that anchors a line — see that file's header for
// the QUO-00011 / QUO-00054 history and why the anchor now has one owner
// shared by the catalogue pick and the manual catalogue-addition entry.

/**
 * A part-pack round-up that a human actually chose (TECH_DEBT #150).
 *
 * Restating a line can land on 2.5 CASE — honest, total-preserving, unshippable.
 * `roundQuantity` refuses to snap it because rounding up is a COMMERCIAL
 * decision: 3 cases is 20% more money and 5 more pieces than the vessel asked
 * for. So the grid offers it as a click, and the click is recorded here rather
 * than left as a bare 3 that reads like it was always 3. It is what a reviewer
 * asking "why does this say 3 when the RFQ said 25 PCS" has to go on, and it is
 * the only trace once the conversion anchor is dropped — which a quantity edit
 * does, deliberately, because a chosen count IS the new baseline.
 *
 * Dropped wherever the anchor is dropped and on any unit change, so it can only
 * ever describe the row as it currently stands.
 */
type PartPackRounding = {
  /** The fractional count the restatement produced. */
  from: number;
  /** The whole count that was quoted instead. */
  to: number;
  /** The unit both are counted in — the record is stale the moment this moves. */
  uom: string;
  /** What the line was established as before the restatement, when it was known. */
  requested: { quantity: number; uom: string } | null;
  /**
   * Where `requested` came from. 'rfq' is the durable generation-time record
   * (TECH_DEBT #151); 'anchor' is the line's last established baseline, which is
   * all that was available before that record existed and all that is available
   * on a hand-added line. Absent on every record written before 2026-08-27, and
   * those are anchors — the wording has to differ, because only one of the two
   * is evidence of what the vessel wrote.
   */
  requestedSource: 'rfq' | 'anchor';
  at: string | null;
};

const readPartPackRounding = (item: QuotationItemDto): PartPackRounding | null => {
  const pd = item.pricingDetails as Record<string, unknown> | undefined | null;
  const r = pd && typeof pd === 'object' ? (pd.partPackRounding as Record<string, unknown> | undefined) : undefined;
  if (!r || typeof r !== 'object') return null;
  const from = positiveNumber(r.from);
  const to = positiveNumber(r.to);
  const uom = typeof r.uom === 'string' && r.uom.trim() ? normalizeUnit(r.uom) : null;
  if (from == null || to == null || !uom) return null;
  const req = r.requested as Record<string, unknown> | undefined | null;
  const reqQty = req && typeof req === 'object' ? positiveNumber(req.quantity) : null;
  const reqUom = req && typeof req === 'object' && typeof req.uom === 'string' ? normalizeUnit(req.uom) : null;
  return {
    from,
    to,
    uom,
    requested: reqQty != null && reqUom ? { quantity: reqQty, uom: reqUom } : null,
    requestedSource: r.requestedSource === 'rfq' ? 'rfq' : 'anchor',
    at: typeof r.at === 'string' ? r.at : null,
  };
};

/**
 * What the VESSEL asked for on this line — the durable record written at
 * generation and never rewritten (TECH_DEBT #151).
 *
 * The distinction this exists to make: `conversionAnchor` is what the line was
 * last ESTABLISHED as, and a hand edit to Quantity or Unit Cost replaces it on
 * purpose (otherwise the next unit change would restate the correction away).
 * So the anchor silently stops being provenance the moment anybody corrects the
 * row — which is exactly when a reviewer asking "did we quote what was asked
 * for?" most needs an answer. This one cannot move.
 *
 * Null for lines generated before 2026-08-27 and for lines added by hand, where
 * the anchor remains the best available answer and callers say so in different
 * words rather than passing one off as the other.
 */
const readOriginalRequest = (item: QuotationItemDto): { quantity: number; uom: string | null } | null => {
  const req = item.originalRequest;
  if (!req || typeof req !== 'object') return null;
  const quantity = typeof req.quantity === 'number' && Number.isFinite(req.quantity) ? req.quantity : null;
  if (quantity == null) return null;
  const uom = typeof req.uom === 'string' && req.uom.trim() ? normalizeUnit(req.uom) : null;
  return { quantity, uom };
};

/**
 * Is this row CURRENTLY sitting on a fractional count of a unit that COUNTS
 * things — 2.5 CASE, 1.5 BAG — whatever put it there?
 *
 * TECH_DEBT #150 used to offer a click here that quoted the next whole pack
 * instead (`handleRoundUpPartPack`, removed). Owner, 2026-09-24: "the
 * round-up thing is wrong" — no rounding, up or to-nearest, ever applied on
 * this screen. What is left is a FLAG: the Quantity cell reddens and says so,
 * and the reviewer fixes it by hand (switch the unit, or type a whole
 * number). `isFractionalWholeCount` is the one owner, shared with the
 * server-side whole-number conversion rule so "is this unit one you cannot
 * have half of" is answered once (`unitVocabulary.isWholeCountUnit` —
 * BAG/DOZ/SET count for this, owner 2026-09-24, though not for pricing).
 */
const fractionalQuantityOf = (item: QuotationItemDto): { quantity: number; uom: string } | null => {
  const uom = normalizeUnit(item.uom || 'PCS');
  if (!isFractionalWholeCount(item.quantity, uom)) return null;
  const qty = typeof item.quantity === 'number' ? item.quantity : parseFloat(String(item.quantity ?? ''));
  return { quantity: qty, uom };
};

/**
 * What a failed catalogue pick says — from the server's CODE, never its
 * sentence (CLAUDE.md invariant 6, D64.10 B). One wording for the pick and
 * both of its VAT-answer paths.
 */
const replaceItemErrorMessage = (err: any): string =>
  err?.code === 'QUOTATION_NOT_FOUND'
    ? 'This quotation could not be found.'
    : err?.code === VAT_RATE_CHANGED
      ? VAT_RATE_CHANGED_MESSAGE
      : isLockedLineError(err)
        ? LOCKED_LINE_UNLOCK_MESSAGE
        : isLineCostNeedsVendorError(err)
          ? LINE_COST_NEEDS_VENDOR_MESSAGE
          : lookupItemPriceErrorMessage(err, 'Failed to replace item. Please try again.');

export const QuotationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Who marked a line's amount right (D51) — recorded on the line beside it.
  const { user } = useAuth();
  const [quotation, setQuotation] = useState<QuotationDetailDto | null>(null);
  // Ref mirror of `quotation`, updated the instant a save of this quotation's
  // items lands — never only by React's own re-render, which can lag a second
  // save queued immediately after the first. Same reasoning as
  // `confirmingOrderRef` below, applied to the whole quotation instead of one
  // flag.
  const quotationRef = useRef<QuotationDetailDto | null>(quotation);
  useEffect(() => {
    quotationRef.current = quotation;
  }, [quotation]);
  // Every save of this quotation's `items` goes through here (QA retest item
  // 1, 2026-10-09, D140): each of this file's ~9 items-save call sites used to
  // build its own full `items` array off the `quotation` STATE captured in
  // its own closure, then PUT the whole array — fine for one save at a time,
  // but two edits fired close together (type a quantity on one line, then
  // another before the first PUT's response had updated state) each built
  // their array from the SAME stale snapshot, so whichever PUT's response
  // landed last silently discarded the other edit (QUO-00252: "Bacon
  // reverted to 6 after reload, Lamb stayed" — reproduced 2 of 2).
  //
  // Closed two ways: `quotationRef` above always holds the freshest known
  // quotation, read HERE instead of from any one handler's own closure; and
  // `itemsSaveQueueRef` chains every save after whichever is already in
  // flight, so two saves fired back to back run one after the other, the
  // second always rebased onto the first's result — never both built from
  // the same starting snapshot. The server's PUT contract (a full `items`
  // array) is unchanged; nothing here is a version token or a per-line PATCH
  // — see D140 for why that wider redesign was not needed to close this.
  //
  // Every caller still gets the exact `updateQuotation` response it always
  // did (success, a `{success:false}` envelope, or a thrown error), so each
  // one's own error handling, toasts and post-save logic are untouched; only
  // which array is sent, and when, is different. `buildItems` must be a pure
  // function of the quotation IT IS HANDED — never of `quotation` the
  // possibly-stale closure — so it sees whatever the previous queued save
  // (if any) actually produced.
  const itemsSaveQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const saveQuotationItems = useCallback(
    (buildItems: (current: QuotationDetailDto) => QuotationItemDto[]) => {
      const run = async () => {
        const current = quotationRef.current;
        if (!current) throw new Error('No quotation loaded');
        const items = buildItems(current);
        const response = await updateQuotation(current._id, { items });
        if (response.success && response.data) {
          quotationRef.current = response.data;
          setQuotation(response.data);
        }
        return response;
      };
      // Chained after whatever is already queued. The queue's own tail must
      // never stay rejected — a `.then()` chained onto a rejected promise
      // never runs, so one failed save would silently stop every save queued
      // after it. `result`, what THIS call returns, is the real promise and
      // still rejects exactly as `updateQuotation` would have, for this
      // caller's own try/catch; only the queue's internal continuation is
      // logged and not re-thrown here.
      const result = itemsSaveQueueRef.current.then(run);
      itemsSaveQueueRef.current = result.catch((err) => {
        console.error('Quotation item save failed (save queue continues):', err);
      });
      return result;
    },
    [],
  );
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  // Confirm Order gets its OWN in-flight flag rather than reusing `updating`,
  // which every other mutation on this page also sets: a shared flag would put
  // the spinner on this button while an unrelated cell edit was saving, and
  // could not tell a second Confirm click from one of those (ClickUp 86d478wek).
  const [confirmingOrder, setConfirmingOrder] = useState(false);
  // Ref, not state: two clicks in the same tick read the same stale `false` from
  // state, so the disabled prop alone does not close the double-submit window.
  const confirmingOrderRef = useRef(false);
  const [confirmOrderError, setConfirmOrderError] = useState<string | null>(null);
  /** Why Send was refused before the send form opened (D105.2, D105.7: no, US$0.00 or a negative selling price), if it was. */
  const [sendRefusal, setSendRefusal] = useState<string | null>(null);
  /** Lines the freeze could not file into the Master Catalogue, if any. */
  const [catalogueHarvestError, setCatalogueHarvestError] = useState<string | null>(null);
  /** The catalogue rows this quotation has already seeded — the receipt panel. */
  const [catalogueRows, setCatalogueRows] = useState<HarvestedCatalogueRow[]>([]);
  const [removingCatalogueRowId, setRemovingCatalogueRowId] = useState<string | null>(null);
  /** The row whose held-back price change is being answered right now. */
  const [resolvingRepriceId, setResolvingRepriceId] = useState<string | null>(null);
  const toast = useToast();
  // Why a Unit change was refused, shown UNDER the Unit cell that was changed.
  // The top-right toast sat a screen-width away from the dropdown the user was
  // looking at (QA, QUO-00171). Fixed-positioned from the select's own rect so
  // the grid's scroll box cannot clip it on the last row.
  const [unitNotice, setUnitNotice] = useState<{ message: string; top: number; left: number } | null>(null);
  useEffect(() => {
    if (!unitNotice) return;
    const t = window.setTimeout(() => setUnitNotice(null), 6000);
    return () => window.clearTimeout(t);
  }, [unitNotice]);
  const refuseUnitChange = (message: string, anchorEl?: HTMLElement | null) => {
    if (!anchorEl) {
      toast.error(message);
      return;
    }
    const rect = anchorEl.getBoundingClientRect();
    setUnitNotice({ message, top: rect.bottom + 6, left: rect.left });
  };
  // The in-app confirmation (ActionDialog), not `window.confirm`, for the Markup cell:
  // a native dialog freezes the tab, so the one thing a stuck user needs, Report a bug,
  // is unreachable until they dismiss it (ClickUp 14yj85ny3x6, 14yj85ny3xf).
  const confirm = useConfirm();
  const [error, setError] = useState<string | null>(null);
  // The house rate comes from the owner. It was `useState(30)` — a fourth
  // written-down copy of a number that must agree with the server's, and the
  // reproduce-check in `resolveMarkupPercent` is asked with THIS value, so a
  // drift here would relabel every house-rate line on the page as hand-priced.
  const [markupPercentage] = useState(DEFAULT_MARKUP_PERCENT);
  const [applyingGlobalMarkup, setApplyingGlobalMarkup] = useState(false);
  // Snapshot of the line items taken right before an "Apply to all lines", so the bulk change
  // can be undone. Cleared once undone or when any other edit supersedes it. (QA T3)
  const [markupUndoSnapshot, setMarkupUndoSnapshot] = useState<{ items: any[]; pct: number } | null>(null);
  const [discountPercentage, setDiscountPercentage] = useState(0); // Default 0% discount
  const [downloading, setDownloading] = useState(false);
  const [ttdToUsdRate, setTtdToUsdRate] = useState<number | null>(null);
  // Set once the quotation reports its own locked rate — see loadExchangeRate.
  const lockedRateRef = useRef<number | null>(null);
  const [exchangeRateError, setExchangeRateError] = useState<string | null>(null);
  // The one Trinidad VAT rate (owner decision 2026-09-24), admin-set in
  // Settings — never a literal in this file. Null while it has not loaded or
  // has never been set: `catalogueRowCostPrice` and every caller of it refuse
  // to gross a VAT-applicable row rather than guess, the same refusal a
  // missing exchange rate already gets.
  const [vatRatePercent, setVatRatePercent] = useState<number | null>(null);
  // Settings `competitorMarkupWarnPercent` (D97). null until loaded, and stays null if the
  // load fails: with no threshold the Competitor Markup % shows with NO highlight, never a guess.
  const [competitorWarnPercent, setCompetitorWarnPercent] = useState<number | null>(null);
  const [competitorWarnFailed, setCompetitorWarnFailed] = useState(false);
  const [showClientView, setShowClientView] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  // Owner decision 2026-09-24: the lines shown in the RFQ-unit-mismatch dialog
  // when Send quote is pressed — null while the dialog is closed. Advisory
  // only: `openSendQuote` never refuses to send over this, only asks first.
  const [unitMismatchDialogLines, setUnitMismatchDialogLines] = useState<ReturnType<
    typeof collectRfqUnitMismatches
  > | null>(null);
  // The line "Go fix" last went to (D101.3), so each press steps to the next mismatched line.
  const [lastUnitFixLine, setLastUnitFixLine] = useState<number | null>(null);
  // D99 item 4: unconfirmed / not-in-catalogue lines, shown in the SAME dialog. null = closed.
  const [reviewDialogLines, setReviewDialogLines] = useState<ReviewEntry[] | null>(null);
  const [transportationCharges, setTransportationCharges] = useState(0);
  const [additionalCharges, setAdditionalCharges] = useState(0);
  const [competitorDiscountPercentage, setCompetitorDiscountPercentage] = useState(0);
  const [competitorTransportationCharges, setCompetitorTransportationCharges] = useState(0);
  const [competitorAdditionalCharges, setCompetitorAdditionalCharges] = useState(0);
  // The merged lookup panel (`ItemLookupPanel`) replaces the old magnifier
  // popup and the Supplier cell's own dropdown — both entry points open THIS,
  // one line's panel open at a time across the whole table, which stays here
  // deliberately (the same "page's business" split `SupplierCell`'s own
  // header comment describes). `lookupTriggerRef` is set to whichever button
  // (magnifier or Supplier cell) was actually clicked, so the panel's own
  // click-outside listener can tell that click apart from an outside one —
  // without it, the Supplier cell's toggle-to-close would close on mousedown
  // and immediately reopen on the click (the old `SupplierDropdown` QA T2 race).
  const [lookupPanelOpenIndex, setLookupPanelOpenIndex] = useState<number | null>(null);
  // D107: the line whose "Add this item to Master Catalogue" popup is open WITHOUT the lookup
  // panel - opened from a vendor-less line's cost cell, or right after a manual Add Item.
  const [catalogueAddIndex, setCatalogueAddIndex] = useState<number | null>(null);
  const lookupTriggerRef = useRef<HTMLElement | null>(null);
  // Owner decisions 2026-09-24: a catalogue/supplier pick from the lookup
  // panel changes the line's price, so the user must SEE it happening and
  // see what changed, not just the panel closing. `savingPickIndex` is which
  // line's pick has a network call in flight (the panel shows a spinner);
  // `pickInFlightRef` refuses a second pick from the moment one starts until
  // it ends, its confirm included — one pick, one dialog (D161.1). A ref, as
  // `confirmingOrderRef`. `flashLineIndex` is which line just landed, for a
  // brief highlight (`flashLine` below clears it itself).
  const [savingPickIndex, setSavingPickIndex] = useState<number | null>(null);
  const pickInFlightRef = useRef(false);
  const [flashLineIndex, setFlashLineIndex] = useState<number | null>(null);
  const flashLine = useCallback((index: number) => {
    setFlashLineIndex(index);
    window.setTimeout(() => setFlashLineIndex((cur) => (cur === index ? null : cur)), 1200);
  }, []);
  /**
   * "Does this item have VAT?" (`CatalogueVatPrompt.tsx`) for a line ALREADY
   * WRITTEN — open for at most one line at a time, the same "one open across
   * the whole table" shape `lookupPanelOpenIndex` already uses. The ONE
   * remaining opener is the line's own "VAT?" badge on a GENERATED line
   * (`pricingDetails.catalogueVatUnset === true`) — no row in memory to
   * re-price from directly, so it re-prices through the page's own per-line
   * Refresh (`answerCatalogueVatPrompt`). A same-session catalogue PICK
   * (`pendingCataloguePick`) or supplier-dropdown pick
   * (`pendingSupplierVatPick`), both below, ask BEFORE the line is written
   * instead — the badge is the only case with nothing in memory to hold.
   */
  const [vatPrompt, setVatPrompt] = useState<{
    index: number;
    masterCatalogueId: string;
    itemName: string;
  } | null>(null);
  const [savingVatPrompt, setSavingVatPrompt] = useState(false);
  /**
   * A same-session catalogue pick (`handleSearchSelect`) whose row's
   * `vat_applicable` is BLANK — held here, UNWRITTEN, while
   * `CatalogueVatPrompt` asks. `priceSelection` is exactly what
   * `ItemLookupPanel` handed the pick (present only when its unit selector
   * was used) and is re-derived for the SAME unit once answered
   * (`pendingCataloguePickVat.priceCataloguePickForVatAnswer`) — the bug that
   * closes is on that function's own header. `applyCataloguePick` (below) is
   * the one place that then actually writes the line, whether the answer was
   * Yes or No — there is no way to skip the question (D101.1).
   */
  const [pendingCataloguePick, setPendingCataloguePick] = useState<{
    index: number;
    item: MasterCatalogueItem;
    priceSelection?: CataloguePriceSelection;
  } | null>(null);
  /**
   * A same-session SUPPLIER-DROPDOWN pick (`handleSupplierSelect`) of a
   * Master Catalogue option whose `vatApplicable` is BLANK — held here,
   * UNWRITTEN, the same shape as `pendingCataloguePick` above but for a
   * `SupplierDropdownItem` (no `MasterCatalogueItem` row in hand, only the
   * option the panel fetched). `handlePendingSupplierVatPickAnswer` re-fetches
   * this line's options once answered, rather than re-deriving a price
   * client-side — see that function's own comment for why.
   */
  const [pendingSupplierVatPick, setPendingSupplierVatPick] = useState<{
    index: number;
    supplierData: SupplierDropdownItem;
    /** The search the panel ran when this row was clicked (F1) — the pick resolves against it. */
    searchTerm: string;
  } | null>(null);
  // The catalogue-addition review queue's mark on a line — "waiting for
  // catalogue review" / "refused" — fetched once per quotation load and held
  // as a map by lineIndex. See the hook's own header for why nothing here is
  // denormalized onto the line.
  const { markerFor: catalogueSubmissionMarkerFor, refresh: refreshCatalogueSubmissionMarkers } =
    useCatalogueSubmissionMarkers(quotation?._id, quotation?.status);
  const [summaryCollapsed, setSummaryCollapsed] = useState(true);
  // D170.5: the in-flight indicator for "Spread charges into prices" and its Undo.
  const [absorbingCharges, setAbsorbingCharges] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  // D45.1: the client changes waiting for review. Kept apart from `quotation` on purpose: every
  // save on this page replaces `quotation` with the PATCH response, which does not carry them, and
  // the banner must not vanish because somebody edited a cell.
  const [pendingAmendments, setPendingAmendments] = useState<ClientAmendmentDto[]>([]);
  // D81 — read with the quotation on load only: a save answers with the quotation alone.
  const [confirmedOrder, setConfirmedOrder] = useState<QuotationConfirmedOrderDto | null>(null);
  // The RFQ behind this quotation, as the load populated it - the heading's subject, the header's
  // vessel and the buyer's own facts (buyer ref, port, dates, currency) are read off it. Read on
  // load only and kept apart from `quotation`, like the two above: GET /quotations/:id populates
  // `rfqId`, a save answers with the quotation alone (`rfqId` a bare id), so a heading read off
  // `quotation` turned into "Quotation QUO-..." after the first cell edit (end-to-end retest,
  // 2026-10-07), the vessel chip went the same way for a quotation whose own vessel copy is blank,
  // and the four fact chips went on QUO-00243 after one line's Refresh price (2026-10-08).
  // Never read the RFQ off `quotation.rfqId` for the header.
  const [loadedRfq, setLoadedRfq] = useState<HeaderRfq>({});
  const [reviewingAmendmentId, setReviewingAmendmentId] = useState<string | null>(null);
  const [showCompareModal, setShowCompareModal] = useState(false);

  // The user's own column order and widths for the items grid (ClickUp
  // 86d478wd1), saved per USER and applied to every quotation they open (owner's
  // request, 2026-09-17). Drags change this page until Save — see the hook.
  const quotationGrid = useQuotationGridLayout();
  const gridLayout = quotationGrid.layout;

  // What is stopping this quotation being confirmed, recomputed as it is edited.
  // Asked with the SAME markup the grid is priced at — the server gate defaults
  // to the house rate, so a line that only clears the bar at an elevated
  // page markup will still be refused there, and the button being enabled is
  // never a promise. It exists so the user is told what to fix before clicking,
  // not so the client decides (ClickUp 86d478wf8).
  const confirmBlockers = useMemo(
    () => (quotation ? confirmationBlockers(quotation, markupPercentage) : []),
    [quotation, markupPercentage]
  );

  // D104.14: what the header still offers once the RFQ has an order.
  const ordered = orderedActions(quotation?.quotationNumber, confirmedOrder);

  // D104.12: the vessel this quotation is for - its own copy, else the RFQ's, read off the RFQ as the
  // load populated it (`loadedRfq`), for the same reason as the subject. Worked out once, by the
  // mirrored owner the server's PDFs and Excel read too: the header's vessel chip, the send form's
  // draft and the Client view's Vessel line all start from it.
  const vesselName = quotationVesselName({ customer: quotation?.customer, rfqId: loadedRfq });

  // D104.13: the tab names the quotation (the headline is the RFQ subject, never a file name: D161.4).
  useEffect(() => {
    if (!quotation?.quotationNumber) return undefined;
    const previous = document.title;
    document.title = quotationTabTitle(quotation.quotationNumber);
    return () => {
      document.title = previous;
    };
  }, [quotation?.quotationNumber]);

  // What is stopping this quotation being SENT (D105.2, D105.7: no, US$0.00 or a negative selling
  // price) - the confirmation gate's own selling-price rules, so the lines named here are the ones Confirm order would
  // name. Asked with the page's markup, as `confirmBlockers` is; the server refuses the same
  // quotation with QUOTATION_NOT_SENDABLE whatever this says, so it is a convenience and never the gate.
  const sendBlockedLines = useMemo(
    () => (quotation ? sendBlockers(quotation, markupPercentage) : []),
    [quotation, markupPercentage]
  );

  // Send quote asks the same D51 question before the email form opens, rather
  // than after it is filled in: the server refuses a line whose amount is not
  // the vessel's ask in its own unit, and the answer is on the grid already.
  const openSendQuote = () => {
    // D105.2 first, and in the page: a dialog listing the lines, not a browser alert. Send from
    // the Client view and Send quote both come through here.
    if (sendBlockedLines.length > 0) {
      setSendRefusal(notSendableMessage(sendBlockedLines.map((b) => b.message)));
      return;
    }
    const lines = quotation ? lineAmountBlockers(quotation.items) : [];
    if (lines.length > 0) {
      toast.error(
        `This quotation cannot be sent yet: ${lines.length} line${lines.length === 1 ? ' does' : 's do'} not state ` +
          `the amount the vessel asked for.\n\n${lines.map((l) => `• ${l.message}`).join('\n')}\n\n` +
          'Correct the quantity (the red Quantity cell offers the right figure), or mark it right with ✓.'
      );
      return;
    }
    // Owner decision 2026-09-24: a nudge, never a block. Unlike the D51 check
    // above, this never refuses to open the send modal by itself — it only
    // asks first, and "Send anyway" goes straight to `setShowSendModal(true)`.
    const unitMismatches = quotation ? collectRfqUnitMismatches(quotation.items) : [];
    // D99 item 4: same nudge, same dialog, for catalogue matches nobody has confirmed.
    const toReview = quotation ? linesToReview(quotation.items) : [];
    if (unitMismatches.length > 0 || toReview.length > 0) {
      setUnitMismatchDialogLines(unitMismatches);
      setReviewDialogLines(toReview);
      return;
    }
    setShowSendModal(true);
  };

  const scrollToQuotationLine = (lineNumber: number) => {
    const el = document.querySelector(`[data-quotation-line="${lineNumber}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // EVERY money figure on this screen — the two profit columns in the table and
  // every row of the Pricing Summary — comes from this one call. They used to be
  // three separate blocks spelling out the same arithmetic, and the discount was
  // missing from all three: a quote marked 10% off reported the profit of a quote
  // at full price. `.lines[index]` is a row's share, and it sums to the box.
  const quotationTotals = useMemo(
    () =>
      resolveQuotationTotals(quotation?.items, {
        defaultMarkupPercent: markupPercentage,
        discountPercent: discountPercentage,
        // The charge figures through the one reader, fed from screen state.
        ...chargesFromDoc({ transportationCharges, additionalCharges, customExpenses: quotation?.customExpenses }),
      }),
    [quotation, markupPercentage, discountPercentage, transportationCharges, additionalCharges]
  );

  const loadQuotation = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await fetchQuotationDetail(id);
      console.log('Loaded quotation data:', data);
      console.log('First item remarks:', data.items[0]?.remarks, 'Internal remarks:', data.items[0]?.internalRemarks);
      setQuotation(data);
      setPendingAmendments(data.pendingClientAmendments || []);
      setConfirmedOrder(data.confirmedOrder || null);
      const rfq: HeaderRfq = data.rfqId && typeof data.rfqId === 'object' ? data.rfqId : {};
      setLoadedRfq({ ...rfq, subject: rfq.subject || data.rfqSubject || '' });

      // Initialize discount and charges from database (Unichand's box)
      setDiscountPercentage(data.discountPercentage || 0);
      setTransportationCharges(data.transportationCharges || 0);
      setAdditionalCharges(data.additionalCharges || 0);
      // Competitor's box - independent
      setCompetitorDiscountPercentage(data.competitorDiscountPercentage ?? data.discountPercentage ?? 0);
      setCompetitorTransportationCharges(data.competitorTransportationCharges ?? data.transportationCharges ?? 0);
      setCompetitorAdditionalCharges(data.competitorAdditionalCharges ?? data.additionalCharges ?? 0);

      // The receipt panel loads from the effect keyed on `reviewedAt` above,
      // which fires on this first render too — so it is not requested a second
      // time here.
    } catch (err: any) {
      console.error('Failed to load quotation:', err);
      // Worded from the server's CODE, never its sentence (invariant 6).
      // `quotation.controller.js`'s GET-by-id throws QUOTATION_NOT_FOUND; any
      // other failure (network, 500, RBAC) reads the same fixed sentence.
      setError(err?.code === 'QUOTATION_NOT_FOUND' ? 'This quotation could not be found.' : 'Could not load this quotation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // The rate a quotation is priced at is locked when it is generated, so an admin
  // changing Settings → Exchange Rate cannot re-price it. The settings fetch below
  // is only a fallback for quotations that predate rate locking — the ref stops it
  // from clobbering a locked rate when the two requests resolve out of order.
  const loadExchangeRate = async () => {
    try {
      setExchangeRateError(null);
      // Use fixed rate from system settings (admin-controlled)
      const rateData = await getExchangeRate();
      if (lockedRateRef.current) return;
      // A response that carries no usable rate is a failure, not a rate of
      // zero: `rateData.rate` was assigned straight into state, so a malformed
      // response put `undefined` where every conversion on the page reads its
      // rate — and the log line below then threw on `undefined.toFixed`, which
      // is the only reason it ever surfaced at all.
      const rate = Number(rateData?.rate);
      if (!Number.isFinite(rate) || rate <= 0) {
        throw new Error('Settings returned no usable TT$ ↔ US$ rate');
      }
      setTtdToUsdRate(rate);
      console.log('TTD to USD rate loaded from settings:', rate, `(1 USD = ${(1 / rate).toFixed(2)} TTD)`);
    } catch (err: any) {
      console.error('Failed to load exchange rate from settings:', err);
      if (lockedRateRef.current) return;
      // Fixed sentence, never `err.message` (invariant 6) — `getExchangeRate`
      // is a legacy hand-rolled fetch (settingsApi.ts) that carries no `code`.
      setExchangeRateError('Could not load the TT$ ↔ US$ rate from settings.');
      // NO FALLBACK RATE. This used to `setTtdToUsdRate(0.1667)` — 6 TT$ to the
      // dollar — while the rate the business has actually set since 2026-08-06
      // is 0.153846 (6.50). So whenever this request failed, every TT$ figure on
      // the screen was ~7.7% out and every TT$ cost applied to a line was wrong
      // by the same, with a red asterisk as the only evidence. A rate nobody set
      // is not a rate (CLAUDE.md: never default missing config to a guess), and
      // it is the ROOT of the family TECH_DEBT #152 is one branch of — a wrong
      // rate is harder to see than a missing one. Null: the TT$ columns show a
      // dash and every conversion refuses until a real rate loads.
      setTtdToUsdRate(null);
    }
  };

  // Whenever the quotation (re)loads, its own locked rate wins over whatever the
  // settings fetch put in state.
  useEffect(() => {
    const locked = quotation?.exchangeRate?.ttdToUsd;
    if (typeof locked === 'number' && locked > 0) {
      lockedRateRef.current = locked;
      setTtdToUsdRate(locked);
      setExchangeRateError(null);
    }
  }, [quotation?.exchangeRate?.ttdToUsd]);

  /**
   * The Settings VAT rate — unlike the exchange rate above, NOT locked at
   * generation: the owner rule is "ALWAYS the Settings rate", so this reads
   * the live value every time the page loads, the same as `MasterCataloguePage`.
   */
  const loadVatRate = async () => {
    try {
      const rateData = await getVatRate();
      setVatRatePercent(rateData.ratePercent);
    } catch (err: any) {
      console.error('Failed to load VAT rate from settings:', err);
      setVatRatePercent(null);
    }
  };

  useEffect(() => {
    getCompetitorMarkupWarn()
      .then((d) => setCompetitorWarnPercent(typeof d?.percent === 'number' ? d.percent : null))
      .catch((err) => {
        console.error('Failed to load competitor markup threshold from settings:', err);
        setCompetitorWarnPercent(null); // no threshold -> no highlight, by rule
        setCompetitorWarnFailed(true);
      });
  }, []);

  /**
   * Keep the receipt panel current after every save, not only after a status
   * change.
   *
   * Editing a line on a SENT quotation re-freezes it server-side, and that is
   * what can move a catalogue price or park a held-back one. Refreshing only on
   * load and on status change would leave "Cost moved 30.00 → 300.00" invisible
   * until the next visit — on the very screen where the edit that raised it was
   * just made.
   *
   * Keyed on `reviewedAt` because every write path sets it, so this fires once
   * per save and not once per keystroke. `quotation` is replaced wholesale by
   * each save's response, so the value is always the server's.
   */
  useEffect(() => {
    if (!quotation?._id) return;
    void refreshCatalogueRows(quotation._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotation?._id, quotation?.reviewedAt]);

  /**
   * Load the receipt: which Master Catalogue rows this quotation has seeded, and
   * whether any of them is holding a price change nobody has answered.
   *
   * A failure leaves the panel empty rather than breaking the page — the rows
   * exist either way, and the quotation is what the user came here for.
   */
  const refreshCatalogueRows = useCallback(async (quotationId: string) => {
    try {
      const res = await fetchQuotationCatalogueRows(quotationId);
      setCatalogueRows(res.data?.rows || []);
    } catch (err) {
      console.error('Failed to load catalogue rows for this quotation:', err);
      setCatalogueRows([]);
    }
  }, []);

  /**
   * Remove one harvested row.
   *
   * Confirmed first, because it deletes from the Master Catalogue — the shared
   * price reference, not something local to this quotation.
   */
  /**
   * Answer a price change the re-freeze held back.
   *
   * "Apply" is confirmed first (D101.2): it moves the Master Catalogue price every
   * future quotation is priced from, so the in-app confirm names the item and both
   * prices (`repriceApplyConfirm`). "Keep old" changes no price and is not confirmed.
   */
  const handleResolveReprice = async (rowId: string, itemName: string, decision: 'apply' | 'keep') => {
    if (!quotation) return;
    if (decision === 'apply') {
      const pending = catalogueRows.find((r) => r._id === rowId)?.pendingReprice;
      if (!(await confirm(repriceApplyConfirm(itemName, pending)))) return;
    }
    try {
      setResolvingRepriceId(rowId);
      const res = await resolveQuotationCatalogueReprice(quotation._id, rowId, decision);
      toast.success(res.message || 'Done.');
      await refreshCatalogueRows(quotation._id);
    } catch (err: any) {
      // Fixed sentence, never `err.message` (invariant 6) — no mapper of its
      // own exists for this action's codes.
      setCatalogueHarvestError(`Could not update "${itemName}" in the Master Catalogue. Please try again.`);
    } finally {
      setResolvingRepriceId(null);
    }
  };

  const handleRemoveCatalogueRow = async (rowId: string, itemName: string) => {
    if (!quotation) return;
    const ok = await confirm({
      title: `Remove "${itemName}" from the Master Catalogue?`,
      summary: 'It will not be added again by this quotation. Other quotations that already reference it are unaffected.',
      tone: 'error',
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    try {
      setRemovingCatalogueRowId(rowId);
      const res = await removeQuotationCatalogueRow(quotation._id, rowId);
      toast.success(res.message || 'Removed from the Master Catalogue.');
      await refreshCatalogueRows(quotation._id);
    } catch (err: any) {
      // Fixed sentence, never `err.message` (invariant 6) — no mapper of its
      // own exists for this action's codes.
      setCatalogueHarvestError(`Could not remove "${itemName}" from the Master Catalogue. Please try again.`);
    } finally {
      setRemovingCatalogueRowId(null);
    }
  };

  // `handleStatusUpdate` was here, and was never called — CRA had been printing
  // it as an unused variable for some time. This page has no status control:
  // the dropdown is on the quotations LIST, and it uses a different endpoint
  // (`PATCH /:id`, not `PATCH /:id/status`). The help text above used to claim
  // otherwise; it no longer does. TECH_DEBT #260.

  // SEND on the send modal — the last step, there is no second screen after the
  // form. Resolves once the attempt is over, either way: the modal keeps its
  // button disabled until then. A failure is reported here and the modal stays
  // open with the form still filled in; a success closes it, and so does a mail
  // that went but could not be recorded (D180), which re-reads the quotation.
  const handleSend = async (emailData: SendQuotationData) => {
    if (!quotation) return;

    try {
      setUpdating(true);
      const response = await sendQuotation(quotation._id, emailData);
      if (response.success) {
        setQuotation(response.data);
        setShowSendModal(false);
        // Sending ends the wait on the client's changes (D45.1). The server says which are left.
        refreshPendingAmendments();
        toast.success('Quotation sent successfully!');
        // The mail server took the mail for some recipients and refused others: it went, but not to
        // everyone, and the clerk is told who missed it rather than left to assume.
        if (response.rejectedRecipients && response.rejectedRecipients.length > 0) {
          toast.error(rejectedRecipientsMessage(response.rejectedRecipients));
        }
      }
    } catch (err: any) {
      console.error('Failed to send quotation:', err);
      // Worded from the server's CODE (and reason), never its sentence (invariant 6): each real
      // failure — no recipient, a bad address, the mail server, a line with no price — says
      // what it is, and anything unrecognised reads the generic sentence.
      if (emailAlreadyWent(err)) {
        toast.error(sendQuotationErrorMessage(err), EMAIL_WENT_NOTICE_MS);
        setShowSendModal(false);
        loadQuotation();
      } else {
        toast.error(sendQuotationErrorMessage(err));
      }
    } finally {
      setUpdating(false);
    }
  };

  const refreshPendingAmendments = async () => {
    if (!id) return;
    try {
      const data = await fetchQuotationDetail(id);
      setPendingAmendments(data.pendingClientAmendments || []);
    } catch (err: any) {
      // Fixed sentence, never `err.message` (invariant 6).
      toast.error("Could not refresh the client's changes. Reload the page.");
    }
  };

  const handleMarkAmendmentReviewed = async (amendmentId: string) => {
    if (!quotation) return;
    setReviewingAmendmentId(amendmentId);
    try {
      const result = await markClientAmendmentReviewed(quotation._id, amendmentId);
      setPendingAmendments(result.pendingClientAmendments);
    } catch (err: any) {
      // Worded from the server's CODE, never its sentence (invariant 6).
      // `markClientAmendmentReviewed` carries QUOTATION_NOT_FOUND or
      // QUOTATION_AMENDMENT_NOT_FOUND; anything else reads a fixed sentence.
      toast.error(
        err?.code === 'QUOTATION_AMENDMENT_NOT_FOUND'
          ? 'That change is no longer on this quotation. Reload the page.'
          : err?.code === 'QUOTATION_NOT_FOUND'
            ? 'This quotation could not be found. Reload the page.'
            : 'Could not mark the change reviewed. Please try again.'
      );
    } finally {
      setReviewingAmendmentId(null);
    }
  };

  const handleAddManualItem = async (newItem: Omit<QuotationItemDto, 'lineNumber'>) => {
    if (!quotation) return;
    // Set inside `buildItems`, which `saveQuotationItems` runs synchronously
    // before the save resolves — read below exactly as before the queue fix.
    let itemWithLine!: QuotationItemDto;
    const response = await saveQuotationItems((current) => {
      const nextLineNumber =
        (current.items || []).reduce((max, it) => Math.max(max, it.lineNumber || 0), 0) + 1;
      itemWithLine = { ...(newItem as QuotationItemDto), lineNumber: nextLineNumber };
      // A line priced per a pack states the pack, in front of whatever remark
      // was typed in the modal (owner decision 2026-09-26).
      Object.assign(itemWithLine, applyPackagingRemark(itemWithLine));
      return [...current.items, itemWithLine];
    });
    if (response.success) {
      // D107: a manual item has no vendor, so it is added UNPRICED and the Master Catalogue
      // popup (vendor required) opens for it straight away. Cancelling leaves it unpriced.
      if (!lineHasRealVendor(itemWithLine)) setCatalogueAddIndex(response.data.items.length - 1);
    } else {
      throw new Error('Failed to add item');
    }
  };

  const handleConfirmOrder = async () => {
    if (!quotation) return;
    // Confirming twice creates (or re-syncs) the order twice. The ref is checked
    // and set synchronously, so the second of two clicks in one tick is refused
    // here even before React has re-rendered the disabled button.
    if (confirmingOrderRef.current) return;
    confirmingOrderRef.current = true;

    try {
      setConfirmingOrder(true);
      const confirmed = await manualConfirmOrder({ quotationId: quotation._id });
      toast.success('Confirmed order created successfully.');
      navigate(`/confirmed-orders/${confirmed._id}`);
    } catch (e) {
      // The copy comes from the envelope's `code`, never from `e.message` —
      // displaying server prose is what invariant 6 forbids.
      setConfirmOrderError(confirmOrderErrorMessage(e));
    } finally {
      confirmingOrderRef.current = false;
      setConfirmingOrder(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!quotation) return;

    try {
      setDownloading(true);
      await downloadQuotationPDF(quotation._id, discountPercentage);
    } catch (err: any) {
      console.error('Failed to download PDF:', err);
      // Worded from the server's CODE, never its sentence (invariant 6).
      toast.error(quotationDownloadErrorMessage(err, 'PDF'));
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!quotation) return;

    try {
      setDownloading(true);
      await downloadQuotationExcel(quotation._id, discountPercentage);
    } catch (err: any) {
      console.error('Failed to download Excel:', err);
      // Worded from the server's CODE, never its sentence (invariant 6).
      toast.error(quotationDownloadErrorMessage(err, 'Excel'));
    } finally {
      setDownloading(false);
    }
  };

  const handleCompetitorPriceChange = async (itemIndex: number, newPrice: number) => {
    // Writes through to item.competitorPrice; the refreshed quotation is the single source
    // of truth for the cell, so there is no local mirror left to fall out of step.
    await updateQuotationItem(itemIndex, 'competitorPrice', newPrice);
  };

  // Returns whether the write landed, so a caller that needs to know (the
  // spinner/flash around a supplier pick, `handleSupplierSelect` below) does
  // not have to re-open this function's own try/catch — this one already
  // reports its own failure (the toast below) and must keep doing so.
  const handleVendorChange = async (
    itemIndex: number,
    vendorName: string,
    pricing?: SupplierPickPricingPayload
  ): Promise<boolean> => {
    if (!quotation) return false;

    try {
      console.log('Changing vendor for item:', { itemIndex, vendorName, quotationId: quotation._id, pricing });
      const response = await updateQuotationItemVendor(quotation._id, itemIndex, vendorName, pricing);

      if (response.success) {
        setQuotation(response.data);
        console.log('Vendor updated successfully');
        return true;
      }
      return false;
    } catch (err: any) {
      console.error('Failed to update vendor:', err);
      // Worded from the server's CODE, never its sentence (invariant 6).
      toast.error(supplierPickErrorMessage(err));
      return false;
    }
  };

  /** A line as the server holds it after Confirm (`confirmLineMatch`), put on the page. */
  const applyConfirmedLine = (line: QuotationItemDto) =>
    setQuotation((prev) =>
      prev
        ? { ...prev, items: prev.items.map((it) => (it.lineNumber === line.lineNumber ? { ...it, ...line } : it)) }
        : prev
    );

  /**
   * D161.1: a pick that changes nothing on the line (`pickChangesNothing`) is not a Replace. A
   * flagged line is offered Confirm match, the Confirm button's own call; any other is told so.
   */
  const settleUnchangedPick = async (currentItem: QuotationItemDto) => {
    if (!quotation) return;
    if (!canConfirmMatch(currentItem)) {
      toast.info('This line is already priced from this item.');
      setLookupPanelOpenIndex(null);
      return;
    }
    const confirmed = await confirm({
      title: 'This line is already priced from this item',
      confirmLabel: 'Confirm match',
      summary: 'Nothing on the line changes. Confirm it as the right match for this request?',
    });
    if (!confirmed) return;
    try {
      applyConfirmedLine(await confirmLineMatch(quotation._id, currentItem.lineNumber));
      setLookupPanelOpenIndex(null);
    } catch (err: any) {
      toast.error(confirmRefusalMessage(err?.code));
    }
  };

  /**
   * The pick itself: preview, confirm, write. Split out of
   * `handleSupplierSelect` so the SAME path runs from all three of its
   * callers: a pick whose option already states VAT (or none, e.g. a
   * non-catalogue supplier row), and `handlePendingSupplierVatPickAnswer` once a blank row's "Does this item
   * have VAT?" question is settled. Every route therefore gets the owner's
   * before -> after confirm (decision C) and resolves against the search the
   * panel actually ran (`searchTerm`, F1).
   *
   * The RAW option goes over the wire, NOT a client-converted figure. The
   * server resolves the option this names — by `vendorName` and
   * `description`, the options service's own identity, for `searchTerm` —
   * for THIS line's unit, writes the price it states there, and converts TT$
   * at the quotation's pinned rate itself (QUO-00068). One payload, sent to
   * the preview and then the write — never rebuilt between the two, so the
   * confirm's "after" and the line the write produces cannot drift apart.
   *
   * Throws on a failed preview or write; the caller words it from the code.
   */
  const applySupplierPick = async (
    itemIndex: number,
    supplierData: SupplierDropdownItem,
    searchTerm: string
  ) => {
    if (!quotation) return;
    const currentItem = quotation.items[itemIndex];
    const rawUnitPrice = Number(supplierData.unitPrice);
    const pricingPayload: SupplierPickPricingPayload = {
      ...(Number.isFinite(rawUnitPrice) && rawUnitPrice > 0 ? { unitPrice: rawUnitPrice } : {}),
      ...(supplierData.currency ? { currency: supplierData.currency } : {}),
      ...(supplierData.uom ? { uom: supplierData.uom } : {}),
      ...(supplierData.source ? { source: supplierData.source } : {}),
      ...(supplierData.description ? { description: supplierData.description } : {}),
      searchTerm,
    };
    // One pick, one dialog (D161.1): nothing above awaits, so taken here it is taken at the start.
    if (pickInFlightRef.current) return;
    pickInFlightRef.current = true;
    try {
      // N8 (owner re-audit, 2026-09-25): the spinner means "a network call is
      // in flight", never "the user is deciding" — set for the preview fetch,
      // cleared while the confirm waits on a person, set again for the write.
      setSavingPickIndex(itemIndex);
      // Owner decision C: the SAME in-app confirm the catalogue pick shows,
      // its "after" computed by the server's own pick logic in a dry run
      // that writes nothing.
      const preview = await previewQuotationItemVendorPick(
        quotation._id,
        itemIndex,
        supplierData.vendorName,
        pricingPayload,
      );
      setSavingPickIndex(null);

      if (pickChangesNothing(currentItem, preview.data, ttdToUsdRate)) {
        await settleUnchangedPick(currentItem);
        return;
      }
      const confirmed = await confirm({
        title: 'Price this line from the picked item?',
        confirmLabel: 'Replace',
        body: <LinePickPreview before={currentItem} after={preview.data} ttdToUsdRate={ttdToUsdRate} />,
      });
      if (!confirmed) return;

      setSavingPickIndex(itemIndex);
      // One request does the whole job: vendor name + price + currency land
      // on the item together, server-side (QUO-00012).
      const applied = await handleVendorChange(itemIndex, supplierData.vendorName, pricingPayload);
      // `handleVendorChange` already reported a failure itself (its own
      // toast), so there is nothing further to say here.
      if (applied) flashLine(itemIndex);
      setLookupPanelOpenIndex(null);
    } finally {
      pickInFlightRef.current = false;
      setSavingPickIndex(null);
    }
  };

  const handleSupplierSelect = async (
    itemIndex: number,
    supplierData: SupplierDropdownItem,
    searchTerm: string
  ) => {
    if (!quotation) return;
    // Same guard as the catalogue pick above — one pick saves at a time
    // (owner decisions 2026-09-24).
    if (savingPickIndex !== null) return;

    // 0) The price per THIS line's unit, then in the currency this line is kept
    //    in. BEFORE the vendor is written, because a price that cannot be
    //    stated per the line's unit, or converted, is not a selection this
    //    screen can honour at all, and half-applying it — the supplier changed,
    //    the cost left describing somebody else's price — would be worse than
    //    refusing.
    //
    //    The unit first (QUO-00068): TT$12.50 per 10 kg BAG was applied to a
    //    600 KG line as TT$12.50 a kilo. The server states each option per the
    //    line's unit when the list is fetched and says why when it cannot;
    //    `supplierOptionRefusal` only words that answer.
    //
    //    The dropdown does not offer these rows, so this is the second gate: a
    //    click that got through a stale list, or a rate that failed to load
    //    between render and click. It refuses rather than falling back, because
    //    the fallback IS the bug (TECH_DEBT #152).
    const unitRefusal = supplierOptionRefusal(supplierData);
    if (unitRefusal) {
      setLookupPanelOpenIndex(null);
      toast.error(unitRefusal);
      return;
    }
    const nextUnitPrice = costToUsd(supplierData.lineUnitPrice, supplierData.currency, ttdToUsdRate);
    if (nextUnitPrice == null) {
      setLookupPanelOpenIndex(null);
      toast.error(
        `${supplierData.vendorName} priced this in ${
          String(supplierData.currency || '').trim().toUpperCase() || 'a currency it did not state'
        }. This quotation converts TT$ at its own locked rate and holds no rate for anything else, so that price cannot be applied to a line — it would go in as if it were US$.`,
      );
      return;
    }

    // The picked option prices a Master Catalogue row whose VAT is BLANK —
    // ask BEFORE the line is written (bug fix, same shape as
    // `handleSearchSelect`'s own `pendingCataloguePick`). The OLD flow wrote
    // the vendor first and answered AFTER, through the existing per-line
    // Refresh — a NAME-based re-lookup from scratch that can land on a
    // DIFFERENT supplier than the one just picked here. Held here, UNWRITTEN,
    // until `CatalogueVatPrompt` answers
    // (`handlePendingSupplierVatPickAnswer` below); the lookup panel closes now, same point a normal pick already
    // closes it.
    if (supplierData.masterCatalogueId && supplierData.vatApplicable == null) {
      setLookupPanelOpenIndex(null);
      setPendingSupplierVatPick({ index: itemIndex, supplierData, searchTerm });
      return;
    }

    try {
      await applySupplierPick(itemIndex, supplierData, searchTerm);
    } catch (err: any) {
      console.error('Failed to select supplier:', err);
      // Worded from the server's CODE, never its sentence (invariant 6) — the
      // preview call and the write share the same error shape.
      toast.error(supplierPickErrorMessage(err));
    }
  };

  /**
   * Yes/No from `CatalogueVatPrompt` for a PENDING supplier-dropdown pick —
   * nothing has been written yet (`pendingSupplierVatPick`,
   * `handleSupplierSelect`'s own comment above). Thin:
   * `pendingSupplierVatPick.resolvePendingSupplierVatPick`
   * (dependency-injected, tested on its own — see that file's header for the
   * bug this closes) owns the save + re-fetch-by-`masterCatalogueId`; this
   * wrapper supplies the real API calls, then the one write
   * (`applySupplierPick`) any supplier pick uses.
   */
  const handlePendingSupplierVatPickAnswer = async (vatApplicable: boolean) => {
    if (!pendingSupplierVatPick) return;
    const { index, supplierData, searchTerm } = pendingSupplierVatPick;
    setSavingVatPrompt(true);
    // Two steps, two failure messages: whether the VAT answer itself was
    // saved is what the person needs to know when something goes wrong.
    let found: SupplierDropdownItem;
    try {
      found = await resolvePendingSupplierVatPick(
        { supplierData, vatApplicable, quotationId: quotation?._id },
        { setCatalogueVat, fetchSupplierOptions: fetchSupplierDropdownDataFromAllTables }
      );
    } catch (err: any) {
      console.error('Failed to save VAT answer', err);
      toast.error('The VAT answer could not be saved, so the line was not changed. Please try again.');
      // Ends the pick (D101.1): the popup has no Cancel, so it must not outlive a failure.
      setPendingSupplierVatPick(null);
      setSavingVatPrompt(false);
      return;
    }
    try {
      // Same order as the catalogue pick's VAT answer: the prompt stays open
      // (its buttons disabled by `saving`) under the before -> after confirm,
      // and closes once the pick is written or cancelled.
      await applySupplierPick(index, found, searchTerm);
    } catch (err: any) {
      console.error('Failed to select supplier:', err);
      // Worded from the server's CODE, never its sentence (invariant 6).
      toast.error(`The VAT answer was saved. ${supplierPickErrorMessage(err)}`);
    } finally {
      setPendingSupplierVatPick(null);
      setSavingVatPrompt(false);
    }
  };

  const handleLineItemUnitChange = async (
    itemIndex: number,
    targetUnit: string,
    altPricing: ReturnType<typeof getAlternateQuotationPricing>,
    /** The Unit select, so a refusal is shown beside it rather than top-right. */
    anchorEl?: HTMLElement | null
  ) => {
    if (!quotation) return;
    // `item` is the line as it stands right now, for validation and as the
    // base this conversion computes from; the array it is finally written
    // into is rebuilt from the freshest known state at save time, below
    // (`saveQuotationItems`), not from this closure's `quotation.items`.
    const item = { ...quotation.items[itemIndex] };

    // D97 (5th round): a locked IFS line never changes unit - refused before anything moves.
    if (isLockedAtStatedPrice(item)) {
      const lockedRefusal = lockedUnitRefusal(item, targetUnit);
      if (lockedRefusal) refuseUnitChange(lockedRefusal, anchorEl);
      return;
    }

    const currentUnitPrice = typeof item.unitPrice === 'number' ? item.unitPrice : parseFloat(String(item.unitPrice || 0)) || 0;
    // UNROUNDED (D85): stored as `unitPriceExact` and fed to the selling price. Every
    // other reader below gets `targetCost`, the cent-ceiled figure they always had.
    const targetUnitPrice = targetUnitPriceFor(item, targetUnit, altPricing);
    if (!targetUnitPrice || targetUnitPrice <= 0 || currentUnitPrice <= 0) {
      refuseUnitChange(`Cannot convert this line item to ${targetUnit} from current data.`, anchorEl);
      return;
    }
    const targetCost = ceilPrice(targetUnitPrice);

    const currentQty = typeof item.quantity === 'number' ? item.quantity : parseFloat(String(item.quantity || 0)) || 0;
    if (currentQty <= 0) return;

    // The anchor this conversion was computed from, seeded on the FIRST
    // conversion from the line as the user last left it and reused by every
    // conversion after. Persisting it is what makes the sequence reversible: a
    // reload, or a fifth trip round the dropdown, still resolves against the
    // same fixed point rather than against the last answer.
    const pack = readPackStructure(item);
    const anchor = readConversionAnchor(item) || anchorFromItem(item);

    // THE QUANTITY IS PART OF THE CONVERSION. This line used to read
    // `const convertedQty = currentQty` — the price was restated and the count
    // was carried over verbatim, so an RFQ asking for 20 PCS of a 10-per-case
    // item (US$10.00/PCS, US$200.00) became "20 CASE at US$100.00" and quoted
    // US$2,000.00. The same line switched to LBS became 20 lb of a 20 kg
    // request and quoted US$90.72. Ten times over in one direction, less than
    // half in the other, on a dropdown whose whole purpose is to say the SAME
    // thing a different way.
    //
    // `convertQuantity` scales by the reciprocal of the price's ratio, so
    // quantity x unit price is unchanged by construction and the line still
    // states the amount the RFQ actually asked for. Anchored, and quantised
    // once here at the edge, for the same reasons as the price above.
    // FOUR sources, best first, and the first of them is the RFQ itself.
    //   1. What the vessel asked for — `originalRequest`, written once at
    //      generation and rewritten by nothing (TECH_DEBT #151). Owner
    //      instruction, ClickUp 86d478wv2: "the conversion should ALWAYS use
    //      that as the base". It is the only source here that cannot drift,
    //      because no later path is allowed to touch it.
    //
    //      THE CONSEQUENCE, STATED RATHER THAN HIDDEN: this outranks a
    //      reviewer's own corrected count. Somebody who changes 2 CASE to 3 and
    //      then touches the Unit dropdown gets the RFQ's ask back, because the
    //      RFQ is the base by instruction. That was raised before this was
    //      built and decided the other way. It falls through where the request
    //      states no unit (nothing to convert FROM) and on any line the RFQ
    //      never wrote about — a hand-added line, or anything quoted before
    //      2026-08-28 whose `originalRequest` the backfill has not yet filled.
    //   2. The anchor's own count. Exact, and immune to chaining for the same
    //      reason the price is.
    //   3. The line's current count through the pack. Also exact per hop — this
    //      is where anchors written before the anchor carried a quantity land,
    //      and where a line whose count was edited mid-sequence lands.
    //   4. The price ratio, when there is no pack at all. Both ratios are
    //      pieces-per-target over pieces-per-current, so this restates the
    //      amount as faithfully as the data allows and keeps the line total
    //      intact. Rounded prices make it approximate; leaving the quantity
    //      alone makes it wrong by the whole ratio, which is the bug.
    // `restateQuantity` owns the order above (utils/quotationUnitConversions).
    // It lived here, inline, where nothing could test it — this page has no test
    // of its own, so a rule that decides how much of a thing a client is quoted
    // was pinned by a comment and nothing else.
    const restatement = restateQuantity({
      rfqAsked: readOriginalRequest(item),
      anchor,
      currentQty,
      currentUom: normalizeUnit(item.uom || 'PCS'),
      currentUnitPrice,
      targetUnitPrice: targetCost,
      targetUnit,
      pack,
    });
    // WHOLE-NUMBER CONVERSION RULE (owner, 2026-09-24): the pack says this
    // restatement lands on a part pack of a unit that COUNTS things (CASE,
    // PCS, ...), and no amount of rounding makes that honest — "the round-up
    // thing that appears when quantity is in fraction is wrong." The dropdown
    // refuses the unit change outright; the line stays exactly as it was.
    if (restatement.source === 'refused') {
      refuseUnitChange(
        restatement.message || `Cannot convert this line item to ${targetUnit}: not a whole number.`,
        anchorEl
      );
      return;
    }
    const convertedQty = restatement.quantity;
    // Off the UNROUNDED cost (D85): a sub-cent per-unit cost (an ounce) ceiled to the cent
    // first inflated the total by the whole rounding (B001: US$30.00 -> US$35.36).
    const convertedTotalPrice = ceilPrice(convertedQty * targetUnitPrice);

    // The selling and competitor prices are restated from the anchor for exactly
    // the same reason as the cost. Scaling them by targetUnitPrice /
    // currentUnitPrice — the old `ratio` — chained them onto the previous hop's
    // rounded output, so they ratcheted alongside it and, worse, by a ratio that
    // had itself been rounded twice.
    const fromAnchor = (value: number | null | undefined): number | null => {
      if (!pack || !anchor || value == null || !(value > 0)) return null;
      const converted = convertUnitPrice({
        price: value,
        fromUom: anchor.uom,
        toUom: targetUnit,
        pack,
      });
      return converted != null && converted > 0 ? ceilPrice(converted) : null;
    };

    // A unit conversion RESTATES a line; it does not re-rate it. 30% of a case is
    // still 30%, so the rate has to be read off the line BEFORE either half moves.
    const quotedBefore = resolveMarkupPercent(item, markupPercentage);
    // ...and whether the line is still AT that rate: a stale one is restated, not re-priced (D85).
    const atStatedRateBefore = isPricedAtStatedRate(item, markupPercentage);

    // The TT$ pin is stated per the unit the line is in RIGHT NOW, so it moves
    // through the same pack ratio the cost does — and read here, before
    // `item.uom` becomes the target.
    //
    // A pack ratio is a whole number of pieces, not a rounded price, so this is
    // exact in both directions: TT$100.00 a CASE restates to TT$10.00 a PCS and
    // back to TT$100.00. That is the whole point of pinning it — the US$ cost
    // ceils a cent on each hop and the TT$ column no longer inherits that.
    //
    // Dropped, not guessed, when there is no pack to restate it through: a TT$
    // figure belonging to a unit the line no longer has would be printed against
    // the wrong row, while deriving is only imprecise.
    const pinnedTtdBefore =
      typeof item.unitPriceTtd === 'number' && Number.isFinite(item.unitPriceTtd) && item.unitPriceTtd > 0
        ? item.unitPriceTtd
        : null;
    const restatedTtd =
      pack && pinnedTtdBefore != null
        ? convertUnitPrice({
            price: pinnedTtdBefore,
            fromUom: normalizeUnit(item.uom || 'PCS'),
            toUom: targetUnit,
            pack,
          })
        : null;

    item.uom = targetUnit;
    item.quantity = convertedQty;
    item.unitPrice = targetCost;
    // D85: the unrounded cost that cent was ceiled from, so the next re-price marks up the same figure.
    item.unitPriceExact = targetUnitPrice;
    item.unitPriceTtd = restatedTtd != null && restatedTtd > 0 ? ceilPrice(restatedTtd) : null;
    item.totalPrice = convertedTotalPrice;

    // Which of re-price / anchor / proportional scale applies is
    // `restateSellingPrice`'s call (D85): a line quoted at a RULE is re-priced from
    // its restated cost and pin at the quotation's locked rate (a generated line
    // stores a selling price now, so a stored price no longer means hand-priced);
    // only a hand-priced ('derived') line restates through the anchor or the ratio.
    const restated = restateSellingPrice({
      quotedBefore,
      atStatedRate: atStatedRateBefore,
      currentUnitSelling: item.unitSellingPrice,
      currentUnitPrice,
      anchoredSelling: fromAnchor(anchor?.unitSellingPrice),
      targetUnitPrice,
      convertedQty,
      fallbackMarkupPercent: markupToApply(item, markupPercentage),
    });
    Object.assign(item, restated);

    const anchoredCompetitor = fromAnchor(anchor?.competitorPrice);
    if (anchoredCompetitor != null) {
      item.competitorPrice = anchoredCompetitor;
    } else if (item.competitorPrice && item.competitorPrice > 0 && currentUnitPrice > 0) {
      item.competitorPrice = ceilPrice(item.competitorPrice * (targetCost / currentUnitPrice));
    }

    // Carry the pre-conversion rate onto the restated line. Without it, a line
    // whose money was RESTATED rather than re-priced (a stale line, D85) would have
    // its rate measured off a price that ceiled again in the new unit — $3.44 ->
    // $34.40 against $4.48 -> $44.80 reads 30.23%, not 30%. (A line at its rate is
    // re-priced by `restateSellingPrice`, 44.72 here, and stores the rate itself.)
    // A hand-priced line keeps its null:
    // no rate produced its price before the hop and none produces it after.
    if (quotedBefore.source !== 'derived') item.markup = quotedBefore.markupPercent;

    // A restatement re-counts the line, so any part-pack round-up recorded
    // against the OLD unit is answering a question about a row that is gone —
    // 3 CASE restated to PCS is 30 PCS, where "no vendor ships half a case"
    // has nothing to say. Dropped unconditionally, anchor or no anchor.
    {
      const { partPackRounding: _stale, ...restDetails } =
        ((item.pricingDetails as Record<string, unknown>) || {});
      item.pricingDetails = (anchor
        ? { ...restDetails, conversionAnchor: anchor }
        : restDetails) as QuotationItemDto['pricingDetails'];
    }

    // A unit change can put a line on, or take it off, a per-pack price, so the
    // client-visible packaging sentence follows (owner decision 2026-09-26).
    Object.assign(item, applyPackagingRemark(item));

    try {
      const response = await saveQuotationItems((current) => {
        const updatedItems = [...current.items];
        updatedItems[itemIndex] = item;
        return updatedItems;
      });
      if (!response.success) {
        throw new Error('Update failed');
      }
    } catch (err: any) {
      console.error('Failed to convert line unit:', err);
      // Worded from the server's CODE, never its sentence (invariant 6, N3
      // owner re-audit 2026-09-25).
      toast.error(
        err?.code === 'QUOTATION_NOT_FOUND'
          ? 'This quotation could not be found.'
          : isLockedLineError(err)
            ? LOCKED_LINE_UNLOCK_MESSAGE
            : isLineCostNeedsVendorError(err)
              ? LINE_COST_NEEDS_VENDOR_MESSAGE
              : "Could not convert this line's unit. Try again."
      );
    }
  };

  /**
   * `pricingDetailsPatch` is how a caller records WHY it made this edit without
   * owning a second copy of what the edit does. The part-pack round-up is a
   * quantity change in every arithmetic respect — total cost, total selling,
   * the retired anchor — and rebuilding that in its own handler would be the
   * duplication this file has been paying for elsewhere. It merges after the
   * anchor drop below, so a caller can retire the old provenance and write its
   * own in one save.
   */
  const updateQuotationItem = async (
    itemIndex: number,
    field: keyof QuotationItemDto,
    newValue: string | number,
    pricingDetailsPatch?: Record<string, unknown>,
    /**
     * D85.6: what a TT$ cost edit STATES — the cost, its exact US$ figure and the
     * TT$ pin (`ttdCostEdit`); the TT$ cost cell passes it. Absent = the edit says
     * nothing about TT$; `costFieldsAfterEdit` decides what the line keeps.
     */
    costEdit?: CostEdit,
  ) => {
    if (!quotation) return;

    console.log('Updating quotation item:', { itemIndex, field, newValue, quotationId: quotation._id });

    // The line as it stands BEFORE this edit. Asking the owner what rate a line
    // is quoted at only works while its cost and its selling price still belong
    // to the same state of it — once the new cost is in, the two numbers
    // describe different lines and the answer is meaningless. The array THIS
    // edit is finally written into is rebuilt from the freshest known state at
    // save time, below (`saveQuotationItems`), not from this closure's
    // `quotation.items`.
    const before = quotation.items[itemIndex];
    const item = { ...before };

    // Update the specific field
    (item as any)[field] = newValue;
    // D97: a person typing a selling price or a markup is the explicit override of a stated
    // (IFS list) selling price.
    Object.assign(item, statedSellingAfterEdit(String(field), item));

    // The TT$ pin follows the COST, and only the cost.
    //
    // A hand edit of the US$ cell RETIRES it: once somebody types a US$ figure,
    // US$ is what this line states, and leaving the supplier's old TT$ behind
    // would print that old number in the TT$ column beside the new cost — the
    // two columns silently describing different prices. Falling back to the
    // derivation is merely imprecise, which is what it was before the pin.
    //
    // The TT$ cell passes its own typed figure instead, so a TT$ edit is stored
    // to the cent it was typed at rather than being re-derived from the US$ it
    // converts to (`unitCostTtd`).
    // D85: a person stating the cost retires the unrounded cost the previous price was
    // ceiled from; D85.6: a TT$ typed in the TT$ cell states its exact US$, so it is set.
    // `costFieldsAfterEdit` owns both (tested).
    Object.assign(item, costFieldsAfterEdit(field, costEdit));

    // A hand-edited price is the user stating a new truth for the unit the line
    // is in RIGHT NOW, which retires whatever fixed point the line was anchored
    // to. Dropping it re-seeds the next conversion from the figure they just
    // typed; keeping it would restate their edit away the moment they touched
    // the Unit dropdown.
    //
    // A QUANTITY edit is NOT the same statement, and treating it as one is how
    // the US$ cost drifted (ClickUp 86d478wv2). Correcting 2 CASE to 3 CASE
    // says nothing whatever about what a CASE costs — so the count the anchor
    // remembers has to go, and the PRICE it remembers must not.
    //
    // Dropping the whole anchor there discarded `anchor.unitPrice`, the last
    // unrounded price anybody established. `targetUnitPriceFor` then fell back
    // to `anchorFromItem`, which then read `item.unitPrice` — a figure already
    // through `ceilPrice` (since D85 it reads the exact cost while that still
    // explains `unitPrice`, else the same ceiled figure) — so the next conversion re-anchored on a rounded
    // number and ratcheted: ceilPrice(15.39/10) = 1.54, ceilPrice(1.54*10) =
    // 15.40. That is how QUO-00013's two lines came out a cent apart off one
    // catalogue row. Keeping the price half makes convert → edit Quantity →
    // convert back return the cent it started from.
    //
    // Nothing is lost by keeping it: `convertedQty` simply falls through to its
    // next source, the line's own corrected count through the pack, which is
    // exactly the right reading of a count somebody just typed.
    // `pricingDetailsAfterFieldEdit` owns which of those an edit retires, and
    // is where the reasoning above is written down in full. Extracted for the
    // same reason as `restateQuantity` below it: the drift it fixes is a money
    // bug, and a money bug needs a test that fails when it comes back.
    item.pricingDetails = pricingDetailsAfterFieldEdit(
      item.pricingDetails as Record<string, unknown> | undefined | null,
      field
    ) as QuotationItemDto['pricingDetails'];

    if (pricingDetailsPatch) {
      item.pricingDetails = {
        ...((item.pricingDetails as Record<string, unknown>) || {}),
        ...pricingDetailsPatch,
      } as QuotationItemDto['pricingDetails'];
    }
    console.log(`Updated item ${itemIndex} field ${field} to:`, newValue);
    console.log('Updated item:', item);

    // Auto-calculate related fields based on what was changed
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = typeof item.quantity === 'number' ? item.quantity : parseFloat(String(item.quantity)) || 0;
      const unitPrice = typeof item.unitPrice === 'number' ? item.unitPrice : parseFloat(String(item.unitPrice || 0)) || 0;

      // Total Cost is always Qty × Unit Cost — it is a derived, read-only column, and
      // Quotation.pre('save') recomputes it the same way server-side.
      item.totalPrice = ceilPrice(quantity * unitPrice);

      // WHICH EDIT WAS THIS. Both fields land here and they are not the same
      // question. A COST change invalidates the selling price that was derived
      // from the old cost, so the line is re-priced at the rate it carries. A
      // QUANTITY change invalidates nothing about the unit price — 30 of a thing
      // costs the same each as 20 of it — and re-deriving it there is how a line
      // somebody had priced at $60.92 against a $1.90 cost snapped back to $2.47
      // the moment anyone touched the Qty cell. (TECH_DEBT #147.)
      //
      // A hand-priced line survives a cost change too, on the same reasoning
      // `updateQuotationItemVendor` already uses: `source === 'derived'` means no
      // rate produced this price, so somebody typed it, and a cost edit is not
      // permission to overwrite it. Asked of `before`, not of `item`, because
      // `item` already carries the new cost against the old selling price.
      //
      // Total Selling is Qty × Unit Selling either way, so it always reconciles
      // with the displayed unit selling price. (ClickUp 86d3t6v1d)
      const handPriced = resolveMarkupPercent(before, markupPercentage).source === 'derived';
      const keepUnitSelling =
        (field === 'quantity' || handPriced) && Number(item.unitSellingPrice) > 0;

      if (keepUnitSelling) {
        item.totalSellingPrice = ceilPrice(Number(item.unitSellingPrice) * quantity);
      } else {
        // A 0% markup is honoured, not treated as "no markup set" — the old
        // truthiness guard re-marked an at-cost line up by the house default.
        // `priceLineAt` writes the rate with the money, so the line records what
        // it was priced by instead of leaving the next reader to divide it out.
        Object.assign(
          item,
          priceLineAt({
            unitCost: unitPrice,
            unitCostExact: item.unitPriceExact,
            quantity,
            markupPercent: markupToApply(item, markupPercentage),
            statedSellingPrice: lockedUnitSellingPrice(item),
          }),
        );
      }
    }

    // If markup is changed, recalculate selling prices
    if (field === 'markup') {
      const markup = typeof newValue === 'number' ? newValue : parseFloat(String(newValue)) || 0;
      const quantity = typeof item.quantity === 'number' ? item.quantity : parseFloat(String(item.quantity)) || 0;
      if (item.unitPrice) {
        // Through the owner, so the cell that SETS the rate stores it as the same
        // number every other write site stores — `(item as any)[field] = newValue`
        // above puts the raw cell value on the line, and that is a string.
        Object.assign(item, priceLineAt({ unitCost: item.unitPrice, unitCostExact: item.unitPriceExact, quantity, markupPercent: markup }));
      }
    }

    // If unit selling price is changed, calculate total selling price
    if (field === 'unitSellingPrice') {
      const quantity = typeof item.quantity === 'number' ? item.quantity : parseFloat(String(item.quantity)) || 0;
      const unitSellingPrice = typeof newValue === 'number' ? newValue : parseFloat(String(newValue)) || 0;
      item.totalSellingPrice = ceilPrice(quantity * unitSellingPrice);
    }

    // If total selling price is manually changed, ensure it's properly rounded
    if (field === 'totalSellingPrice') {
      const totalSellingPrice = typeof newValue === 'number' ? newValue : parseFloat(String(newValue)) || 0;
      item.totalSellingPrice = ceilPrice(totalSellingPrice);
    }

    // If quantity changes, recalculate total selling price from unit selling price
    if (field === 'quantity' && item.unitSellingPrice) {
      const quantity = typeof newValue === 'number' ? newValue : parseFloat(String(newValue)) || 0;
      item.totalSellingPrice = ceilPrice(quantity * item.unitSellingPrice);
    }

    try {
      console.log('Sending API request to update quotation...');
      const response = await saveQuotationItems((current) => {
        const updatedItems = [...current.items];
        updatedItems[itemIndex] = item;
        console.log('Updated items being sent:', updatedItems);
        return updatedItems;
      });
      console.log('API response:', response);

      if (response.success) {
        console.log('Quotation state updated successfully');
      } else {
        throw new Error('Update failed');
      }
    } catch (err: any) {
      console.error('Failed to update item:', err);
      if (isLockedLineError(err)) toast.error(LOCKED_LINE_UNLOCK_MESSAGE);
      else if (isLineCostNeedsVendorError(err)) toast.error(LINE_COST_NEEDS_VENDOR_MESSAGE);
      throw err; // Re-throw to handle in EditableCell
    }
  };

  /**
   * D51 — the answers to a line whose amount is not the vessel's ask in its
   * own unit (QUO-00054: 20 CASE of water quoted as 20 L). Finalize, Send and
   * Confirm refuse until one is given.
   *
   * "→240": take the ask restated through the line's pack. An ordinary quantity
   * change, through its owner.
   *
   * OWNER DECISION A, F5 (2026-09-25 audit): this never writes a FRACTIONAL
   * count of a whole-count unit — "→3.333333" offered nothing a vendor ships.
   * The chip that calls this is hidden whenever `expectedIsWholeCount` says so
   * (see the D51 block's rendering below, which offers "switch to the asked
   * unit" and "type a whole number" instead); this guard is the same rule kept
   * here too, so a stale render can never write the fraction through anyway.
   * The MISMATCH itself, and the block on Finalize/Send/Confirm, are untouched
   * either way — only which remedy this one chip may apply.
   */
  const handleUseAskedAmount = async (itemIndex: number) => {
    if (!quotation) return;
    const check = lineAmountCheck(quotation.items[itemIndex]);
    if (check.status !== LINE_AMOUNT_STATUS.MISMATCH || check.expected == null) return;
    if (!expectedIsWholeCount(check)) return;
    try {
      await updateQuotationItem(itemIndex, 'quantity', check.expected);
    } catch (err: any) {
      // Worded from the server's CODE, never its sentence (invariant 6, N3
      // owner re-audit 2026-09-25).
      toast.error(
        err?.code === 'QUOTATION_NOT_FOUND'
          ? 'This quotation could not be found.'
          : `Could not set the quantity to ${check.expected} ${check.quoted.uom}. Try again.`
      );
    }
  };

  /**
   * D51's other remedy for a fractional count (F5, owner decision A,
   * 2026-09-25 audit): the line becomes the asked quantity AND unit, repriced
   * through the SAME owner the Unit dropdown itself calls
   * (`handleLineItemUnitChange`) — never a new repricing. Reusing it also
   * means the restatement lands on the RFQ's own ask automatically: with the
   * target unit now equal to `originalRequest.uom`, `restateQuantity`'s first
   * source (the RFQ) applies directly, at the amount actually asked for.
   *
   * The THIRD D51 remedy, "type a whole number", needs no handler — it is the
   * Quantity cell, already editable.
   */
  const handleSwitchToAskedUnit = async (
    itemIndex: number,
    altPricing: ReturnType<typeof getAlternateQuotationPricing>
  ) => {
    if (!quotation) return;
    const check = lineAmountCheck(quotation.items[itemIndex]);
    if (check.status !== LINE_AMOUNT_STATUS.MISMATCH || !check.asked) return;
    await handleLineItemUnitChange(itemIndex, check.asked.uom, altPricing);
  };

  /**
   * "✓": a person says the amount on the line is right as it stands. Recorded
   * with the quantity and unit it was said about — any later change to either
   * voids it — and who said it and when. Not a quantity edit: nothing else on
   * the line moves, the conversion anchor included.
   */
  const handleAcceptLineAmount = async (itemIndex: number) => {
    if (!quotation) return;
    const item = quotation.items[itemIndex];
    const check = lineAmountCheck(item);
    if (!blocksQuotation(check)) return;
    const ok = await confirm({
      title: `Keep ${check.quoted.quantity} ${check.quoted.uom} on this line?`,
      summary:
        `${describeLineAmount(check)}\n\n` +
        'It can then be finalized, sent and confirmed as it stands, until its quantity or unit changes.',
      confirmLabel: 'Keep it',
    });
    if (!ok) return;
    try {
      const response = await saveQuotationItems((current) => {
        const updatedItems = [...current.items];
        // The freshest copy of this line, not `item` read before the confirm
        // dialog was open and answered — only the mark being added is new.
        const freshItem = current.items[itemIndex] ?? item;
        updatedItems[itemIndex] = {
          ...freshItem,
          pricingDetails: {
            ...((freshItem.pricingDetails as Record<string, unknown>) || {}),
            amountAccepted: {
              quantity: check.quoted.quantity,
              uom: check.quoted.uom,
              asked: check.asked,
              expected: check.expected,
              by: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email : null,
              at: new Date().toISOString(),
            },
          },
        };
        return updatedItems;
      });
      if (!response.success) throw new Error('Update failed');
    } catch (err: any) {
      console.error('Failed to mark the amount right:', err);
      // Fixed sentence, never `err.message` (invariant 6) — `updateQuotation`
      // is a generic PATCH with no mapper of its own for this action's codes.
      toast.error('Failed to mark the amount right. Please try again.');
    }
  };

  type UnichandField = 'discountPercentage' | 'additionalCharges';
  type CompetitorField = 'competitorDiscountPercentage' | 'competitorAdditionalCharges';

  /**
   * D170.5: "Spread charges into prices" builds the charges into the line
   * selling prices by line value ON THE SERVER (`utils/chargeSpread`), the Final Price unchanged;
   * Undo is offered while the server keeps the spread's record (`quotation.chargeSpread`).
   */
  const runChargeSpread = async (action: 'spread' | 'undo') => {
    if (!quotation) return;
    if (action === 'spread') {
      const ok = await confirm({
        title: 'Spread charges into prices?',
        summary:
          `${USD_LABEL}${quotationTotals.charges.toFixed(2)} of charges (Additional Charges, Custom Expenses) are built ` +
          "into the line selling prices, in proportion to each line's value. The Final Price does not change; cents " +
          'that cannot be spread stay on a Customs Expense row, or on Additional Charges when there are no custom ' +
          'expenses. The amount stays on this summary as "Charges built into prices", and you can undo it until the ' +
          'quotation next changes.',
        confirmLabel: 'Spread charges',
      });
      if (!ok) return;
    }
    setAbsorbingCharges(true);
    try {
      await itemsSaveQueueRef.current; // after any line save still in flight
      const saved = await (action === 'spread' ? spreadQuotationCharges : undoQuotationChargeSpread)(quotation._id);
      quotationRef.current = saved;
      setQuotation(saved);
      const charges = chargesFromDoc(saved);
      setTransportationCharges(charges.transportationCharges);
      setAdditionalCharges(charges.additionalCharges);
      toast.success(action === 'spread' ? 'Charges spread into the line prices.' : 'Spread undone: the charges are back on their rows.');
    } catch (err: any) {
      console.error(`Charge ${action} failed:`, err);
      toast.error(spreadRefusalMessage(err?.code, action === 'spread' ? 'Failed to spread the charges. Please try again.' : 'Failed to undo the spread. Please try again.'));
    } finally {
      setAbsorbingCharges(false);
    }
  };

  const updateQuotationField = async (field: UnichandField | CompetitorField, value: number) => {
    if (!quotation) return;

    try {
      console.log('Updating quotation field:', { field, value, quotationId: quotation._id });
      const response = await updateQuotation(quotation._id, { [field]: value });

      if (response.success) {
        setQuotation(response.data);
        if (field === 'discountPercentage') setDiscountPercentage(value);
        else if (field === 'additionalCharges') setAdditionalCharges(value);
        else if (field === 'competitorDiscountPercentage') setCompetitorDiscountPercentage(value);
        else if (field === 'competitorAdditionalCharges') setCompetitorAdditionalCharges(value);
        console.log('Quotation field updated successfully');
      } else {
        throw new Error('Update failed');
      }
    } catch (err: any) {
      console.error('Failed to update quotation field:', err);
      throw err;
    }
  };

  /**
   * Bulk markup override: set every line's markup % in one action (lines stay individually editable).
   * GlobalMarkupBar has already asked, in the app (CS-1); this only writes. Resolves true once
   * the rate landed, so the bar clears its box. Failures are toasts, not native alerts, for the
   * same reason the question is not a native confirm: a frozen tab hides Report a bug.
   */
  const handleApplyGlobalMarkup = async (pct: number): Promise<boolean> => {
    if (!quotation) return false;
    // Snapshot the current lines BEFORE overwriting, so the bulk change is reversible. (QA T3)
    const snapshot = quotation.items.map((it) => ({ ...it }));
    setApplyingGlobalMarkup(true);
    try {
      // CR-5 — Use the dedicated server endpoint. The server does the calc +
      // save atomically (no risk of concurrent-edit clobber vs the client-side
      // items-map approach we used before).
      const { applyGlobalMarkup } = await import('../lib/quotationApi');
      const response = await applyGlobalMarkup(quotation._id, pct);
      if (response.success) {
        setQuotation(response.data);
        setMarkupUndoSnapshot({ items: snapshot, pct });
        return true;
      }
      throw new Error('Update failed');
    } catch (err: any) {
      console.error('Failed to apply global markup:', err);
      // Fixed sentence, never `err.message` (invariant 6).
      toast.error(isLockedLineError(err) ? LOCKED_LINE_UNLOCK_MESSAGE : 'Failed to apply markup to all lines. Please try again.');
      return false;
    } finally {
      setApplyingGlobalMarkup(false);
    }
  };

  // Undo the last "Apply to all lines" by restoring the snapshot of line items. (QA T3)
  const handleUndoGlobalMarkup = async () => {
    if (!quotation || !markupUndoSnapshot) return;
    setApplyingGlobalMarkup(true);
    try {
      // A full revert to the remembered snapshot, by design (QA T3) — not a
      // one-line patch, so `buildItems` ignores `current` and returns the
      // snapshot verbatim. Still queued through `saveQuotationItems`, so this
      // restore cannot land in the middle of some OTHER save and be
      // overwritten by it landing after.
      const response = await saveQuotationItems(() => markupUndoSnapshot.items);
      if (response.success) {
        setMarkupUndoSnapshot(null);
      } else {
        throw new Error('Restore failed');
      }
    } catch (err: any) {
      console.error('Failed to undo global markup:', err);
      // Fixed sentence, never `err.message` (invariant 6).
      toast.error('Failed to undo. Please try again.');
    } finally {
      setApplyingGlobalMarkup(false);
    }
  };

  /**
   * The write itself, once the row's VAT is already answered
   * (`item.vat_applicable` boolean). Split out of `handleSearchSelect` so
   * the SAME write can run from either of its two callers: a pick whose row
   * already states VAT, or `handlePendingCataloguePickAnswer` once a blank row's "Does this item
   * have VAT?" question is settled.
   */
  const applyCataloguePick = async (
    index: number,
    item: MasterCatalogueItem,
    priceSelection?: CataloguePriceSelection
  ) => {
    // One pick, one dialog (D161.1): refused while another is open or saving.
    if (pickInFlightRef.current) return;
    pickInFlightRef.current = true;
    try {
      if (!quotation) return;
      // The array this pick is finally written into is rebuilt from the
      // freshest known state at save time, below (`saveQuotationItems`), not
      // from this closure's `quotation.items` — this line's OWN current value
      // is still read here, same as before.
      const currentItem = quotation.items[index];

      // "Is this row's price in TT$?" through the one owner, which the popup
      // and the Master Catalogue screen also call. Spelled out here, this
      // read a stated `currency: 'USD'` on a vendor-invoice row as TT$ and
      // divided a genuine US$ price by 6.5.
      const cataloguePricedTtd = catalogueRowIsTtd(item);
      // The row's own price, grossed for VAT when it carries it — the one
      // owner, `catalogueRowCostPrice` (owner rule, Deborah's VAT report,
      // 2026-09-24: a quotation line's COST is the final VAT-inclusive
      // price USC pays; input VAT is cost). This used to read `item_price`
      // raw here, so a vat_applicable row without a picked
      // `priceSelection` priced the line at the VAT-EXCLUSIVE figure.
      // At the Settings VAT rate — null for a VAT row while no rate is set.
      const catalogueCostPrice = catalogueRowCostPrice(item, vatRatePercent);
      // Prefer the unit price the user picked in the popup (per case/pack/kg/lb/L).
      // The TT$ branch goes through the owner: this read
      // `ttdToUsdRate ?? 0.1667` and priced a TT$ catalogue row at a rate
      // nobody had set whenever the real one failed to load — the system
      // DEFAULT, not the rate this quotation is quoted at, and the QA setting
      // has been 0.153846 since 2026-08-06. Same defect as TECH_DEBT #152 one
      // screen along, so the same answer: refuse, do not guess. No `|| 0`: a
      // row with no honest cost (no price, or VAT with no Settings rate) must
      // reach the refusal below, never price the line at US$0.00.
      // D85.6: a TT$ row's cost is ceiled ONCE from the unrounded conversion, which
      // is kept as the line's exact cost — `catalogueCostFacts` (tested) owns it.
      const costFacts = catalogueCostFacts({
        priceSelection,
        costPrice: catalogueCostPrice,
        pricedTtd: cataloguePricedTtd,
        ttdToUsd: ttdToUsdRate,
      });
      const unitPriceUsd = costFacts?.unitPrice ?? null;
      if (unitPriceUsd == null) {
        // Reads the owner's own test (`catalogueRowNeedsUnsetVatRate`) for
        // the message's wording — never a second, independently-derived
        // boolean; `costPriceTiersTtd`/`costUnitOptions` already refuse on
        // this exact condition, so this toast only has to word it.
        toast.error(
          catalogueRowNeedsUnsetVatRate(item, vatRatePercent)
            ? "VAT rate is not set in Settings, so this item's cost cannot be worked out."
            : "This item is priced in TT$ and this quotation's TT$ ↔ US$ rate has not loaded, so it cannot be costed. Reload the page and try again.",
        );
        return;
      }

      // THE QUANTITY MOVES WITH THE UNIT (QUO-00054 line 3). The vessel asked
      // for 20 CASE of "water 24*500ml"; picking this popup's "Per L" price put
      // TT$2.00 a litre on the line and kept the 20, so it quoted 20 L — one
      // case read as one litre, US$6.80 for goods worth US$80. The Unit
      // dropdown has restated the amount since 2026-08-27, from the RFQ's ask
      // first (ClickUp 86d478wv2); a pick that changes the unit is the same
      // statement and goes through the same owner, and the picked ROW's pack
      // (built on the server, `packStructureOfCatalogueRow`), since that is
      // the product the line is priced from now.
      //
      // Pack sources only — never the price ratio the dropdown falls back to:
      // the old and new prices belong to two different products. A pick whose
      // amount cannot be restated is refused, as the dropdown refuses a unit it
      // cannot reach. A unit the vocabulary does not know (a "C-12" unit
      // column) states nothing to convert through and keeps the count, as
      // before.
      //
      // Restated whenever the picked unit is not the line's OR not the RFQ's.
      // A pick in the line's own unit is still a conversion when the vessel
      // asked in another one — re-picking "Per L" on the line prod already
      // stores as 20 L must give 240 L, not keep the wrong 20. Only a pick in
      // the unit the line and the RFQ both state keeps a typed count.
      const pickedUom = priceSelection?.uom || item.unit || currentItem.uom || 'pcs';
      // "Per Pack" is one piece of the row's pack — the case price over its
      // count — so its amount is counted in pieces.
      // Only the still-generic 'pack' tier (one piece of the case) is counted
      // in PCS; a tier relabelled to the row's real piece unit (BTL, PCS —
      // `itemLookupRows.realPackUnit`, D64.10) is counted in that unit.
      const quantityUnit =
        priceSelection?.unitKey === 'pack' && isSameUnit(pickedUom, 'PACK') ? 'PCS' : pickedUom;
      const currentUom = currentItem.uom || 'PCS';
      const currentQty =
        typeof currentItem.quantity === 'number'
          ? currentItem.quantity
          : parseFloat(String(currentItem.quantity ?? '')) || 0;
      const asked = readOriginalRequest(currentItem);
      const isConversion =
        !isSameUnit(currentUom, pickedUom) || (asked?.uom != null && !isSameUnit(asked.uom, pickedUom));
      let pickedQuantity = currentItem.quantity;
      // Overridden below ONLY by the whole-number rule's fallback: the pick
      // is then applied at the unit the vessel actually asked in, never the
      // popup's chosen unit, and priced off the same row at THAT unit.
      let appliedUom = pickedUom;
      let appliedUnitPriceUsd = unitPriceUsd;
      let appliedExactUsd = costFacts?.unitPriceExact;
      let appliedPriceSelection = priceSelection;
      let pickNote: string | null = null;
      if (currentQty > 0 && canonicalUnit(quantityUnit) && isConversion) {
        const restated = restateQuantityThroughPack({
          rfqAsked: asked,
          anchor: readConversionAnchor(currentItem),
          currentQty,
          currentUom,
          targetUnit: quantityUnit,
          pack: item.packStructure ?? null,
          // F3 (D64.10): a unit the row reaches only through its own
          // conversion graph (1 PACK = 2 CASE) restates through that graph
          // when the pack cannot state the hop.
          conversions: item.unitConversions ?? null,
        });
        if (restated.ok) {
          pickedQuantity = restated.quantity;
        } else {
          // WHOLE-NUMBER CONVERSION RULE (owner, 2026-09-24): "if a case has
          // 150 kg then we shouldn't allow converting cause it doesn't make
          // sense." No rounding up, here or anywhere: the pick KEEPS the
          // unit and amount actually asked for and is repriced there off
          // the row just picked, never a guess. Both refusal reasons land
          // here — a ratio that does not divide evenly ('not_whole') and
          // the pre-existing "this row states no path at all" ('no_data') —
          // because both answer the same question ("can this pick restate
          // the amount honestly?") the same way: no.
          const target = normalizeUnit(pickedUom);
          const askedQty = restated.reason === 'not_whole' ? restated.quantity : (asked?.quantity ?? currentQty);
          const askedUnit = restated.reason === 'not_whole' ? restated.fromUnit : (asked?.uom ?? currentUom);
          const rowUnit = normalizeUnit(item.unit || '');
          const whyRefused =
            restated.reason === 'not_whole'
              ? `${restated.message}.`
              : `This line is ${asked?.uom ? `${asked.quantity} ${asked.uom}` : `${currentQty} ${normalizeUnit(currentUom)}`}, ` +
                `and "${item.item_name}" does not say how many ${target} that is, so the quantity cannot be put in ${target}.`;

          // The picked row's own price AT THE ASKED UNIT, through the same
          // owner the lookup panel itself uses to price the option a person
          // clicks (`itemLookupRows.priceAtUnit`). Never a second, ad hoc
          // conversion of the popup's chosen price.
          const fallback = priceAtUnit(
            { kind: 'catalogue', id: item._id, item },
            { unit: askedUnit, quotationUsdToTtd: ttdToUsdRate ? 1 / ttdToUsdRate : null, vatRatePercent },
          );
          if (fallback.ok) {
            pickedQuantity = askedQty;
            appliedUom = askedUnit;
            appliedUnitPriceUsd = fallback.usd;
            appliedExactUsd = fallback.usdExact;
            appliedPriceSelection = fallback.catalogueSelection;
            // Said inside the confirm below, not as a toast here: a toast
            // before the confirm announced a change a Cancel then never made.
            pickNote =
              `${whyRefused} Kept at ${askedQty} ${normalizeUnit(askedUnit)}, ` +
              `priced per ${normalizeUnit(askedUnit)} from "${item.item_name}" instead.`;
          } else {
            toast.error(
              `${whyRefused} "${item.item_name}" states no ${normalizeUnit(askedUnit)} price either, ` +
                `so the line is unchanged. ` +
                (rowUnit && rowUnit !== target ? `Pick the row's ${rowUnit} price instead, or add` : 'Add') +
                ` its packaging (Units / Case, Unit Size) on the Master Catalogue row first.`,
            );
            return;
          }
        }
      }

      // The price-application tail — everything from here down that turns a
      // resolved price into money on the line, the markup, and the
      // conversion anchor a future Unit change restates from — is the ONE
      // owner shared with the catalogue-addition lookup panel's typed-price
      // path (`linePriceApplication.applyLinePriceFacts`). See that file's
      // header for why it moved out, and QUO-00054 below for what the
      // anchor's exact-price override closes.
      const applied = applyLinePriceFacts(currentItem, {
        // `appliedUnitPriceUsd` is `unitPriceUsd` unchanged UNLESS the
        // whole-number rule's fallback repriced this pick at the asked unit
        // instead of the popup's chosen one (see above).
        unitPriceUsd: appliedUnitPriceUsd,
        // The TT$ this line is now priced at, pinned exactly as a generated
        // line pins it (`utils/pricing.unitCostTtd`). Two reasons, and the
        // second is a bug the old `...currentItem` spread was carrying:
        //
        //  1. Without the pin the TT$ cost cell derives back out of the US$
        //     cent and lands beside the catalogue's own figure but not on it —
        //     the catalogue says TT$345.00 and the line reads TT$345.02.
        //  2. `...currentItem` carried the REPLACED item's pin forward, so the
        //     new item's TT$ cost cell showed the OLD item's price, exactly and
        //     convincingly, beside the new item's US$.
        //
        // Null for a US$-priced row, which is precisely when deriving is right.
        // VAT-inclusive, through the same `catalogueRowCostPrice` owner as
        // `unitPriceUsd` above — never the raw, VAT-exclusive `item_price`.
        unitPriceTtd: appliedPriceSelection
          ? appliedPriceSelection.unitPriceTtdCost
          : cataloguePricedTtd && catalogueCostPrice != null
            ? ceilPrice(catalogueCostPrice)
            : null,
        uom: appliedUom,
        vat: item.vat_applicable || false,
        selectedVendor: item.supplier || undefined,
        quantity: pickedQuantity,
        markupPercent: markupToApply(currentItem, markupPercentage),
        // Anchored at the unrounded cost just chosen, as a supplier pick is
        // re-anchored — the popup's exact figure, not `unitPrice`, which is
        // ceiled to the cent: anchored at US$0.34 a litre, the next Unit
        // change quoted the 12 L case at US$4.08 against the row's US$4.00
        // (QUO-00054).
        exactUnitPriceUsd: appliedExactUsd,
      });

      const newItem = {
        ...currentItem,
        // The vessel's own wording, kept (2026-09-22 client call, JP /
        // Deborah, above the import) — this used to be `item.item_name`,
        // which replaced it with whatever the picked row was called.
        itemName: itemNameAfterCataloguePick({ currentName: currentItem.itemName, rowName: item.item_name }),
        // The shared owner, not a second copy of the mapping. This chain
        // used to be spelled out here and drifted from the server's the day
        // a hand-entered row stopped being a "price list" (QUO-00017).
        pricingSource: mapCatalogueSourceToPricingSource(item.source || '') as any,
        // The catalogue just answered for this line, so it no longer needs a
        // row: leaving `notInCatalogue` stale kept the pink "Not in
        // catalogue" cell AND made `quotationCatalogueHarvest.planHarvest`
        // (which only harvests where `notInCatalogue === true`) write a
        // genuine duplicate row at Finalized/Rejected — see
        // `catalogueFlagsAfterCataloguePick`.
        ...catalogueFlagsAfterCataloguePick(),
        ...applied.line,
      };

      // The row just picked is now what this line is priced from, so the line
      // records IT and stops describing the row it replaced. The spread above
      // used to carry `currentItem.pricingDetails` through untouched — the old
      // row's `masterCatalogueId`, its pack, and the conversion anchor a Unit
      // change restates FROM — so the next Unit change put the replaced row's
      // price back on the line, and the line's catalogue link highlighted a
      // row that no longer priced it.
      newItem.pricingDetails = pricingDetailsForCataloguePick({
        replaced: currentItem.pricingDetails,
        row: item,
        pricedPer: newItem.uom,
        anchor: applied.conversionAnchor,
      }) as QuotationItemDto['pricingDetails'];
      // The picked row's pack, in the remark the client reads (owner decision
      // 2026-09-26) — computed before the confirm, so its preview shows it.
      Object.assign(newItem, applyPackagingRemark(newItem));

      // THE IN-APP CONFIRM (owner decisions 2026-09-24) — asked only now,
      // with `newItem` fully computed, so the before -> after table shows
      // EXACTLY what "Replace" is about to write, never a second guess at
      // the price. Replaces `window.confirm("Are you sure you want to
      // replace this quotation item with the shown item?")`.
      if (pickChangesNothing(currentItem, newItem, ttdToUsdRate)) {
        await settleUnchangedPick(currentItem);
        return;
      }
      const confirmed = await confirm({
        title: 'Price this line from the picked item?',
        confirmLabel: 'Replace',
        body: (
          <>
            {pickNote && <p className="mb-2 text-xs text-amber-800">{pickNote}</p>}
            <LinePickPreview before={currentItem} after={newItem} ttdToUsdRate={ttdToUsdRate} />
          </>
        ),
      });
      if (!confirmed) return;

      // The panel shows a spinner until this lands or fails; further picks
      // are already refused (`pickInFlightRef`, taken at the start).
      setSavingPickIndex(index);
      const response = await saveQuotationItems((current) => {
        const updatedItems = [...current.items];
        updatedItems[index] = newItem;
        return updatedItems;
      });
      if (response.success) {
        // A short, subtle highlight so the user sees WHICH line just
        // changed, beside the before/after table they already confirmed.
        flashLine(index);
        if (pickNote) toast.info(pickNote);
      } else {
        // An unsuccessful (not thrown) response used to fall straight
        // through to closing the popup with the line unchanged and nothing
        // said — Deborah, 2026-09-22: "It said it is gonna go and update
        // it, but I clicked it, it didn't go — the prices remained what
        // the supplier column had." Thrown here, same as every other
        // items-update path in this file (`handleAddManualItem`,
        // `handleRefreshLinePrice`, `handleUndoGlobalMarkup`, …), so the
        // catch below reports it and the popup stays open on the unpriced
        // line rather than closing over a lost edit.
        throw new Error('Update failed');
      }

      setLookupPanelOpenIndex(null);
    } catch (err: any) {
      console.error("Failed to replace item", err);
      toast.error(replaceItemErrorMessage(err));
    } finally {
      pickInFlightRef.current = false;
      setSavingPickIndex(null);
    }
  };

  const handleSearchSelect = async (item: MasterCatalogueItem, priceSelection?: CataloguePriceSelection) => {
    if (lookupPanelOpenIndex === null) return;
    // A pick already saving refuses a second one rather than queuing it — the
    // guard for a click that gets through the disabled panel below (owner
    // decisions 2026-09-24: in-app confirm + spinner replace `window.confirm`,
    // and the new line is computed BEFORE the confirm is asked, never after).
    if (savingPickIndex !== null) return;

    // The in-app confirm with its before -> after preview is asked inside
    // `applyCataloguePick`, AFTER any VAT answer below, so the preview shows
    // the price the line will actually get.
    const index = lookupPanelOpenIndex;

    // The picked row's VAT is BLANK — nobody has said yes or no. Ask BEFORE
    // the line is written (bug fix, QUO-VAT2): the same-session re-price
    // used to run AFTER the write, from the row's BASE-unit cost, ignoring
    // whatever unit `priceSelection` named — a user who had picked "Per
    // Pack"/"Per kg" off a case row in the lookup panel's unit selector
    // (`costUnitOptions`) got the CASE price relabelled under the pack unit
    // the moment they answered. Held here, UNWRITTEN, until
    // `CatalogueVatPrompt` answers (`handlePendingCataloguePickAnswer` below). The lookup panel closes now,
    // the same point a normal pick already closes it (once it writes), so
    // only the VAT popup is left on screen.
    if (item.vat_applicable == null && item._id) {
      setLookupPanelOpenIndex(null);
      setPendingCataloguePick({ index, item, priceSelection });
      return;
    }

    // The row's fact was served at the rate the server read at search time;
    // `applyCataloguePick` grosses the cost at the rate this page loaded. Not
    // one reading -> not priced (D82 pre-push review).
    if (!costVatAgreesWithLoadedRate(item, vatRatePercent)) {
      toast.error(VAT_RATE_CHANGED_MESSAGE);
      return;
    }

    try {
      await applyCataloguePick(index, item, priceSelection);
    } catch (err: any) {
      console.error('Failed to replace item', err);
      toast.error(replaceItemErrorMessage(err));
    }
  };

  /**
   * Yes/No from `CatalogueVatPrompt` for a PENDING catalogue pick — nothing
   * has been written yet (`pendingCataloguePick`, `handleSearchSelect`'s own
   * comment above). `setCatalogueVat` first, exactly as the OTHER two VAT
   * entry points save it (`answerCatalogueVatPrompt`), then re-derive the
   * popup's own unit choice for the ANSWERED row
   * (`pendingCataloguePickVat.priceCataloguePickForVatAnswer` — the same
   * `priceAtUnit` owner the lookup panel itself used to build
   * `priceSelection`, never a second pricing formula), then the one write
   * (`applyCataloguePick`) any catalogue pick uses.
   */
  const handlePendingCataloguePickAnswer = async (vatApplicable: boolean) => {
    if (!pendingCataloguePick) return;
    const { index, item, priceSelection } = pendingCataloguePick;
    setSavingVatPrompt(true);
    try {
      const answer = await setCatalogueVat(item._id, vatApplicable);
      // RE-FETCHES the row, with or without a unit choice —
      // its stored tiers are computed server-side and the ones on `item` are
      // still the pre-answer, no-VAT figures (`pendingCataloguePickVat.ts`'s
      // own header, "THE SECOND BUG THIS CLOSED").
      const { item: answeredItem, priceSelection: nextSelection } = await priceCataloguePickForVatAnswer(
        {
          item,
          priceSelection,
          vatApplicable,
          costVatPercent: answer.costVatPercent,
          quotationUsdToTtd: ttdToUsdRate ? 1 / ttdToUsdRate : null,
          vatRatePercent,
        },
        { getCatalogueItem: getMasterCatalogueItem }
      );
      await applyCataloguePick(index, answeredItem, nextSelection);
    } catch (err: any) {
      console.error('Failed to replace item', err);
      toast.error(replaceItemErrorMessage(err));
    } finally {
      // The pick ends with its answer, success or not (D101.1): the popup has no Cancel, so a
      // failure that left it open would be a dead end. Nothing was written without an answer.
      setPendingCataloguePick(null);
      setSavingVatPrompt(false);
    }
  };

  /**
   * "Does this item have VAT?" — Yes / No from `CatalogueVatPrompt`, for the
   * ONE remaining entry point where the line ALREADY EXISTS with no row in
   * memory: a generated line's "VAT?" badge (`vatPrompt`'s own state comment
   * above). Thin: `answerCatalogueVatPrompt` (dependency-injected, tested on
   * its own) owns the save, the re-fetch-by-id, and the price-at-the-line's-
   * unit; this wrapper supplies the real API calls, the line's own current
   * unit, and `applyCataloguePick` — the SAME write any catalogue pick uses,
   * so the badge answer shows the same before -> after confirm rather than
   * silently re-pricing through a NAME-based search that could land on a
   * different row (`answerCatalogueVatPrompt.ts`'s own header). A
   * same-session catalogue PICK is `handlePendingCataloguePickAnswer`
   * instead, and a same-session supplier-dropdown pick is
   * `handlePendingSupplierVatPickAnswer`.
   */
  const handleCatalogueVatAnswer = async (vatApplicable: boolean) => {
    if (!vatPrompt || !quotation) return;
    setSavingVatPrompt(true);
    try {
      const line = quotation.items[vatPrompt.index];
      await answerCatalogueVatPrompt(
        {
          index: vatPrompt.index,
          masterCatalogueId: vatPrompt.masterCatalogueId,
          vatApplicable,
          lineUom: line?.uom || 'pcs',
          quotationUsdToTtd: ttdToUsdRate ? 1 / ttdToUsdRate : null,
          vatRatePercent,
        },
        {
          setCatalogueVat,
          getCatalogueItem: getMasterCatalogueItem,
          applyCataloguePick,
        }
      );
      setVatPrompt(null);
    } catch (err: any) {
      console.error('Failed to save VAT answer', err);
      // D64.10 B: in-app, and a fixed sentence — never `err.message` (invariant 6).
      toast.error(
        err?.code === VAT_RATE_CHANGED
          ? VAT_RATE_CHANGED_MESSAGE
          : lookupItemPriceErrorMessage(err, 'Failed to save the VAT answer. Please try again.'),
      );
    } finally {
      setSavingVatPrompt(false);
    }
  };

  /** Cancel: the line stays priced without VAT, and the "VAT?" badge stays too — nothing is saved. */
  const handleCatalogueVatCancel = () => setVatPrompt(null);

  /**
   * `ItemLookupPanel`'s "+ Add this item to Master Catalogue" —
   * `CatalogueItemForm`'s `onSubmitOverride`, so this owns what "saving"
   * means here, the same way the Review page's own override owns
   * edit-then-approve.
   *
   * A thin wrapper: `addCatalogueItemFromLine` (feature folder, dependency-
   * injected and unit-tested on its own — see that file's header) owns the
   * two-things-happen orchestration and the refusal; this supplies the real
   * API calls and the page's own state.
   */
  const handleAddToCatalogueFromLookup = async (
    payload: CreateCatalogueItemPayload
  ): Promise<MasterCatalogueItem | null> => {
    // The panel's line, or (D107) the line whose cost cell opened the popup directly.
    const index = lookupPanelOpenIndex ?? catalogueAddIndex;
    if (index === null || !quotation) return null;

    await addCatalogueItemFromLine(
      {
        quotationId: quotation._id,
        quotationNumber: quotation.quotationNumber,
        lineIndex: index,
        // Freshest known items, not this closure's `quotation.items` — this
        // function itself awaits a network call (`submitCatalogueAddition`)
        // before its own write, the same staleness window every other save
        // on this page now closes.
        items: quotationRef.current?.items ?? quotation.items,
        payload,
        ttdToUsdRate,
        markupPercentage,
        vatRatePercent,
      },
      {
        submitCatalogueAddition,
        // Queued through the same save queue as every other items-write on
        // this page, so this cannot land in the middle of, or be clobbered
        // by, a grid edit saving concurrently. `addCatalogueItemFromLine`
        // already computed the full array it wants written; `buildItems`
        // here only needs to hand that array through.
        updateQuotationItems: (items) => saveQuotationItems(() => items),
      }
    );

    // `saveQuotationItems` already called `setQuotation` with this same
    // object on success (`addCatalogueItemFromLine` returns `response.data`
    // unchanged).
    await refreshCatalogueSubmissionMarkers();
    setLookupPanelOpenIndex(null);
    setCatalogueAddIndex(null);
    // No `MasterCatalogueItem` exists yet — only a HELD submission, released
    // when this quotation is sent/finalized (straight to a row for an admin,
    // to the Review page for everyone else).
    return null;
  };

  // ----------- Custom Expenses Helpers (Unichand's Total) -----------
  // Add and edit work on the SHOWN rows, so the unstored default row is saved with them (D159.1).
  const handleAddCustomExpense = async (insertIndex: number) => {
    if (!quotation) return;
    try {
      const newExpenses = customExpensesAfterInsert(quotation.customExpenses, insertIndex);
      const response = await updateQuotation(quotation._id, { customExpenses: newExpenses });
      if (response?.success && response?.data) {
        setQuotation(response.data);
      }
    } catch (err: any) {
      console.error('Failed to add custom expense:', err);
      // Fixed sentence, never `err.message` (invariant 6).
      toast.error('Failed to add custom expense. Please try again.');
    }
  };

  const handleUpdateCustomExpense = async (index: number, updates: Partial<{ label: string; amount: number; currency?: string; notes?: string }>) => {
    if (!quotation) return;
    const newExpenses = customExpensesAfterEdit(quotation.customExpenses, index, updates);
    if (!newExpenses) return;
    try {
      const response = await updateQuotation(quotation._id, { customExpenses: newExpenses });
      if (response.success) {
        setQuotation(response.data);
      }
    } catch (err: any) {
      console.error('Failed to update custom expense:', err);
      // Fixed sentence, never `err.message` (invariant 6).
      toast.error('Failed to update custom expense. Please try again.');
      throw err;
    }
  };

  const handleRemoveCustomExpense = async (index: number) => {
    if (!quotation) return;
    try {
      const newExpenses = (quotation.customExpenses || []).filter((_, i) => i !== index);
      const response = await updateQuotation(quotation._id, { customExpenses: newExpenses });
      if (response.success) {
        setQuotation(response.data);
      }
    } catch (err: any) {
      console.error('Failed to remove custom expense:', err);
      // Fixed sentence, never `err.message` (invariant 6).
      toast.error('Failed to remove custom expense. Please try again.');
    }
  };

  // Remove a single line item from the quote (QA Ticket 2B). Renumbers the remaining lines and
  // persists via the same items-update path used everywhere else.
  const [removingItemIndex, setRemovingItemIndex] = useState<number | null>(null);
  const handleRemoveItem = async (index: number) => {
    if (!quotation) return;
    const target = quotation.items[index];
    const ok = await confirm({
      title: `Remove "${target?.itemName || `line ${index + 1}`}" from this quote?`,
      tone: 'error',
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    setRemovingItemIndex(index);
    try {
      // `response.success` is checked only to decide whether to set state —
      // same as the code this replaces, which had no `else` branch either.
      await saveQuotationItems((current) =>
        current.items
          .filter((_, i) => i !== index)
          .map((it, i) => ({ ...it, lineNumber: i + 1 })),
      );
    } catch (err: any) {
      console.error('Failed to remove item:', err);
      // Fixed sentence, never `err.message` (invariant 6).
      toast.error('Failed to remove item. Please try again.');
    } finally {
      setRemovingItemIndex(null);
    }
  };

  // Re-price ONE line by re-running the same lookup `AddQuotationItemModal`
  // uses, without regenerating the whole quotation. D157.1: a line a person
  // matched to a catalogue row (`pinnedCatalogueRowId`) is re-priced from THAT
  // row, never re-searched by name, so its match label and pick mark stay.
  //
  // Written for TECH_DEBT #61's flagged lines: a line priced honestly in a
  // different unit than the RFQ asked for, which a reviewer fixes by adding
  // Units/Case in Master Catalogue and then re-pricing. It is no longer
  // limited to those. A stored quotation does not re-price itself, so when a
  // PRICING RULE changes — and several did on 2026-08-26/27 — every existing
  // line keeps whatever it was written with, flagged or not. The lines that
  // were confidently wrong are the ones that most need this, and they are
  // exactly the ones that never showed a flag.
  //
  // Re-asks WHAT THE VESSEL ASKED — `originalRequest`, its count in its unit —
  // and takes back the count and unit the server priced, as one pair. Only a
  // line with no recorded ask (added by hand, or quoted before 2026-08-28) is
  // re-asked in its own count and unit.
  //
  // QUO-00054 (D51): it used to ask in `unitRequested` with the line's CURRENT
  // count. The Unit dropdown restates the count and leaves `unitRequested`
  // alone, so a line converted to 240 L that still carried unitRequested CASE
  // came back as 240 CASE — twelve times the order.
  const [refreshingPriceIndex, setRefreshingPriceIndex] = useState<number | null>(null);
  const handleRefreshLinePrice = async (index: number) => {
    if (!quotation) return;
    const item = quotation.items[index];
    if (!item) return;
    setRefreshingPriceIndex(index);
    try {
      const asked = readOriginalRequest(item);
      const lineQuantity =
        typeof item.quantity === 'number' ? item.quantity : parseFloat(String(item.quantity)) || 0;
      const ask =
        asked && asked.uom && asked.quantity > 0
          ? { quantity: asked.quantity, uom: asked.uom }
          : { quantity: lineQuantity > 0 ? lineQuantity : 1, uom: item.uom || 'pcs' };
      const pinnedRowId = pinnedCatalogueRowId(item);
      const result = await lookupItemPrice({
        itemName: item.itemName,
        quantity: ask.quantity,
        uom: ask.uom,
        // The server composes the new remark from the line's own (D119.5): not re-spelled here.
        internalRemarks: item.internalRemarks ?? null,
        ...(pinnedRowId ? { masterCatalogueId: pinnedRowId } : {}),
      });
      // No price found: the line stays exactly as it was. Moving its count and
      // unit while keeping the old price would put a per-L price on a CASE.
      if (result.unitPrice == null) {
        // A fixed client sentence, never the server's own composed
        // `internalRemarks` text (N3 owner re-audit, 2026-09-25) — that field
        // is provenance for the line's own remark cell, not vetted copy for a
        // toast.
        // B018: a row that was found but refused says why, in the Add Item form's words.
        const reason = lookupNoPriceReason(result, ask.uom);
        toast.error(
          reason
            ? `${reason} The line is left as it was.`
            : `No price was found for ${ask.quantity} ${ask.uom} of "${item.itemName}". The line is left as it was.`
        );
        return;
      }
      // D97: a line locked at its IFS list price is never moved to another unit by a refresh.
      const lockedRefusal = lockedUnitRefusal(item, result.uom || ask.uom);
      if (lockedRefusal) {
        toast.error(lockedRefusal);
        return;
      }
      // The array this refresh is finally written into is rebuilt from the
      // freshest known state at save time, below (`saveQuotationItems`), not
      // from this closure's `quotation.items`.
      const updated = { ...item };
      // The count the server priced, in the unit it priced it in — one pair.
      const quantity = typeof result.quantity === 'number' && result.quantity > 0 ? result.quantity : ask.quantity;
      updated.quantity = quantity;
      updated.uom = result.uom || ask.uom;
      updated.unitMismatch = !!result.unitMismatch;
      updated.unitRequested = result.unitMismatch ? (result.unitRequested ?? updated.unitRequested ?? null) : null;
      updated.unitMismatchReason = result.unitMismatch ? (result.unitMismatchReason ?? null) : null;
      if (result.unitPrice != null) {
        updated.unitPrice = ceilPrice(result.unitPrice);
        // Re-priced from the catalogue means re-pinned from the catalogue —
        // including back to null when the new source states US$, which is the
        // only honest answer once the TT$ this line used to carry belongs to a
        // price it no longer has.
        updated.unitPriceTtd = result.unitPriceTtd ?? null;
        // D85: and the unrounded price it was ceiled from (null when the lookup has none).
        updated.unitPriceExact = result.unitPriceExact ?? null;
        updated.totalPrice = ceilPrice(quantity * updated.unitPrice);
        Object.assign(
          updated,
          priceLineAt({
            unitCost: updated.unitPrice,
            unitCostExact: updated.unitPriceExact,
            quantity,
            markupPercent: markupToApply(updated, markupPercentage),
            statedSellingPrice: lockedUnitSellingPrice(updated),
          }),
        );
      }
      updated.pricingSource = result.pricingSource;
      updated.pricingConfidence = result.pricingConfidence;
      updated.pricingDetails = pricingDetailsAfterRefresh(item, result);
      // Refreshed with the rest, not left stale. This is the line that clears
      // the highlight after an item has just been added to the catalogue — the
      // lookup re-runs Step 1, finds the new row, and answers false. Leaving it
      // out would keep a sky-flagged line on screen for an item that is now in
      // the catalogue, and the flag would only correct itself on a full reload.
      // D99: the label follows the re-price; a changed catalogue row also drops a person's
      // match on screen (the server is the authority at save). `item` still holds
      // the pre-refresh pricingDetails the helper compares against.
      Object.assign(updated, mergeLookupMatch({ ...updated, pricingDetails: item.pricingDetails }, result), {
        pricingDetails: updated.pricingDetails,
      });
      updated.selectedVendor = result.selectedVendor ?? updated.selectedVendor;
      updated.vendorOptions = result.vendorOptions || updated.vendorOptions;
      // The line's own remark, composed by the server: the old price's notes out, the new in, the
      // buyer's, the RFQ match's and typed text kept (D119.5). Nothing left is saved as no remark
      // (the save replaces the whole line), never the old text kept.
      updated.internalRemarks = result.internalRemarks ?? undefined;
      // Re-priced means re-packed: the client-visible packaging sentence is
      // restated from the pack just returned (owner decision 2026-09-26).
      Object.assign(updated, applyPackagingRemark(updated));

      const response = await saveQuotationItems((current) => {
        const updatedItems = [...current.items];
        updatedItems[index] = updated;
        return updatedItems;
      });
      if (!response.success) {
        throw new Error('Update failed');
      }
    } catch (err: any) {
      console.error('Failed to refresh line price:', err);
      // The `code` first (invariant 6: never string-match server prose) — a
      // VAT-applicable row this Refresh just re-resolved with no Settings
      // rate loaded is a named, expected refusal, not a generic failure. This
      // path is now reachable from `answerCatalogueVatPrompt`'s badge answer.
      // Anything else: a fixed sentence, never `err.message`.
      toast.error(
        isLineCostNeedsVendorError(err)
          ? LINE_COST_NEEDS_VENDOR_MESSAGE
          : lookupItemPriceErrorMessage(err, 'Failed to refresh price. Please try again.'),
      );
    } finally {
      setRefreshingPriceIndex(null);
    }
  };

  // ----------- Competitor Custom Expenses (Competitor's Total) -----------
  const competitorCustomExpenses = quotation?.competitorCustomExpenses ?? [];

  const handleAddCompetitorCustomExpense = async (insertIndex: number) => {
    if (!quotation) return;
    try {
      const newExpenses = customExpensesAfterInsert(quotation.competitorCustomExpenses, insertIndex);
      const response = await updateQuotation(quotation._id, { competitorCustomExpenses: newExpenses });
      if (response?.data) setQuotation(response.data);
    } catch (err: any) {
      console.error('Failed to add competitor custom expense:', err);
      // Fixed sentence, never `err.message` (invariant 6).
      toast.error('Failed to add custom expense. Please try again.');
    }
  };

  const handleUpdateCompetitorCustomExpense = async (index: number, updates: Partial<{ label: string; amount: number; currency?: string; notes?: string }>) => {
    if (!quotation) return;
    const newExpenses = customExpensesAfterEdit(competitorCustomExpenses, index, updates);
    if (!newExpenses) return;
    try {
      const response = await updateQuotation(quotation._id, { competitorCustomExpenses: newExpenses });
      if (response.success) setQuotation(response.data);
    } catch (err: any) {
      console.error('Failed to update competitor custom expense:', err);
      // Fixed sentence, never `err.message` (invariant 6).
      toast.error('Failed to update custom expense. Please try again.');
      throw err;
    }
  };

  const handleRemoveCompetitorCustomExpense = async (index: number) => {
    if (!quotation) return;
    try {
      const newExpenses = competitorCustomExpenses.filter((_, i) => i !== index);
      const response = await updateQuotation(quotation._id, { competitorCustomExpenses: newExpenses });
      if (response.success) setQuotation(response.data);
    } catch (err: any) {
      console.error('Failed to remove competitor custom expense:', err);
      // Fixed sentence, never `err.message` (invariant 6).
      toast.error('Failed to remove custom expense. Please try again.');
    }
  };

  useEffect(() => {
    loadQuotation();
    loadExchangeRate();
    loadVatRate();
  }, [id]);


  const formatDate = (dateString: string) => {
    return fmtDateTime(dateString);
  };

  if (loading) {
    return (
      <div className="h-full bg-brand-bg">
        <LoadingState text="Loading quotation..." />
      </div>
    );
  }

  if (error || !quotation) {
    return (
      <div className="h-full flex items-center justify-center bg-brand-bg">
        <div className="text-center">
          <div className="text-sm font-semibold text-red-600 mb-2">Error Loading Quotation</div>
          <div className="text-xs text-slate-500 mb-4">{error}</div>
          <button
            onClick={() => navigate('/quotations')}
            className="px-4 py-2 rounded-md bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600"
          >
            Back to Quotations
          </button>
        </div>
      </div>
    );
  }

  return (
    // Focus view covers the sidebar (no z-index) and the header (z-[45]) but must stay BELOW every popup
    // the grid opens: the supplier card and the quote-send and other dialogs are z-[70], the add-to-catalogue and
    // VAT prompts z-[80]. At z-[90] they opened behind the table and the Supplier cell looked dead.
    <div className={`flex flex-col bg-brand-bg ${focusMode ? 'fixed inset-0 z-[60] bg-white' : 'h-full'}`}>
      <style>{stickyScrollbarStyles}</style>
      {unitNotice && (
        <div
          role="alert"
          style={{ top: unitNotice.top, left: unitNotice.left }}
          className="fixed z-[1000] max-w-[320px] flex items-start gap-2 rounded-md border border-red-200 bg-white px-3 py-2 text-xs text-slate-800 shadow-lg"
        >
          <span className="flex-1">{unitNotice.message}</span>
          <button
            type="button"
            onClick={() => setUnitNotice(null)}
            aria-label="Dismiss"
            className="text-slate-400 hover:text-slate-600 leading-none"
          >
            ×
          </button>
        </div>
      )}
      {/* Header or Focus Mode bar */}
      {focusMode ? (
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50 shrink-0">
          <span className="text-sm font-medium text-slate-800">Focus Mode — {quotation.quotationNumber}</span>
          <button
            onClick={() => setFocusMode(false)}
            className="px-3 py-1.5 rounded-md bg-slate-200 hover:bg-slate-300 text-sm font-medium text-slate-800"
          >
            Exit Focus
          </button>
        </div>
      ) : (
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 pt-4 pb-4 bg-brand-bg">
        <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 shrink">
            <BackArrow label="Back to quotations" onClick={() => navigate('/quotations')} />
            <div className="flex flex-col gap-0.5 min-w-0">
              {(() => {
                // The RFQ's subject (`loadedRfq`, read once on load - never off
                // `quotation`, whose shape depends on which call last answered), or
                // "Quotation <#>" when the RFQ has none. Strip common reply/forward
                // prefixes for readability.
                const rawSubject = loadedRfq.subject || '';
                const cleanedSubject = String(rawSubject)
                  .replace(/^(?:\s*(?:re|fw|fwd|aw|sv)\s*:\s*)+/i, '')
                  .trim();
                const headline = cleanedSubject || `Quotation ${quotation.quotationNumber}`;

                // Customer often comes through as `"Name" <email>` — show just the
                // human-readable name in the chip; keep the full thing in the tooltip.
                const customerRaw = quotation.customer?.customerName ?? '';
                const customerNameMatch = customerRaw.match(/"?([^"<]+?)"?\s*<[^>]+>/);
                const customerLabel = customerNameMatch
                  ? customerNameMatch[1].trim()
                  : customerRaw.trim();

                const chipClass =
                  'inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-xs sm:text-xs text-slate-600 truncate max-w-[180px] sm:max-w-[260px]';

                return (
                  <>
                    <div className="flex items-center gap-2 min-w-0">
                      <h1
                        className="truncate text-xl font-semibold leading-7 text-slate-900"
                        title={rawSubject || `Quotation ${quotation.quotationNumber}`}
                      >
                        {headline}
                      </h1>
                      <ModuleInfoButton content={QUOTATION_DETAIL_INFO_CONTENT} ariaLabel="Quotation detail information" />
                    </div>
                    <div className="flex items-center gap-1.5 min-w-0 flex-wrap text-xs sm:text-xs text-slate-500">
                      <span className={chipClass} title={`Quotation ${quotation.quotationNumber}`}>
                        {quotation.quotationNumber}
                      </span>
                      {(quotation.revisionNumber ?? 0) > 0 && quotation.revisionRootNumber ? (
                        <>
                          <span className="text-slate-300">·</span>
                          {/* D45.2: a version made because the client's quotation had been sent. */}
                          <span className={chipClass} title="A sent quotation is never edited; changes go into a new version">
                            Version {quotation.revisionNumber} of {quotation.revisionRootNumber}
                          </span>
                        </>
                      ) : null}
                      {quotation.rfqNumber ? (
                        <>
                          <span className="text-slate-300">·</span>
                          <span className={chipClass} title={`RFQ ${quotation.rfqNumber}`}>
                            {quotation.rfqNumber}
                          </span>
                        </>
                      ) : null}
                      {vesselName ? (
                        <>
                          <span className="text-slate-300">·</span>
                          <span className="truncate max-w-[260px]" title="Vessel">
                            Vessel: {vesselName}
                          </span>
                        </>
                      ) : null}
                      {/* What the buyer's own document says: their RFQ / order number, port, dates,
                          currency. Read from the RFQ as the load populated it (`loadedRfq`), never
                          off `quotation.rfqId`, which is a bare id once anything has been saved. */}
                      {quotationRfqFactRows({ rfqId: loadedRfq }).map((fact) => (
                        <React.Fragment key={fact.key}>
                          <span className="text-slate-300">·</span>
                          <span className="truncate max-w-[260px]" title={fact.hint}>
                            {fact.label}: {fact.value}
                          </span>
                        </React.Fragment>
                      ))}
                      {customerLabel ? (
                        <>
                          <span className="text-slate-300">·</span>
                          <span className="truncate max-w-[260px]" title={customerRaw}>
                            {customerLabel}
                          </span>
                        </>
                      ) : null}
                      {ttdToUsdRate ? (
                        <>
                          <span className="text-slate-300">·</span>
                          <span
                            className={chipClass}
                            title={
                              quotation.exchangeRate?.lockedAt
                                ? `Rate locked when this quotation was generated (${fmtDateTime(
                                    quotation.exchangeRate.lockedAt
                                  )}). Later changes in Settings do not affect it.`
                                : 'Rate this quotation is priced at. Later changes in Settings do not affect it.'
                            }
                          >
                            {USD_LABEL}1 = {TTD_LABEL}{(1 / ttdToUsdRate).toFixed(2)}
                          </span>
                        </>
                      ) : null}
                    </div>
                    {/*
                      Only the RFQ is known here — this screen never loads the
                      confirmed order, its purchase orders or the invoice, so
                      those hops are omitted and the rail reports them as not
                      loaded rather than asserting the order was never
                      confirmed.
                    */}
                    <ChainRail
                      current="quotation"
                      rfq={quotation.rfqId}
                      rfqNumber={quotation.rfqNumber}
                      quotation={quotation._id}
                      quotationNumber={quotation.quotationNumber}
                      confirmedOrder={confirmedOrder?._id}
                      confirmedOrderNumber={confirmedOrder?.quotationNumber}
                      className="mt-1"
                    />
                    {confirmedOrder && (
                      <div className="text-xs font-medium text-emerald-700" role="status">
                        Confirmed — order {confirmedOrder.quotationNumber || ''}{' '}
                        <Link to={`/confirmed-orders/${confirmedOrder._id}`} className="underline">
                          open the order
                        </Link>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 shrink-0">
            {/* Focus Mode - full screen table */}
            <Button
              onClick={() => setFocusMode(true)}
              title="Focus mode - hide sidebar and header for full table view"
              icon={<Maximize2 aria-hidden="true" className="h-4 w-4" />}
              className="touch-manipulation"
            >
              Focus
            </Button>

            {/* Export menu: PDF and Excel, each downloads on the click */}
            <ExportDropdown
              onExportPDF={handleDownloadPDF}
              onExportExcel={handleDownloadExcel}
              disabled={downloading}
            />

            {/* Client View Button. Secondary, like Focus and Export: the header keeps
                one filled button (docs/DESIGN.md), and that is Send quote on a
                finalized quotation or Confirm order on a sent one. */}
            <Button onClick={() => setShowClientView(true)} className="touch-manipulation">
              Client view
            </Button>

            {(quotation.revisionNumber ?? 0) > 0 && (
              <Button onClick={() => setShowCompareModal(true)} className="touch-manipulation">
                Compare with sent version
              </Button>
            )}

            {quotation.status === 'finalized' && (
              <Button
                variant={ordered.demote ? 'secondary' : 'primary'}
                onClick={openSendQuote}
                disabled={updating}
                className="touch-manipulation"
              >
                Send quote
              </Button>
            )}

            {(quotation.status === 'sent' || quotation.status === 'finalized') && ordered.showConfirm && (
              <Button
                variant={quotation.status === 'sent' && !ordered.demote ? 'primary' : 'secondary'}
                onClick={handleConfirmOrder}
                disabled={updating || confirmBlockers.length > 0}
                loading={confirmingOrder}
                title={
                  confirmBlockers.length > 0
                    ? `${describeConfirmationBlockers(confirmBlockers)}\n\n${confirmBlockers
                        .map((b) => `• ${b.message}`)
                        .join('\n')}`
                    : undefined
                }
                className="touch-manipulation"
              >
                {confirmingOrder ? 'Confirming…' : ordered.confirmLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Content */}
      <div className="flex-1 bg-brand-bg overflow-hidden flex flex-col">
        {/* The grid owns the vertical scroll, not this wrapper — see the note on the
            scroll container below. `min-h-0` at every level is what lets a flex child
            be SHORTER than its content; without it the chain silently falls back to
            content height and the table pushes its own scrollbar off screen again. */}
        <div className={`flex-1 min-h-0 flex flex-col px-2 sm:px-4 lg:px-6 overflow-y-auto ${focusMode ? 'py-2' : 'py-4 sm:py-6'}`}>
          <div className="w-full flex-1 min-h-0 flex flex-col space-y-6">

            {/* D81: once the RFQ is ordered, its quantities change on the order. */}
            <OrderedQuotationNote order={confirmedOrder} />

            {/* D45.1: changes the client asked for by email, applied here and waiting for review. */}
            {pendingAmendments.length > 0 && (
              <ClientAmendmentBanner
                amendments={pendingAmendments}
                onMarkReviewed={handleMarkAmendmentReviewed}
                reviewingId={reviewingAmendmentId}
              />
            )}

            {/* D99 item 4: advisory count of lines whose catalogue match nobody confirmed. */}
            <MatchReviewBanner items={quotation.items} sendBlocked={sendBlockedLines.length > 0} />

            {/* WHAT IS BLOCKING THE CONFIRMATION (ClickUp 86d478wf8).
                The ticket's acceptance is that the UI lists what is blocking
                BEFORE the user clicks, rather than failing after — so this is a
                panel on the page and not only the disabled button's tooltip.
                Shown only in the states where Confirm Order is offered at all;
                on any other status it would be advice about a button that is
                not there. The server refuses the same quotation for the same
                reasons whatever this panel says. */}
            {confirmBlockers.length > 0 &&
              (quotation.status === 'sent' || quotation.status === 'finalized') && (
              <div className="rounded-md border border-[#F3C6C0] bg-[#FDECEA] px-4 py-3" role="status">
                <div className="font-['Public_Sans'] font-semibold text-[13px] leading-[18px] text-slate-900">
                  {describeConfirmationBlockers(confirmBlockers)}
                </div>
                <ul className="mt-1.5 list-disc pl-5 space-y-0.5">
                  {confirmBlockers.map((blocker, i) => (
                    <li
                      key={`${blocker.code}-${blocker.lineNumber}-${i}`}
                      className="font-['Public_Sans'] font-normal text-xs leading-[18px] text-slate-900"
                    >
                      {blocker.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* WHAT FREEZING THIS QUOTATION PUT IN THE MASTER CATALOGUE
                (TECH_DEBT #258).

                The harvest writes catalogue rows on its own the moment the
                status becomes Finalized / Sent / Rejected. The toast that
                announces it is gone on the next reload, and until this panel
                existed there was no way to see afterwards what had been written
                — an automatic write to the price reference every future
                quotation is priced against, with no receipt.

                Read from the catalogue rows themselves rather than from anything
                stored on the quotation, so it cannot claim a row that is not
                there: delete one from the Master Catalogue page and it stops
                being listed here on the next load. */}
            {catalogueRows.length > 0 && (
              <div className="rounded-md border border-[#D7DBD9] bg-white px-4 py-3" role="status">
                <div className="font-['Public_Sans'] font-semibold text-[13px] leading-[18px] text-slate-900">
                  {catalogueRows.length} item{catalogueRows.length === 1 ? '' : 's'} added to the Master
                  Catalogue from this quotation
                </div>
                <p className="mt-0.5 font-['Public_Sans'] font-normal text-xs leading-[18px] text-slate-500">
                  Added automatically when this quotation was finalized, so the next quotation can price
                  them. Remove one if its price is wrong — it will not be added again.
                </p>
                <ul className="mt-2 space-y-1">
                  {catalogueRows.map((row) => (
                    <li
                      key={row._id}
                      className="font-['Public_Sans'] font-normal text-xs leading-[18px] text-slate-900"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="min-w-0">
                          <span className="font-medium">{row.item_name}</span>
                          {row.unit ? <span className="text-slate-500"> · per {row.unit}</span> : null}
                          {typeof row.item_price === 'number' ? (
                            <span className="text-slate-500">
                              {' '}
                              · {row.currency || 'USD'} {row.item_price.toFixed(2)}
                            </span>
                          ) : null}
                        </span>
                        <button
                          type="button"
                          onClick={() => void handleRemoveCatalogueRow(row._id, row.item_name)}
                          disabled={removingCatalogueRowId === row._id}
                          className="flex-shrink-0 text-xs leading-none text-rose-700 hover:text-rose-900 disabled:opacity-40"
                        >
                          {removingCatalogueRowId === row._id ? 'Removing…' : 'Remove'}
                        </button>
                      </div>

                      {/* A PRICE CHANGE THIS ROW WOULD NOT MAKE ON ITS OWN.
                          While the quotation is Sent, editing a cost carries the
                          catalogue row with it — but only up to 25%. Past that
                          the row keeps what it has and the question lands here,
                          because "US$30 became US$300" is a typed zero more
                          often than it is a cost, and this row is what every
                          future quotation is priced against. Durable: it lives
                          on the row, so it survives the reload a toast would
                          not, and it is still here tomorrow. */}
                      {row.pendingReprice && (
                        <div className="mt-1 mb-1.5 ml-0 flex flex-wrap items-center gap-x-2 gap-y-1 rounded border border-[#F0D9A8] bg-[#FDF6E7] px-2 py-1.5">
                          <span className="text-xs leading-[18px] text-slate-900">
                            {/* `fromPrice` is legitimately NULL for the two reasons
                                that are not about size — a row in another currency
                                and a row with no price on file. It used to be
                                rendered with `.toFixed()` regardless, which threw on
                                exactly those two and took the whole panel down with
                                it. Both figures are checked before they are printed. */}
                            {typeof row.pendingReprice.fromPrice === 'number' ? (
                              <>
                                Cost moved{' '}
                                <span className="font-medium">
                                  {row.pendingReprice.currency} {row.pendingReprice.fromPrice.toFixed(2)}
                                </span>{' '}
                                →{' '}
                              </>
                            ) : (
                              <>New cost </>
                            )}
                            <span className="font-medium">
                              {row.pendingReprice.currency}{' '}
                              {typeof row.pendingReprice.toPrice === 'number'
                                ? row.pendingReprice.toPrice.toFixed(2)
                                : '—'}
                            </span>
                            {row.pendingReprice.reason === 'size' &&
                            typeof row.pendingReprice.changePct === 'number' ? (
                              <span className="text-slate-500">
                                {' '}
                                ({row.pendingReprice.changePct > 0 ? '+' : ''}
                                {row.pendingReprice.changePct.toFixed(0)}%)
                              </span>
                            ) : null}
                            {row.pendingReprice.reason === 'currency' ? (
                              <span className="text-slate-500"> (this row is in another currency)</span>
                            ) : null}
                            {row.pendingReprice.reason === 'no_baseline' ? (
                              <span className="text-slate-500"> (this row had no price to compare)</span>
                            ) : null}
                          </span>
                          <span className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => void handleResolveReprice(row._id, row.item_name, 'apply')}
                              disabled={resolvingRepriceId === row._id}
                              className="text-xs leading-none font-medium text-primary-500 hover:text-[#08507f] disabled:opacity-40"
                            >
                              Apply
                            </button>
                            <span className="text-[#C9CCCB]">|</span>
                            <button
                              type="button"
                              onClick={() => void handleResolveReprice(row._id, row.item_name, 'keep')}
                              disabled={resolvingRepriceId === row._id}
                              className="text-xs leading-none text-slate-500 hover:text-slate-900 disabled:opacity-40"
                            >
                              Keep old
                            </button>
                          </span>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Items Table - Grid Layout */}
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden flex-1 min-h-0 flex flex-col">
              {/* Bulk markup override */}
              <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
                {/* Reset columns / Save columns (ClickUp 86d478wd1; saved per
                    user 2026-09-17). Each shows only when it would do something. */}
                <QuotationGridLayoutButtons grid={quotationGrid} />
                <GlobalMarkupBar
                  lineCount={quotation.items.length}
                  applying={applyingGlobalMarkup}
                  undoPct={markupUndoSnapshot ? markupUndoSnapshot.pct : null}
                  onApply={handleApplyGlobalMarkup}
                  onUndo={handleUndoGlobalMarkup}
                />
              </div>
              {/* The horizontal scrollbar has to be ON SCREEN, always. This box used to be
                  capped at `calc(100vh - 240px)` — a guess at how much page sat above it —
                  while the wrapper around it did the scrolling, so on any real quotation the
                  box ended below the fold and the bar with it: to scroll the grid RIGHT you
                  first had to scroll the page all the way DOWN. `flex-1` inside the flex
                  chain above sizes it to exactly the space that is left, so its bottom edge
                  is the bottom of the visible area and the bar sits there — as does the
                  "Add Item" footer under it. `minHeight` keeps it usable on a short viewport;
                  the wrapper's `overflow-y-auto` is the fallback if one ever gets shorter
                  than that. Header cells stay `position: sticky; top: 0` against THIS box. */}
              <div className="overflow-x-auto overflow-y-auto sticky-scrollbar flex-1" style={{ minHeight: '160px' }}>
                {/* `border-l` sits on the CONTAINER, not on the first cell. The
                    "#" cell used to carry it, which was fine while it was
                    permanently the leftmost column — once columns can be dragged
                    (ClickUp 86d478wd1), moving "#" took the grid's left edge with
                    it and left the table unbordered down one side. On the
                    container it is always drawn; when "#" IS first the two
                    coincide and nothing changes. */}
                <div
                  className="grid border-l border-solid border-[#D7DBD9]"
                  style={{
                    fontFamily: 'Public Sans, sans-serif',
                    // 24 columns, and the widths are now the USER'S — dragged on
                    // the header grips and saved to their account with Save
                    // columns (ClickUp 86d478wd1; per user since 2026-09-17). The default track list moved to
                    // `features/quotations/gridLayout.ts`, which is also where
                    // the column COUNT is stated: add or remove a column here
                    // and it must be added or removed there in the same change,
                    // or every stored layout is the wrong length. (It is
                    // detected and discarded rather than rendering a grid with
                    // a hole in it, but the user loses their layout.)
                    //
                    // That coupling was exercised immediately: this merge took
                    // Requested Qty (RFQ) IN at position 4 and Suggested Markup
                    // OUT at what was position 22, so the defaults moved 24 -> 25
                    // in the same change. 25 -> 24 on 2026-09-03, when
                    // "Packing Size Requested (RFQ)" was dropped: the RFQ has no
                    // pack-size field to fill it, so it could only ever print the
                    // VENDOR's pack under a client label (ClickUp 86d478wgu).
                    //
                    // The cells below stay in CANONICAL order in the source —
                    // `QuotationGridHeaderCells` / `QuotationGridRowCells`
                    // apply the display order, so reading this file still shows
                    // the columns in the order they were designed in.
                    gridTemplateColumns: templateColumnsFor(gridLayout),
                    gridAutoRows: 'min-content',
                    // WITHOUT THIS THE FROZEN COLUMNS DO NOT WORK, and they fail
                    // in a way that looks like the CSS was never applied.
                    //
                    // A sticky box is clamped to its CONTAINING BLOCK, which for
                    // these cells is this grid's content box. Overflowing fixed
                    // tracks do not widen a block box, so without a min-width
                    // this box stayed at the scroll container's own width — 981px
                    // measured on QA — while the 24 tracks totalled 3738. So
                    // `left: 0` could only pull the "#" cell as far as
                    // (boxRight - cellWidth) and it was then dragged off-screen
                    // with the scroll: measured at 182px against a 264px target,
                    // and Item Name / Description at -30.7px, both exactly the
                    // containing-block edge rather than the scrollport edge.
                    //
                    // `top: 0` on the header cells was never affected, which is
                    // why the row froze and the columns did not: the box DOES
                    // take its full content height (2481px), so a header cell can
                    // travel the whole way down it.
                    //
                    // `max-content` is the sum of the tracks, so the box spans
                    // the whole scrollable width and the clamp stops mattering.
                    // As a MIN, not a width: the box still fills a container
                    // wider than the table, which is what it did before.
                    minWidth: 'max-content'
                  }}
                >
                  {/* Header Row — the cells are written in CANONICAL order and
                      displayed in the user's. Each is made draggable and given a
                      resize grip by the wrapper, so the cells themselves stay a
                      plain description of what each column is (ClickUp 86d478wd1). */}
                  <QuotationGridHeaderCells
                    layout={gridLayout}
                    onMoveColumn={quotationGrid.moveColumn}
                    onResizeColumn={quotationGrid.resizeColumn}
                    onResetColumnWidth={quotationGrid.resetColumnWidth}
                  >
                  <div className="box-border flex flex-row justify-center items-center py-[3px] px-[8px] gap-[10px] bg-[#F9FAF9] border-l border-t border-b border-solid border-[#D7DBD9] rounded-tl-md" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-center text-slate-900">#</div>
                  </div>
                  <div className="box-border flex flex-row justify-center items-center py-[3px] px-[8px] gap-[10px] bg-[#F9FAF9] border border-solid border-[#D7DBD9]" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-slate-900">Item Name / Description</div>
                  </div>
                  {/* How much the vessel asked for, immediately left of what we
                      quoted. This is now the ONLY column that states the client's
                      ask: "Packing Size Requested (RFQ)" used to sit to its left
                      and was removed 2026-09-03, because `RFQItem` carries no
                      pack size and it therefore printed the vendor's own pack
                      under a client label (ClickUp 86d478wgu). Reads
                      `originalRequest` — the durable record, written once at
                      generation (TECH_DEBT #151) — and nothing else: the anchor is
                      the last point the line was ESTABLISHED at, which a hand edit
                      replaces, so passing it off as the request would quietly turn
                      "asked for" into "last typed". Empty stays empty for the same
                      reason. (ClickUp 86d478wv2.) */}
                  <div
                    className="box-border flex flex-row justify-center items-center py-[3px] px-[8px] gap-[10px] bg-[#F0F7F4] border-t border-r border-b border-solid border-[#D7DBD9]"
                    style={{ position: 'sticky', top: 0, zIndex: 10 }}
                    title="How much the client asked for on the RFQ line. Conversions are counted from this."
                  >
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[14px] flex flex-col items-center text-center text-primary-600">
                      <span>Requested Qty</span>
                      <span className="font-normal text-[#444]">(RFQ)</span>
                    </div>
                  </div>
                  <div className="box-border flex flex-row justify-center items-center py-[3px] px-[8px] gap-[10px] bg-[#F9FAF9] border-t border-r border-b border-solid border-[#D7DBD9]" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-center text-slate-900">Quantity</div>
                  </div>
                  <div className="box-border flex flex-row justify-center items-center py-[3px] px-[8px] gap-[10px] bg-[#F9FAF9] border-t border-r border-b border-solid border-[#D7DBD9]" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-center text-slate-900">Unit</div>
                  </div>
                  <div
                    className="box-border flex flex-row justify-center items-center py-[3px] px-[6px] gap-[10px] bg-[#F0F7F4] border-t border-r border-b border-solid border-[#D7DBD9]"
                    style={{ position: 'sticky', top: 0, zIndex: 10 }}
                    title="Cost price per kg, derived from pack text (e.g. 20×500g) or when the unit is already per kg"
                  >
                    {/* "Cost Price" is in the name because nothing else said so:
                        beside Unit Cost Price and Unit Selling Price, a bare
                        "Equiv. per KG" read as either (owner, 2026-09-17). */}
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[14px] flex flex-col items-center text-center text-primary-600">
                      <span>Equiv. Cost Price per KG</span>
                      <span className="font-normal text-[#444]">(USD / TTD)</span>
                    </div>
                  </div>
                  <div
                    className="box-border flex flex-row justify-center items-center py-[3px] px-[6px] gap-[10px] bg-[#F0F7F4] border-t border-r border-b border-solid border-[#D7DBD9]"
                    style={{ position: 'sticky', top: 0, zIndex: 10 }}
                    title="Cost price per lb when the line is per kg; otherwise per inner pack unit when derivable"
                  >
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[14px] flex flex-col items-center text-center text-primary-600">
                      <span>Equiv. Cost Price per lb / inner</span>
                      <span className="font-normal text-[#444]">(USD / TTD)</span>
                    </div>
                  </div>
                  <div className="box-border flex flex-row justify-center items-center py-[3px] px-[8px] gap-[10px] bg-[#F9FAF9] border-t border-r border-b border-solid border-[#D7DBD9]" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex flex-col items-center text-center text-slate-900">
                      <span>Unit Cost Price (USD)</span>
                      <span className="text-xs font-normal text-slate-500">per line unit</span>
                    </div>
                  </div>
                  <div className="box-border flex flex-row justify-center items-center py-[3px] px-[8px] gap-[10px] bg-[#F9FAF9] border-t border-r border-b border-solid border-[#D7DBD9]" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex flex-col items-center text-center text-slate-900">
                      <span>Unit Cost Price (TTD)</span>
                      <span className="text-xs font-normal text-slate-500">per line unit</span>
                    </div>
                  </div>
                  <div
                    className="box-border flex flex-row justify-center items-center py-[3px] px-[8px] gap-[10px] bg-[#F0F7F4] border-t border-r border-b border-solid border-[#D7DBD9]"
                    style={{ position: 'sticky', top: 0, zIndex: 10 }}
                    title="Pack size USC is quoting on, from the vendor invoice / master catalogue"
                  >
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[14px] flex flex-col items-center text-center text-primary-600">
                      <span>Packing Size</span>
                      <span className="font-normal text-[#444]">(Quoting)</span>
                    </div>
                  </div>
                  <div className="box-border flex flex-row justify-center items-center py-[3px] px-[8px] gap-[10px] bg-[#F9FAF9] border-t border-r border-b border-solid border-[#D7DBD9]" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-center text-slate-900">Total Cost Price (USD)</div>
                  </div>
                  {/* THE CLIENT-FACING REMARK. `item.remarks` is the only remark
                      channel any client export carries — see the client-visible
                      field owner, `utils/clientVisibleFields`. The header said
                      just "Remarks" beside another column saying "Internal
                      Remarks", and nothing told anyone which of the two the
                      client would receive; people typed into the internal one
                      and reported that remarks never reached the export
                      (ClickUp 86d478w9v). The second line is not decoration —
                      it is the answer to the question that was being asked. */}
                  <div className="box-border flex flex-col justify-center items-center py-[3px] px-[8px] bg-[#F9FAF9] border-t border-r border-b border-solid border-[#D7DBD9]" style={{ position: 'sticky', top: 0, zIndex: 10 }} title="Sent to the client. This is the ONLY remark that appears on the client PDF and the client Excel.">
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-center text-slate-900">Remarks</div>
                    <div className="font-['Public_Sans'] font-normal text-xs leading-[12px] text-primary-500">the client sees this</div>
                  </div>
                  <div className="box-border flex flex-row justify-center items-center py-[3px] px-[8px] gap-[10px] bg-[#F9FAF9] border-t border-r border-b border-solid border-[#D7DBD9]" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-center text-slate-900">Supplier</div>
                  </div>
                  <div className="box-border flex flex-row justify-center items-center py-[3px] px-[8px] gap-[10px] bg-[#F9FAF9] border-t border-r border-b border-solid border-[#D7DBD9]" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-center text-slate-900">Markup</div>
                  </div>
                  <div className="box-border flex flex-row justify-center items-center py-[3px] px-[8px] gap-[10px] bg-[#F9FAF9] border-t border-r border-b border-solid border-[#D7DBD9]" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-center text-slate-900">Unit Selling Price (USD)</div>
                  </div>
                  <div className="box-border flex flex-row justify-center items-center py-[3px] px-[8px] gap-[10px] bg-[#F9FAF9] border-t border-r border-b border-solid border-[#D7DBD9]" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-center text-slate-900">Total Selling Price (USD)</div>
                  </div>
                  <div className="box-border flex flex-row justify-center items-center py-[3px] px-[8px] gap-[10px] bg-[#F9FAF9] border-t border-r border-b border-solid border-[#D7DBD9]" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-center text-slate-900">Unit Selling Price (TTD)</div>
                  </div>
                  {/* Total Cost Price (TTD) — the money that actually leaves the business.
                      USC buys in Trinidad dollars, so this is the figure the owner reads
                      first. It sits immediately LEFT of Total Selling Price (TTD): cost then
                      price, the order they are read in, so the pair can be compared without
                      crossing the grid. The light red FILL is the whole highlight — the type
                      is the same weight and colour as every other column, because a column
                      that is tinted AND set differently reads as a different kind of thing
                      rather than as the same kind, marked.
                      Through ONE owner, never a conversion spelled out here:
                      `totalCostTtd` (BUSINESS_RULES#usd-cost-and-ttd-display, invariant 5), which is
                      Qty × the Unit Cost Price (TTD) beside it. That unit figure is
                      the TT$ the line was PRICED at when the price came from a TT$
                      source, and only otherwise a conversion of the US$ — so a
                      TT$100.00 catalogue row totals TT$100.00 here instead of the
                      TT$100.04 a round trip through the US$ cent used to produce.
                      A second conversion in this cell is exactly how the same
                      figure has shipped three different answers before. */}
                  <div className="box-border flex flex-row justify-center items-center py-[3px] px-[8px] gap-[10px] bg-[#FBE0DC] border-t border-r border-b border-solid border-[#D7DBD9]" style={{ position: 'sticky', top: 0, zIndex: 10 }} title="Qty × Unit Cost Price, in Trinidad dollars at the quotation's locked rate — what USC pays out on this line">
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-center text-slate-900">Total Cost Price (TTD)</div>
                  </div>
                  <div className="box-border flex flex-row justify-center items-center py-[3px] px-[8px] gap-[10px] bg-[#F9FAF9] border-t border-r border-b border-solid border-[#D7DBD9]" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-center text-slate-900">Total Selling Price (TTD)</div>
                  </div>
                  <div className="box-border flex flex-row justify-center items-center py-[3px] px-[8px] gap-[10px] bg-[#F9FAF9] border-t border-r border-b border-solid border-[#D7DBD9]" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-center text-slate-900">Competitor's Price (USD)</div>
                  </div>
                  <div className="box-border flex flex-col justify-center items-center py-[3px] px-[8px] bg-[#F9FAF9] border-t border-r border-b border-solid border-[#D7DBD9]" style={{ position: 'sticky', top: 0, zIndex: 10 }} title="Profit against cost price — profit as a % of COST, the markup this line realised after the discount. US$ and TT$, each with the % its own figures give.">
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-center text-slate-900">Profit against cost price</div>
                    <div className="font-['Public_Sans'] font-normal text-xs leading-[12px] text-slate-500">profit ÷ cost · US$ / TT$</div>
                  </div>
                  {/* No "Total Profit (USD)" column. It printed the WHOLE
                      quote's profit on every line — the same figure in all
                      forty rows — so it said nothing about the row it sat in.
                      The Pricing Summary under the grid states it once, from
                      the same `resolveQuotationTotals` call. */}
                  {/* NOT sent to the client. No client export reads
                      `internalRemarks`, and the client-visible field owner
                      lists it as internal-only by name. Paired with the
                      "the client sees this" line on the Remarks header so the
                      two columns answer the question together rather than
                      leaving it to be guessed (ClickUp 86d478w9v). */}
                  <div className="box-border flex flex-col justify-center items-center py-[3px] px-[8px] bg-[#F9FAF9] border-t border-r border-b border-solid border-[#D7DBD9]" style={{ position: 'sticky', top: 0, zIndex: 10 }} title="Internal only. Never appears on any client export — use the Remarks column for anything the client should read.">
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-center text-slate-900">Internal Remarks</div>
                    <div className="font-['Public_Sans'] font-normal text-xs leading-[12px] text-slate-500">never exported</div>
                  </div>
                  <div className="box-border flex flex-row justify-center items-center py-[3px] px-[8px] gap-[10px] bg-[#F9FAF9] border-t border-r border-b border-solid border-[#D7DBD9]" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-center text-slate-900">Internal More Details</div>
                  </div>
                  {/* Profit against selling price (was "Item Margin") — prod ticket
                      USC-BUG-0038 item 6, and the client's 2026-09-25/26 asks that the
                      two profit columns say which base each divides by, in the client's
                      own words: profit ÷ SELLING price (margin) is "Profit against
                      selling price"; profit ÷ cost (the realised markup — not titled
                      "Markup" because column 14 already is) is "Profit against cost
                      price". Both
                      state US$ and TT$. Appended here, last in canonical order, so
                      a layout saved before this shipped is one column short rather
                      than reshuffled (gridLayout.ts, `usableLayout`). */}
                  <div
                    className="box-border flex flex-col justify-center items-center py-[3px] px-[8px] bg-[#F9FAF9] border-t border-r border-b border-solid border-[#D7DBD9] rounded-tr-md"
                    style={{ position: 'sticky', top: 0, zIndex: 10 }}
                    title="Profit against selling price (gross margin) — profit as a % of the final selling price, after this line's share of any discount. US$ and TT$, each with the % its own figures give."
                  >
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-center text-slate-900">Profit against selling price</div>
                    <div className="font-['Public_Sans'] font-normal text-xs leading-[12px] text-slate-500">profit ÷ selling · US$ / TT$</div>
                  </div>
                  {/* Competitor Markup % (D97) - the 25th canonical column, appended last so saved
                      layouts stay repairable and SHIPPED right after Competitor's Price
                      (gridLayout.ts). Its cell is the last child of the row for the same reason. */}
                  <div
                    className="box-border flex flex-col justify-center items-center py-[3px] px-[8px] bg-[#F9FAF9] border-t border-r border-b border-solid border-[#D7DBD9]"
                    style={{ position: 'sticky', top: 0, zIndex: 10 }}
                    title={`The competitor's price against OUR current unit cost: (competitor price - unit cost) / unit cost. A dash when either is missing.${competitorWarnFailed ? ' The warning threshold could not be loaded, so nothing is highlighted.' : ''}`}
                  >
                    <div className="font-['Public_Sans'] font-medium text-xs leading-[18px] flex items-center text-center text-slate-900">Competitor Markup %</div>
                    <div className="font-['Public_Sans'] font-normal text-xs leading-[12px] text-slate-500">on our cost</div>
                  </div>
                  </QuotationGridHeaderCells>

                  {/* Data Rows */}
                  {quotation.items.map((item, index) => {
                    const usdPrice = ceilPrice(item.unitPrice || 0); // USD is the primary currency, properly rounded
                    // The line's cost (exact, D104.10), selling and markup from the one resolver. The
                    // per-KG / per-lb / per-inner equivalents below stay on the stored cent cost
                    // (`usdPrice`): the unit-change paths read them as conversion targets.
                    const lineSelling = resolveItemPricing(item, markupPercentage);
                    // The line's OWN TT$ cost: the figure it was priced at when
                    // that figure was TT$ (`unitPriceTtd`), and only otherwise a
                    // conversion of the US$ beside it. Deriving unconditionally is
                    // what made two lines off one TT$100.00 catalogue row read
                    // TT$100.04 and TT$100.10 (QUO-00013) — see `unitCostTtd`.
                    // Still null, never the US$ number, when there is no rate and
                    // nothing pinned.
                    const ttdPrice = unitCostTtd(item, ttdToUsdRate);
                    // D107: no price without a registered vendor. A line with no real vendor has
                    // no typeable cost; its cost cells open the Master Catalogue popup instead.
                    const costNeedsVendor = !lineHasRealVendor(item);
                    const hasCost = !lineIsUnpriced(item);
                    const altPricing = getAlternateQuotationPricing(
                      usdPrice,
                      item.uom || 'pcs',
                      item.itemName || '',
                      item.pricingDetails,
                      item.originalRequest?.uom
                    );
                    const ttdFromUsd = (usd: number | null) => usdToTtd(usd, ttdToUsdRate);
                    const perKgTtd = ttdFromUsd(altPricing.perKgUsd);
                    const lbOrInner = perLbOrInner(altPricing);
                    const lbOrInnerTtdValue = lbOrInner.kind === 'none' ? null : ttdFromUsd(lbOrInner.value);
                    const currentUomNormalized = normalizeUnit(item.uom || 'PCS');
                    // The line's OWN unit always leads, so trimming
                    // INDUSTRY_UNIT_OPTIONS can never strand a line already
                    // quoted in a unit that is no longer offered. After it come
                    // the units this particular line can actually answer in —
                    // including the outer pack its reference row is sold by,
                    // under that row's own name rather than a generic "CASE".
                    const rfqRelevantUnits = [
                      currentUomNormalized,
                      ...(altPricing.outerUomLabel ? [normalizeUnit(altPricing.outerUomLabel)] : []),
                      ...(altPricing.perKgUsd != null ? ['KG'] : []),
                      ...(altPricing.perLbUsd != null ? ['LB'] : []),
                      ...(altPricing.perLitreUsd != null ? ['L'] : []),
                    ];
                    // When the line has a pack on file, the reachable set is
                    // KNOWN, so offer exactly that. Appending INDUSTRY_UNIT_OPTIONS
                    // unconditionally is what put CASE and KG in front of a user
                    // on rows that could not answer in either, and then refused
                    // with "Cannot convert this line item to KG from current
                    // data" — a dropdown offering a choice it will not honour.
                    const packForOptions = readPackStructure(item);
                    // D170.4: never a unit across the ounce from the one the line was asked or established in.
                    const establishedForOptions = establishedUnits(item.originalRequest?.uom, item.pricingDetails, item.uom);
                    const reachableUnits = packForOptions
                      ? convertibleUnits(
                          currentUomNormalized,
                          [
                            ...INDUSTRY_UNIT_OPTIONS,
                            ...(altPricing.outerUomLabel ? [altPricing.outerUomLabel] : []),
                          ],
                          packForOptions
                        ).filter((unit) => !barredByWater(establishedForOptions, unit))
                      : [];
                    // With no pack on file the reachable set is not KNOWN, but it
                    // is still ASKABLE: `targetUnitPriceFor` is the function the
                    // click itself calls, so asking it here is the same question
                    // the user is about to ask, and the list cannot promise a
                    // unit the click will refuse. Filtering rather than trimming
                    // the static list keeps every unit that IS reachable by the
                    // legacy readings — OZ off a per-pound figure, a case size
                    // pattern-matched out of the item name — which a hand-written
                    // shorter list would have quietly stranded.
                    // Plus L where the line's own ounce reaches it, which no pack piece states (D170.4).
                    const lineUnitOptions =
                      reachableUnits.length > 0
                        ? dedupeUnitOptions([currentUomNormalized, ...reachableUnits, ...(altPricing.perLitreUsd != null ? ['L'] : [])])
                        : dedupeUnitOptions([
                            currentUomNormalized,
                            ...[...rfqRelevantUnits, ...INDUSTRY_UNIT_OPTIONS].filter(
                              (candidate) => targetUnitPriceFor(item, candidate, altPricing) != null
                            ),
                          ]);
                    // One resolver for the whole row: stored selling prices win, else cost ×
                    // markup, with a 0% markup honoured. The MIRRORED twin of
                    // src/utils/pricing.js, so the table, the Pricing Summary and the
                    // PDF/Excel exports cannot disagree about a line.
                    const dispTotalSelling = lineSelling.totalSellingPrice;
                    const dispUnitSelling = lineSelling.unitSellingPrice;
                    // Both Item Profit columns read these two — the same owners the
                    // Pricing Summary reads, so a line's profit is one figure everywhere.
                    const lineProfitUsd = quotationTotals.lines[index];
                    const lineProfitTtd = lineProfitUsd
                      ? resolveProfitTtd([item], lineProfitUsd.netSellingPrice, ttdToUsdRate)
                      : null;

                    // The Markup column shows the RATE THE LINE IS QUOTED AT, and nothing
                    // else. It used to show the rate divided back out of the rounded selling
                    // price — 30.2% on a line set to 30, 33.3% on a nine-cent one — because
                    // `ceilPrice` rounds every selling price UP to the next cent and a
                    // division cannot un-round it. 825 of 1,539 QA lines displayed a number
                    // nobody had chosen. (QA T1 asked for the grid to reconcile with the
                    // profit tooltip; it does that below, by naming both figures, instead of
                    // by putting a measurement in the column that sets the rule.)
                    // Two decimals, which is the precision `markup` is ever stored at, and
                    // no trailing zeros — a line quoted at 30 reads "30", not "30.00".
                    const setMarkupPct = Math.round(lineSelling.markup * 100) / 100;
                    const realizedPct = Math.round(lineSelling.realizedMarkup * 100) / 100;
                    // Does the stated rate actually PRODUCE the stored selling price?
                    //
                    // This distinguishes the two reasons realized can differ from set, and
                    // they are not remotely the same size. `ceilPrice` rounds every selling
                    // price up to the next cent, which moves a 30% line to 30.23% and is
                    // harmless. A cost that MOVED after the line was priced leaves the old
                    // selling price against the new cost, and that gap is unbounded — on
                    // QUO-00013 it was 3.77 points (ClickUp 86d46vbf5): cost went TT$103 to
                    // TT$100 and the US$20.60 stayed, so the line reads "30%" while earning
                    // 33.77% on a cost USC never paid.
                    //
                    // The old tooltip fired on ANY gap >= 0.01 and blamed all of them on the
                    // rounding cent, so the one place a reviewer could have caught that told
                    // them it was nothing. Ask the question `resolveMarkupPercent` asks —
                    // recompute and compare exactly, no tolerance band, because "within a
                    // cent of 30%" is the same guesswork in a smaller coat.
                    // `isPricedAtStatedRate` is the owner — the mirrored twin of
                    // src/utils/pricing.js, so an export asking the same question of the
                    // same line cannot get a different answer.
                    const rateProducesPrice = isPricedAtStatedRate(item, markupPercentage);
                    // A line whose price its own rate cannot reproduce. Not flagged as an
                    // error — nobody did anything wrong, and the money that was quoted is
                    // the money that was quoted — but it is the row where the Markup column
                    // is describing a rule the line is no longer priced by.
                    const markupStale = !rateProducesPrice && Math.abs(realizedPct - setMarkupPct) >= 0.01;
                    const expectedAtRate = sellingPriceAt(markupCostUsd(lineSelling.unitCost, item.unitPriceExact), setMarkupPct);
                    // D161.2: a line with no cost is quoted at nothing yet; the cell's rate is the one that will apply.
                    const markupTooltip = !hasCost
                      ? `Not priced yet: no cost was found for this line. The ${setMarkupPct}% applies once it has a cost.`
                      : markupStale
                      ? `Quoted at ${setMarkupPct}%, but this line is not priced at ${setMarkupPct}% any more. `
                        + `${setMarkupPct}% of the current cost is ${USD_LABEL}${expectedAtRate.toFixed(2)}; the stored selling price is `
                        + `${USD_LABEL}${dispUnitSelling.toFixed(2)}, which realizes ${realizedPct}%. The cost moved after this line was `
                        + `priced and the selling price did not follow. The quoted price stands — change the markup or the `
                        + `selling price only if you mean to reprice it.`
                      : Math.abs(realizedPct - setMarkupPct) >= 0.01
                        ? `Quoted at ${setMarkupPct}%. Realized ${realizedPct}% — unit selling is rounded up to the next cent.`
                        : `Quoted at ${setMarkupPct}%.`;
                    // ── What the row does not otherwise say about itself ──────────────
                    // A restated line reads "2 CASE at US$100.00" and is correct, but a
                    // reviewer cannot tell from it that the line was established as 20 PCS
                    // — the count and the price both moved, so nothing on the row is
                    // evidence that anything moved (TECH_DEBT #149). The anchor already
                    // knows; this only shows it. Deliberately NOT the amber the Unit cell
                    // uses for `unitMismatch`: that means "no ratio was on file, this is
                    // the honest price but not the asked unit", and a flag that also fires
                    // on an exact, money-preserving restatement stops meaning anything.
                    // It tints the cell rather than adding a glyph beside the dropdown —
                    // a third child in that cell starved the select to "CAS" — which also
                    // makes the whole cell the hover target for the explanation.
                    const rowAnchor = readConversionAnchor(item);
                    const rfqAsked = readOriginalRequest(item);
                    const restatedFrom =
                      rowAnchor && rowAnchor.quantity != null && normalizeUnit(rowAnchor.uom) !== currentUomNormalized
                        ? rowAnchor
                        : null;
                    const restatedTooltip = restatedFrom
                      ? `Restated: ${restatedFrom.quantity} ${restatedFrom.uom} at ${USD_LABEL}${restatedFrom.unitPrice.toFixed(2)} `
                        + `(${USD_LABEL}${(restatedFrom.quantity! * restatedFrom.unitPrice).toFixed(2)}) `
                        + `→ ${item.quantity} ${currentUomNormalized} at ${USD_LABEL}${exactCostText(lineSelling.unitCost)} `
                        + `(${USD_LABEL}${exactCostText(lineProfitUsd ? lineProfitUsd.cost : ceilPrice(item.totalPrice || 0))}). Same goods — the unit change moved the `
                        + `count with the price. Editing Quantity or Unit Cost makes this line the new baseline.`
                      : null;

                    // The reviewer's question the anchor could never answer: did we quote
                    // what was asked for? (TECH_DEBT #151.) The durable record is the only
                    // thing on the line that still knows, because a hand edit to Quantity
                    // or Unit Cost replaces the anchor on purpose — so a corrected row
                    // used to have no memory of the request at all. Said only where the
                    // answer is "not exactly": a line quoted as it was asked says so by
                    // saying nothing. A request that states no unit compares on the count
                    // alone rather than reading "no unit" as a difference.
                    const askedDiffers =
                      rfqAsked &&
                      ((rfqAsked.uom != null && rfqAsked.uom !== currentUomNormalized) ||
                        Number(item.quantity) !== rfqAsked.quantity);
                    const askedTooltip = askedDiffers
                      ? `RFQ asked for ${rfqAsked!.quantity}${rfqAsked!.uom ? ` ${rfqAsked!.uom}` : ''}; `
                        + `quoted ${item.quantity} ${currentUomNormalized}.`
                      : null;

                    // Owner decision 2026-09-24: "ideally we want to send the quotation
                    // in the unit they requested" — the ROW-WIDE amber nudge, from the
                    // one owner (`findRfqUnitMismatch`, features/quotations/
                    // rfqUnitMismatch.ts). UNIT only, unlike `askedDiffers` just above
                    // (which also fires on a quantity difference, for the blue
                    // "restated" cell mark): a line correctly restated to a new
                    // quantity in the same unit is never amber here, only a unit that
                    // does not match the RFQ's ask is. Advisory — nothing reads this to
                    // block anything; Send only asks about it (`openSendQuote`).
                    const rfqUnitMismatch = findRfqUnitMismatch(item);
                    const rfqUnitMismatchTooltip = rfqUnitMismatch
                      ? `RFQ asked ${rfqUnitMismatch.askedQty} ${rfqUnitMismatch.askedUom}, ` +
                        `${describeQuotedAmount(rfqUnitMismatch)}.`
                      : null;

                    // D51 (QUO-00054): is the amount on this line the vessel's ask
                    // in the line's own unit? Red, with the right figure, when it
                    // is not — Finalize, Send and Confirm refuse until it is
                    // corrected or somebody marks it right.
                    const amountCheck = lineAmountCheck(item);
                    const amountBlocks = blocksQuotation(amountCheck);
                    const accepted = (item.pricingDetails as Record<string, any> | undefined)?.amountAccepted;
                    const amountTooltip = amountBlocks
                      ? `${describeLineAmount(amountCheck)} This quotation cannot be finalized, sent or confirmed until `
                        + 'the quantity is corrected or marked right.'
                      : amountCheck.status === LINE_AMOUNT_STATUS.ACCEPTED
                        ? `Marked right${accepted?.by ? ` by ${accepted.by}` : ''}`
                          + `${accepted?.at ? ` on ${new Date(accepted.at).toLocaleDateString()}` : ''}: `
                          + `the vessel asked for ${amountCheck.asked?.quantity} ${amountCheck.asked?.uom}; `
                          + `quoted ${amountCheck.quoted.quantity} ${amountCheck.quoted.uom}.`
                        : null;

                    // A FLAG, not a gate or an offer (TECH_DEBT #150 struck —
                    // owner, 2026-09-24: "the round-up thing is wrong"). No
                    // click quotes the next whole pack any more; the cell
                    // just reddens and says why, and a reviewer fixes it by
                    // hand — switch the Unit, or type a whole number.
                    const fractionalQty = fractionalQuantityOf(item);
                    const fractionalQtyTooltip = fractionalQty
                      ? `${fractionalQty.quantity} ${fractionalQty.uom} is not a whole number of ${fractionalQty.uom} `
                        + `— switch the line to the unit the RFQ asked for, or type a whole number.`
                      : null;
                    // Historical only: a round-up a person chose BEFORE the chip was
                    // removed. Still an honest fact about how the line's count was
                    // arrived at, so it keeps its own quiet badge — never re-offered,
                    // never re-applied. `fractionalQty` is null here by construction
                    // (`Number(item.quantity) === roundedUp.to` is a whole count), so
                    // the two badges are never both live for the same line.
                    const roundedUp = readPartPackRounding(item);
                    const roundedUpTooltip =
                      roundedUp &&
                      roundedUp.uom === currentUomNormalized &&
                      Number(item.quantity) === roundedUp.to
                        ? `Rounded up from ${roundedUp.from} to ${roundedUp.to} ${roundedUp.uom}`
                          + `${roundedUp.at ? `, ${formatDate(roundedUp.at)}` : ''}.`
                          + `${
                              roundedUp.requested
                                ? roundedUp.requestedSource === 'rfq'
                                  ? ` The RFQ asked for ${roundedUp.requested.quantity} ${roundedUp.requested.uom}.`
                                  : ` The line was last established as ${roundedUp.requested.quantity} ${roundedUp.requested.uom}.`
                                : ''
                            }`
                          + ` There was no fraction of a ${roundedUp.uom} to ship, so the next whole one was `
                          + `quoted at the time. Edit Quantity to choose a different count.`
                        : null;

                    // Which Master Catalogue row priced this line, when a catalogue
                    // search pick is what priced it — the tooltip's only way to say
                    // so now that the pick no longer renames the line (2026-09-22
                    // client call). INTERNAL pricing metadata, never for a customer-facing
                    // document; none for an item still awaiting approval (no row, D119.5).
                    const pickedRowName = catalogueSearchPickRowName(item.pricingDetails);
                    const catalogueVatUnset =
                      (item.pricingDetails as Record<string, unknown> | undefined)?.catalogueVatUnset === true;
                    // The tags under the name; a line with none stays one row high (D171.7).
                    const hasTagsUnderName = Boolean(item.notInCatalogue) || catalogueVatUnset || needsConfirmation(item);

                    const isLastRow = index === quotation.items.length - 1;

                    return (
                      <React.Fragment key={`row-${item.lineNumber}`}>
                        {/* The row's cells, written in CANONICAL order and shown
                            in the user's — the wrapper reorders them to match the
                            header (ClickUp 86d478wd1). It renders a Fragment, so
                            these stay direct children of the CSS grid.

                            It also PINS the leading `FROZEN_COLUMN_COUNT` cells
                            to the left edge, which is why it takes the whole
                            layout and not just the order: the offset a column
                            freezes at is the sum of the track WIDTHS to its
                            left.

                            `rowTint` / `rowAnchor`: the RFQ-unit-mismatch nudge
                            (owner decision 2026-09-24) merges its amber tint onto
                            every plain cell here and its scroll-to anchor onto the
                            first one — see QuotationGridCells' own header for why
                            that has to happen there rather than on a row div that
                            does not exist.

                            R11 (owner decisions 2026-09-25): the tint YIELDS while
                            this row is flashing green (`flashLineIndex`) — the
                            tint's `!bg-amber-50` is Tailwind's important modifier
                            (needed to beat a cell's own plain `bg-white`/
                            `bg-[#FAFCFB]`), so it also beat the flash's plain
                            `bg-emerald-100`, and a save on an amber row landed with
                            no visible confirmation at all. Suppressing the tint for
                            the ~1.2s the flash runs is simpler and more certain than
                            racing two `!important` classes against each other. */}
                        <QuotationGridRowCells
                          layout={gridLayout}
                          rowAnchor={String(item.lineNumber)}
                          rowTint={
                            rfqUnitMismatch && rfqUnitMismatchTooltip && flashLineIndex !== index
                              ? { className: '!bg-amber-50', title: rfqUnitMismatchTooltip }
                              : null
                          }
                        >
                        {/* # and the per-line delete (QA Ticket 2B), shown at rest on every
                            line in a quiet red that turns full red on hover (D171.7). */}
                        <div className={`group/num box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-1 bg-white border-l border-b border-solid border-[#D7DBD9] ${isLastRow ? 'rounded-bl-md' : ''}`}>
                          <div className="font-['Public_Sans'] font-normal text-xs leading-[16px] flex items-center text-center text-slate-900">
                            {item.lineNumber}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            disabled={removingItemIndex === index}
                            title="Remove this item"
                            aria-label="Remove this item"
                            className="flex-shrink-0 rounded px-1 py-1 -my-1 text-xs leading-[16px] text-red-300 group-hover/num:text-red-600 focus-visible:text-red-600 transition-colors disabled:opacity-40"
                          >
                            ✕
                          </button>
                        </div>

                        {/* Item Name */}
                        {/* PINK, the shared "missing or wrong — fix it" level.
                            This cell used to be its own sky, on the reasoning
                            that amber already meant "something about this line's
                            NUMBERS is off" (quantityFlag, unitMismatch) and a
                            statement about the CATALOGUE deserved its own
                            colour. The colour it was given was the problem: the
                            invoice grid's blue means "a person confirmed this",
                            i.e. settled, and sky beside it reads as the same
                            colour saying the opposite. Missing-from-the-catalogue
                            is a gap somebody has to close, which is what pink
                            already means everywhere else.
                            The line is flagged whatever priced it — a historical
                            or price-list price is still an item the catalogue has
                            never heard of. */}
                        <div
                          className={`box-border flex flex-row items-center py-[4px] px-[8px] gap-[10px] border-r border-b border-l border-solid border-[#D7DBD9] min-w-0 overflow-hidden transition-colors duration-700 ${
                            item.notInCatalogue
                              ? flagCellClass({ level: 'error', reason: '' })
                              : needsConfirmation(item)
                                ? // D99 item 4: amber = we guessed the match, check it. Warn only.
                                  flagCellClass({ level: 'warn', reason: '' })
                                : flashLineIndex === index
                                ? 'bg-emerald-100'
                                : 'bg-white'
                          }`}
                          title={
                            // Independent sentences, joined into one `title`, which can
                            // hold as many sentences as it likes.
                            [
                              item.notInCatalogue
                                ? `"${item.itemName}" is not in the Master Catalogue${
                                    item.catalogueNearMisses
                                      ? ` (${item.catalogueNearMisses} similar row${item.catalogueNearMisses === 1 ? '' : 's'} exist but none matched well enough to price it)`
                                      : ''
                                  }. Search it with the magnifier and use "+ Add this item to Master Catalogue", so this and every future quotation can price from it.`
                                : null,
                              pickedRowName
                                ? `Priced from catalogue row: "${pickedRowName}"`
                                : null,
                              matchTooltip(item),
                            ]
                              .filter((sentence): sentence is string => Boolean(sentence))
                              .join(' ') || undefined
                          }
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              lookupTriggerRef.current = e.currentTarget;
                              setLookupPanelOpenIndex(index);
                            }}
                            disabled={savingPickIndex !== null}
                            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Search Master Catalogue"
                          >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <mask id={`mask0_search_${index}`} style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                                <rect width="20" height="20" fill="#D9D9D9" />
                              </mask>
                              <g mask={`url(#mask0_search_${index})`}>
                                <path d="M10 7.52083C11.1944 7.52083 12.4271 7.34028 13.6979 6.97917C14.9687 6.61805 15.6806 6.24306 15.8333 5.85417C15.6806 5.45139 14.9826 5.06944 13.7396 4.70833C12.4965 4.34722 11.25 4.16667 10 4.16667C8.73611 4.16667 7.49653 4.34375 6.28125 4.69792C5.06597 5.05208 4.36111 5.4375 4.16667 5.85417C4.375 6.25694 5.10069 6.63542 6.34375 6.98958C7.5868 7.34375 8.80555 7.52083 10 7.52083ZM8.72917 15.7708C8.86805 16.0903 9.02778 16.3958 9.20833 16.6875C9.38889 16.9792 9.59722 17.25 9.83333 17.5C8.81944 17.4861 7.86458 17.3924 6.96875 17.2187C6.07292 17.0451 5.29514 16.809 4.63542 16.5104C3.97569 16.2118 3.45486 15.8611 3.07292 15.4583C2.69097 15.0556 2.5 14.625 2.5 14.1667V5.83333C2.5 5.375 2.69792 4.94444 3.09375 4.54167C3.48958 4.13889 4.02778 3.78472 4.70833 3.47917C5.38889 3.17361 6.18403 2.93403 7.09375 2.76042C8.00347 2.58681 8.97222 2.5 10 2.5C11.0278 2.5 11.9965 2.58681 12.9062 2.76042C13.816 2.93403 14.6111 3.17361 15.2917 3.47917C15.9722 3.78472 16.5104 4.13889 16.9062 4.54167C17.3021 4.94444 17.5 5.375 17.5 5.83333C17.5 6.29167 17.3021 6.72222 16.9062 7.125C16.5104 7.52778 15.9722 7.88194 15.2917 8.1875C14.6111 8.49305 13.816 8.73264 12.9062 8.90625C11.9965 9.07986 11.0278 9.16667 10 9.16667C8.81944 9.16667 7.72917 9.0625 6.72917 8.85417C5.72917 8.64583 4.875 8.34028 4.16667 7.9375V10.0417C4.72222 10.5556 5.41667 10.9306 6.25 11.1667C7.08333 11.4028 7.92361 11.5556 8.77083 11.625C8.65972 11.8333 8.56944 12.0729 8.5 12.3437C8.43055 12.6146 8.38194 12.9167 8.35417 13.25C7.52083 13.1528 6.74653 13.0139 6.03125 12.8333C5.31597 12.6528 4.69444 12.4097 4.16667 12.1042V14.1667C4.36111 14.5139 4.89583 14.8403 5.77083 15.1458C6.64583 15.4514 7.63194 15.6597 8.72917 15.7708ZM18 19.1667L15.75 16.9167C15.4444 17.0972 15.125 17.2396 14.7917 17.3437C14.4583 17.4479 14.1111 17.5 13.75 17.5C12.7083 17.5 11.8229 17.1354 11.0937 16.4062C10.3646 15.6771 10 14.7917 10 13.75C10 12.7083 10.3646 11.8229 11.0937 11.0937C11.8229 10.3646 12.7083 10 13.75 10C14.7917 10 15.6771 10.3646 16.4062 11.0937C17.1354 11.8229 17.5 12.7083 17.5 13.75C17.5 14.1111 17.4479 14.4583 17.3437 14.7917C17.2396 15.125 17.0972 15.4444 16.9167 15.75L19.1667 18L18 19.1667ZM13.75 15.8333C14.3333 15.8333 14.8264 15.6319 15.2292 15.2292C15.6319 14.8264 15.8333 14.3333 15.8333 13.75C15.8333 13.1667 15.6319 12.6736 15.2292 12.2708C14.8264 11.8681 14.3333 11.6667 13.75 11.6667C13.1667 11.6667 12.6736 11.8681 12.2708 12.2708C11.8681 12.6736 11.6667 13.1667 11.6667 13.75C11.6667 14.3333 11.8681 14.8264 12.2708 15.2292C12.6736 15.6319 13.1667 15.8333 13.75 15.8333Z" fill="#6B6B6B" />
                              </g>
                            </svg>
                          </button>
                          <div className="flex-1 min-w-0 flex flex-col">
                            <div className="font-['Public_Sans'] font-normal text-xs leading-[16px] text-slate-900">
                              <EditableCell
                                value={item.itemName}
                                onSave={(newValue) => updateQuotationItem(index, 'itemName', newValue)}
                                truncate
                                className="w-full text-left min-w-0"
                              />
                            </div>
                            {/* The catalogue-addition review queue's mark on THIS
                                line — pending or refused only; an approved
                                submission is an ordinary catalogue-backed line
                                now and gets none. `text-slate-500`, the small
                                grey this grid already uses for secondary text
                                (e.g. the Internal Remarks header's "never
                                exported" note above). Truncates rather than
                                widening the column; the full text sits in the
                                title for anyone who needs the refusal reason
                                in full. */}
                            {catalogueSubmissionMarkerFor(index) && (
                              <div
                                className="truncate font-['Public_Sans'] text-xs leading-[14px] text-slate-500"
                                title={catalogueSubmissionMarkerFor(index) || undefined}
                              >
                                {catalogueSubmissionMarkerFor(index)}
                              </div>
                            )}
                            {/* The badge, VAT? and the match actions share one wrapping row UNDER the
                                name, so the name keeps its width (D99 item 4, D171.7). */}
                            {hasTagsUnderName && (
                              <div className="mt-0.5 flex flex-wrap items-center gap-1">
                                {/* Said in words, not only in the pink tint and a hover title:
                                    Preet, 2026-09-16, "highlighted in red saying not in catalogue". */}
                                {item.notInCatalogue && (
                                  <span className="flex-shrink-0 whitespace-nowrap rounded border border-red-300 bg-red-50 px-[4px] font-['Public_Sans'] text-xs font-semibold leading-[14px] text-red-700">
                                    Not in catalogue
                                  </span>
                                )}
                                {/* This line was priced from a catalogue row whose VAT
                                    was BLANK when it was generated — nobody was there
                                    to ask (a bulk RFQ->quotation generation, not an
                                    interactive pick). Opens the same
                                    `CatalogueVatPrompt` an interactive pick asks
                                    BEFORE writing (`handleSearchSelect`); here the
                                    line already exists, so answering re-prices
                                    through the existing per-line Refresh path
                                    (`answerCatalogueVatPrompt`) instead. */}
                                {catalogueVatUnset && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const masterCatalogueId = (item.pricingDetails as Record<string, unknown> | undefined)
                                        ?.masterCatalogueId as string | undefined;
                                      if (!masterCatalogueId) return;
                                      setVatPrompt({ index, masterCatalogueId, itemName: item.itemName || '' });
                                    }}
                                    className="flex-shrink-0 whitespace-nowrap rounded border border-amber-300 bg-amber-50 px-[4px] font-['Public_Sans'] text-xs font-semibold leading-[14px] text-amber-800 hover:bg-amber-100"
                                    title="Does this item have VAT? Not yet answered — click to say."
                                  >
                                    VAT?
                                  </button>
                                )}
                                {/* An unconfirmed match: neither action is required. */}
                                {needsConfirmation(item) && (
                                  <LineMatchActions
                                    canConfirm={canConfirmMatch(item) && !isFrozenStatus(quotation.status)}
                                    quotationId={quotation._id}
                                    lineNumber={item.lineNumber}
                                    onConfirmed={applyConfirmedLine}
                                    onChangeMatch={() => setLookupPanelOpenIndex(index)}
                                    onError={(message) => toast.error(message)}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                          {/* After the name, so every name in the column starts in
                              one place (BUSINESS_RULES#quotation-line-catalogue-options). */}
                          <CatalogueOptionsLink
                            quotationId={quotation._id}
                            lineNumber={item.lineNumber}
                            pricingDetails={item.pricingDetails}
                          />
                        </div>

                        {/* Requested Qty (RFQ) — read-only, and the base every
                            conversion counts from (ClickUp 86d478wv2).

                            `rfqAsked` is `originalRequest`, never the anchor: the
                            anchor is what the line was last ESTABLISHED as and a
                            hand edit replaces it, so it stops being the request the
                            moment anybody corrects the row. An em dash where the
                            RFQ said nothing — a hand-added line, or a quotation
                            generated before 2026-08-28 whose backfill has not been
                            run — because inventing a request is worse than
                            admitting there is none.

                            Tinted when it differs from what we quoted, which is the
                            question a reviewer actually has: did we quote what was
                            asked for? BLUE, not the amber `unitMismatch` uses — a
                            restatement is correct, not a warning, and blue is the
                            shared level for "a person set this deliberately". It
                            was its own sky before, which read as blue anyway and
                            meant nothing to anyone who had not read this comment. */}
                        <div
                          className={`box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-[10px] border-r border-b border-solid border-[#D7DBD9] min-w-0 overflow-hidden ${
                            askedDiffers
                              ? flagCellClass({ level: 'manual', reason: '' })
                              : 'bg-[#FAFCFB]'
                          }`}
                          title={
                            askedTooltip ||
                            (rfqAsked
                              ? 'What the client asked for on the RFQ line. Conversions are counted from this.'
                              : 'The RFQ line recorded no quantity for this item.')
                          }
                        >
                          <span className="font-['Public_Sans'] font-normal text-xs leading-[16px] text-slate-900 truncate tabular-nums">
                            {rfqAsked
                              ? `${rfqAsked.quantity}${rfqAsked.uom ? ` ${rfqAsked.uom}` : ''}`
                              : '—'}
                          </span>
                        </div>

                        {/* Quantity — carries the fractional-whole-count flag and the D51 amount check */}
                        {/* TECH_DEBT #65 — amber for `quantityFlag`, same treatment the Unit
                            cell gives `unitMismatch` below: the vessel's RFQ line stated a
                            literal 0, the line still prices at 0 x unitPrice, this only makes
                            it visible. Not a hard block — a reviewer can save past it.
                            RED for `fractionalQty` too — TECH_DEBT #150's round-up chip struck
                            2026-09-24 in favour of a flag: styled at `error` level like
                            `amountBlocks` beside it, but never wired into the D51 gate
                            (`blocksQuotation` / `confirmationBlockers`), so it colours the
                            cell without stopping Finalize, Send or Confirm — the same
                            non-blocking use of `error`-level styling `notInCatalogue`
                            already makes on this page. */}
                        <div
                          className={`box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-[4px] border-r border-b border-solid border-[#D7DBD9] min-w-0 ${
                            amountBlocks
                              ? flagCellClass({ level: 'error', reason: '' })
                              : fractionalQty
                                ? flagCellClass({ level: 'error', reason: '' })
                                : item.quantityFlag
                                  ? flagCellClass({ level: 'warn', reason: '' })
                                  : 'bg-white'
                          }`}
                          title={
                            amountTooltip ||
                            fractionalQtyTooltip ||
                            (item.quantityFlag
                              ? rfqAsked
                                ? 'Vessel asked for 0 of this item — quoted at US$0. Confirm this is correct, or update the quantity.'
                                : 'The RFQ line gave no quantity — quoted at 0 (US$0). Enter the quantity the vessel wants.'
                              : undefined)
                          }
                        >
                          <div className="min-w-0 flex-1 font-['Public_Sans'] font-normal text-xs leading-[16px] flex items-center text-center text-slate-900">
                            <EditableCell
                              value={item.quantity}
                              type="number"
                              validate={(v) => quantityEditProblem(v, item.uom)}
                              onSave={(newValue) => updateQuotationItem(index, 'quantity', newValue)}
                              className="text-center w-full"
                            />
                          </div>
                          {roundedUpTooltip && (
                            <span
                              title={roundedUpTooltip}
                              aria-label={roundedUpTooltip}
                              className="flex-shrink-0 text-xs leading-[14px] text-indigo-600 cursor-help"
                            >
                              ↑
                            </span>
                          )}
                          {amountBlocks &&
                            amountCheck.status === LINE_AMOUNT_STATUS.MISMATCH &&
                            amountCheck.expected != null &&
                            (expectedIsWholeCount(amountCheck) ? (
                              <button
                                type="button"
                                onClick={() => void handleUseAskedAmount(index)}
                                title={`Set the quantity to ${amountCheck.expected} ${amountCheck.quoted.uom} — the ${amountCheck.asked?.quantity} ${amountCheck.asked?.uom} the vessel asked for.`}
                                aria-label={`Set the quantity to ${amountCheck.expected} ${amountCheck.quoted.uom}`}
                                className="flex-shrink-0 px-[3px] rounded text-xs leading-[14px] font-medium text-rose-700 bg-white ring-1 ring-inset ring-rose-300 hover:bg-rose-100"
                              >
                                →{amountCheck.expected}
                              </button>
                            ) : (
                              // OWNER DECISION A, F5 (2026-09-25 audit): the ask restates to a
                              // fractional count of a whole-count unit ("3.333333 CASE") — not
                              // something any vendor ships, so the chip never offers it. The two
                              // remedies left: switch the line to the unit and amount actually
                              // asked for (repriced through the SAME owner the Unit dropdown
                              // uses), or type a whole number into the Quantity cell by hand.
                              <button
                                type="button"
                                onClick={() => void handleSwitchToAskedUnit(index, altPricing)}
                                title={
                                  // N6 (edge, wording — owner re-audit,
                                  // 2026-09-25): when the RFQ's own ask is
                                  // ITSELF a fractional count (asked 2.5
                                  // CASE), switching to the asked unit writes
                                  // that same fraction — the vessel's own
                                  // stated amount, not one this screen
                                  // invented. Saying it is "not offered"
                                  // would be false: the button right here
                                  // offers exactly it. Only `expected` (the
                                  // ask restated into the QUOTED unit, by
                                  // conversion) is refused as a Quantity-cell
                                  // figure — no behaviour change, wording only.
                                  Number.isInteger(amountCheck.asked?.quantity)
                                    ? `${amountCheck.expected} ${amountCheck.quoted.uom} is not a whole number, so it is not offered. Switch to the ${amountCheck.asked?.quantity} ${amountCheck.asked?.uom} the vessel asked for, or type a whole number of ${amountCheck.quoted.uom} into the Quantity cell.`
                                    : `${amountCheck.expected} ${amountCheck.quoted.uom} is not a whole number, so it is not offered as a Quantity-cell figure. Switch to ${amountCheck.asked?.quantity} ${amountCheck.asked?.uom} — the vessel's own asked amount, fractional or not — or type a whole number of ${amountCheck.quoted.uom} into the Quantity cell.`
                                }
                                aria-label={`Switch to the asked unit, ${amountCheck.asked?.quantity} ${amountCheck.asked?.uom}`}
                                className="flex-shrink-0 px-[3px] rounded text-xs leading-[14px] font-medium text-rose-700 bg-white ring-1 ring-inset ring-rose-300 hover:bg-rose-100"
                              >
                                →{amountCheck.asked?.uom}
                              </button>
                            ))}
                          {amountBlocks && (
                            <button
                              type="button"
                              onClick={() => void handleAcceptLineAmount(index)}
                              title="The amount on this line is right as it stands — keep it."
                              aria-label="Mark the amount on this line right"
                              className="flex-shrink-0 px-[3px] rounded text-xs leading-[14px] font-medium text-rose-700 bg-white ring-1 ring-inset ring-rose-300 hover:bg-rose-100"
                            >
                              ✓
                            </button>
                          )}
                          {amountCheck.status === LINE_AMOUNT_STATUS.ACCEPTED && amountTooltip && (
                            <span
                              title={amountTooltip}
                              aria-label={amountTooltip}
                              className="flex-shrink-0 text-xs leading-[14px] text-blue-600 cursor-help"
                            >
                              ✓
                            </span>
                          )}
                        </div>

                        {/* Unit — amber when quoted in a different unit than the RFQ asked for
                            (TECH_DEBT #61): no pack ratio was on file to bridge the two, so this
                            is the honest price we have, not the requested unit relabeled. */}
                        <div
                          className={`box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-[6px] border-r border-b border-solid border-[#D7DBD9] ${
                            item.unitMismatch
                              ? flagCellClass({ level: 'warn', reason: '' })
                              : restatedTooltip || askedTooltip
                                // Was its own indigo. A restatement is somebody
                                // deliberately re-expressing the line, which is
                                // exactly the shared "a person set this" level —
                                // it never needed a sixth colour.
                                ? flagCellClass({ level: 'manual', reason: '' })
                                : 'bg-white'
                          }`}
                          title={
                            [
                              item.unitMismatch
                                ? isPackAmbiguousLine(item)
                                  ? PACK_AMBIGUOUS_TIP
                                  : `Vessel asked for ${item.unitRequested || 'a different unit'} — no Units/Case (or Unit Size) is on file for this item, so it's quoted at the ${item.uom} price instead. Add the conversion to this item in Master Catalogue, then click ↻ to re-price.`
                                : null,
                              restatedTooltip,
                              // The indigo mark now covers this too: a line hand-edited
                              // away from the request drops its anchor, so it was
                              // restated-marked by nothing and had no memory of the
                              // request either. One mark, and the tooltip says which of
                              // the two it is (or both).
                              askedTooltip,
                            ]
                              .filter(Boolean)
                              .join(' ') || undefined
                          }
                        >
                          <div className="font-['Public_Sans'] font-normal text-xs leading-[16px] flex items-center text-center text-slate-900 w-full">
                            <select
                              value={currentUomNormalized}
                              onChange={(e) => void handleLineItemUnitChange(index, e.target.value, altPricing, e.currentTarget)}
                              className="quotation-cell-select"
                              title="Select unit"
                            >
                              {lineUnitOptions.map((unitOption) => (
                                <option key={`unit-main-${index}-${unitOption}`} value={unitOption}>
                                  {unitOption}
                                </option>
                              ))}
                            </select>
                          </div>
                          {/* Available on EVERY priced line, not only flagged ones.
                              It used to render only when `item.unitMismatch` was set,
                              which had it exactly backwards: a line that announced its
                              own uncertainty got a fix button, while a line that was
                              CONFIDENTLY wrong got nothing. The confidently-wrong ones
                              are the whole class of pricing bug fixed on 2026-08-26/27
                              (the vendor-hint unit gate, the missing count->weight
                              basis, the dropped pack count), and a stored quotation
                              does not re-price itself — so the lines most needing this
                              control were the only lines without it. QUO-00010 line 1
                              sat at $894.96/BTL against $11.47 due with no way to
                              correct it from this screen. */}
                          {!!item.itemName && (
                            <button
                              type="button"
                              onClick={() => void handleRefreshLinePrice(index)}
                              disabled={refreshingPriceIndex === index}
                              title={
                                item.unitMismatch
                                  ? 'Re-price this line (after updating Units/Case or Unit Size in Master Catalogue)'
                                  : 'Re-price this line from the current Master Catalogue and pricing rules'
                              }
                              aria-label="Refresh price"
                              className={`flex-shrink-0 disabled:opacity-40 text-xs leading-none ${
                                item.unitMismatch
                                  ? 'text-amber-700 hover:text-amber-900'
                                  : 'text-[#9AA3A0] hover:text-slate-900'
                              }`}
                            >
                              {refreshingPriceIndex === index ? '…' : '↻'}
                            </button>
                          )}
                        </div>

                        {/* Equiv. per KG — read-only */}
                        <div
                          className="box-border flex flex-row justify-center items-center py-[4px] px-[4px] gap-[2px] bg-[#FAFCFB] border-r border-b border-solid border-[#D7DBD9]"
                          title={altPricing.basisHint || 'From description pack pattern (e.g. 20×500g) or per-kg line'}
                        >
                          <div className="font-['Public_Sans'] font-normal text-xs leading-[14px] flex flex-col items-center text-center text-slate-900 min-h-[32px] justify-center">
                            {altPricing.perKgUsd != null ? (
                              <>
                                <span>{USD_LABEL}{altPricing.perKgUsd.toFixed(2)}</span>
                                {perKgTtd != null && (
                                  <span className="text-xs text-slate-500">
                                    {TTD_LABEL}{perKgTtd.toFixed(2)}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="text-[#CCC]">—</span>
                            )}
                          </div>
                        </div>

                        {/* Equiv. per lb / inner — read-only */}
                        <div
                          className="box-border flex flex-row justify-center items-center py-[4px] px-[4px] gap-[2px] bg-[#FAFCFB] border-r border-b border-solid border-[#D7DBD9]"
                          title={
                            altPricing.innerUnitLabel
                              ? `Inner: ${altPricing.innerUnitLabel}`
                              : 'Per lb if priced per kg; else per smallest pack unit from description'
                          }
                        >
                          <div className="font-['Public_Sans'] font-normal text-xs leading-[14px] flex flex-col items-center text-center text-slate-900 min-h-[32px] justify-center">
                            {lbOrInner.kind !== 'none' ? (
                              <>
                                <span>{USD_LABEL}{lbOrInner.value.toFixed(2)}</span>
                                {lbOrInnerTtdValue != null && (
                                  <span className="text-xs text-slate-500">
                                    {TTD_LABEL}{lbOrInnerTtdValue.toFixed(2)}
                                  </span>
                                )}
                                {lbOrInner.kind === 'inner' && altPricing.innerUnitLabel && (
                                  <span className="text-xs text-[#888] max-w-[120px] truncate">
                                    {altPricing.innerUnitLabel}
                                  </span>
                                )}
                                {lbOrInner.kind === 'lb' && <span className="text-xs text-[#888]">per lb</span>}
                              </>
                            ) : (
                              <span className="text-[#CCC]">—</span>
                            )}
                          </div>
                        </div>

                        {/* Unit Cost Price (USD) - Editable */}
                        <div className={`box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-[10px] border-r border-b border-solid border-[#D7DBD9] transition-colors duration-700 ${item.unitMismatch ? flagCellClass({ level: 'warn', reason: '' }) : flashLineIndex === index ? 'bg-emerald-100' : 'bg-white'}`}>
                          <div className="font-['Public_Sans'] font-normal text-xs leading-[16px] flex items-center text-center text-slate-900">
                            {/* D104.10: shows the EXACT cost (`lineSelling.unitCost`) that Item Profit,
                                the totals and realized markup are worked out from — cents, or four
                                decimals when it has a sub-cent part. Editing still stores what it
                                stored before. (The equivalents beside it use the cent-ceiled
                                `usdPrice`.) */}
                            {costNeedsVendor ? (
                              <CostNeedsVendorCell
                                shown={hasCost ? `${USD_LABEL}${exactCostText(lineSelling.unitCost)}${unitCostPriceSuffix(item.uom)}` : null}
                                onOpen={() => setCatalogueAddIndex(index)}
                              />
                            ) : (
                            <EditableCell
                              value={exactCostText(lineSelling.unitCost)}
                              type="number"
                              validate={(v) => unitCostEditProblem(v)}
                              onSave={(newValue) => updateQuotationItem(index, 'unitPrice', newValue)}
                              className="text-center w-full"
                              prefix={USD_LABEL}
                              suffix={unitCostPriceSuffix(item.uom)}
                              tooltipText={
                                item.unitMismatch
                                  ? isPackAmbiguousLine(item)
                                    ? PACK_AMBIGUOUS_TIP
                                    : `Asked ${item.unitRequested || 'a different unit'}, quoted per ${item.uom} — no pack ratio on file.${item.pricingSource && item.pricingSource !== 'not_found' ? ` Source: ${formatPricingSource(item.pricingSource)}` : ''}`
                                  : (item.pricingSource && item.pricingSource !== 'not_found' ? `Source: ${formatPricingSource(item.pricingSource)}` : undefined)
                              }
                            />
                            )}
                          </div>
                        </div>

                        {/* Unit Cost Price (TTD) - Editable */}
                        <div className={`box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-[10px] border-r border-b border-solid border-[#D7DBD9] transition-colors duration-700 ${flashLineIndex === index ? 'bg-emerald-100' : 'bg-white'}`}>
                          <div className="font-['Public_Sans'] font-normal text-xs leading-[16px] flex items-center text-center text-slate-900">
                            {costNeedsVendor ? (
                              <CostNeedsVendorCell
                                shown={hasCost ? (ttdPrice != null ? `${TTD_LABEL}${ttdPrice.toFixed(2)}${unitCostPriceSuffix(item.uom)}` : '—') : null}
                                onOpen={() => setCatalogueAddIndex(index)}
                              />
                            ) : (
                            <EditableCell
                              value={ttdPrice != null ? ttdPrice.toFixed(2) : '—'}
                              type="number"
                              validate={(v) => unitCostEditProblem(v)}
                              onSave={async (newValue) => {
                                // Through the owner (`ttdCostEdit`: the cost ceiled once from
                                // the unrounded conversion, the exact kept for the markup, the
                                // TT$ pin kept so this cell never ratchets) and the refusal. This cell
                                // used to fall back to `Math.round(ttdVal * 100) / 100`
                                // when no rate had loaded — a TT$ figure written into a
                                // US$ field at 1:1, the same defect the supplier
                                // dropdown shipped (TECH_DEBT #152). There is no honest
                                // conversion without a rate, so the edit is refused and
                                // says why.
                                const typedCost = ttdCostEdit(newValue, ttdToUsdRate);
                                if (typedCost == null) {
                                  toast.error(
                                    "This quotation's TT$ ↔ US$ rate has not loaded, so a TT$ cost cannot be converted. Enter the cost in the US$ cell beside this one, or reload the page.",
                                  );
                                  return;
                                }
                                // US$ is still what the line stores and still what the
                                // markup is applied to — but the figure typed HERE is
                                // now kept as well, so this cell reads back the number
                                // that was entered instead of the cent the US$ round
                                // trip lands on. Typing TT$100.00 used to redisplay as
                                // TT$100.04.
                                await updateQuotationItem(
                                  index,
                                  'unitPrice',
                                  typedCost.unitPrice,
                                  undefined,
                                  typedCost,
                                );
                              }}
                              className="text-center w-full"
                              prefix={TTD_LABEL}
                              suffix={unitCostPriceSuffix(item.uom)}
                              // The tooltip states which of the two answers this cell is
                              // giving, because they behave differently: a pinned figure is
                              // exact and stays where it is put, while a derived one can
                              // settle a cent from what was typed. Saying only the second
                              // (as this did) now describes the minority of lines.
                              tooltipText={`${
                                item.unitPriceTtd != null
                                  ? 'The TT$ this line was priced at, kept exactly as it was entered. USD stays the currency of record and the markup is applied to it; editing here re-states both.'
                                  : "Derived from the USD unit cost at the quotation's locked rate — this line's cost was stated in USD, so the TTD figure may settle a cent from what you type. Type a TT$ figure here and it will be kept exactly."
                              }${item.pricingSource && item.pricingSource !== 'not_found' ? ` Source: ${formatPricingSource(item.pricingSource)}` : ''}`}
                            />
                            )}
                            {exchangeRateError && (
                              <div className="text-xs text-red-500 mt-1" title={exchangeRateError}>*</div>
                            )}
                          </div>
                        </div>

                        {/* Packing Size (Quoting) — read-only */}
                        <div className={`box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-[10px] border-r border-b border-solid border-[#D7DBD9] min-w-0 overflow-hidden transition-colors duration-700 ${flashLineIndex === index ? 'bg-emerald-100' : 'bg-[#FAFCFB]'}`}>
                          <span
                            className="font-['Public_Sans'] font-normal text-xs leading-[16px] text-slate-900 truncate"
                            title={altPricing.quotingSize || 'No pack size from vendor invoice / catalogue'}
                          >
                            {altPricing.quotingSize || '—'}
                          </span>
                        </div>

                        {/* Total Cost Price — read-only, derived as Qty × Unit Cost.
                            It used to be an editable cell, but Quotation.pre('save') always
                            recomputes it from unitPrice × quantity, so a typed total was
                            silently discarded on the round-trip. Unit Cost Price is the
                            editable figure; this one follows from it. */}
                        <div className="box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-[10px] bg-[#FAFCFB] border-r border-b border-solid border-[#D7DBD9]">
                          <div className="font-['Public_Sans'] font-normal text-xs leading-[16px] flex items-center text-center text-slate-900">
                            <span
                              className={item.pricingSource && item.pricingSource !== 'not_found' ? 'cursor-help' : undefined}
                              title={`Qty × Unit Cost Price — edit the unit cost to change it.${item.pricingSource && item.pricingSource !== 'not_found' ? ` Source: ${formatPricingSource(item.pricingSource)}` : ''}`}
                            >
                              {USD_LABEL}{exactCostText(lineProfitUsd ? lineProfitUsd.cost : ceilPrice(item.totalPrice || 0))}
                            </span>
                          </div>
                        </div>

                        {/* Remarks (to Client) — placed next to Total Cost Price for quick cross-reference */}
                        <div className="box-border flex flex-row items-center min-w-0 overflow-hidden py-[4px] px-[8px] gap-[10px] bg-white border-r border-b border-solid border-[#D7DBD9]">
                          <div className="w-full min-w-0 font-['Public_Sans'] font-normal text-xs leading-[16px] text-slate-900">
                            <EditableCell
                              value={item.remarks && item.remarks.trim() !== "" ? item.remarks : "Click to add a remark the client sees"}
                              onSave={(newValue) => {
                                const cleanValue = newValue === "Click to add a remark the client sees" ? "" : newValue;
                                return updateQuotationItem(index, 'remarks', cleanValue);
                              }}
                              truncate
                              className="w-full text-left min-h-[20px] p-0.5"
                            />
                          </div>
                        </div>

                        {/* Supplier — the cell lives in
                            features/quotations/SupplierCell; clicking it expands its
                            own read-only SupplierDetailsCard for the row this line is
                            priced from (owner decisions 2026-09-24). It no longer opens
                            the shared ItemLookupPanel (the magnifier still does) and
                            needs no open-index coordination from this page — see
                            SupplierCell's own header comment. */}
                        <SupplierCell
                          selectedVendor={item.selectedVendor}
                          pricingSource={item.pricingSource}
                          uom={item.uom}
                          unitCostTtd={unitCostTtd(item, ttdToUsdRate)}
                          // D86: the date the line stored for its source price, if any
                          // (the recorded supplier choice); the card says "No date" else.
                          priceDate={storedPriceDate(item.pricingDetails)}
                          masterCatalogueId={
                            typeof item.pricingDetails?.masterCatalogueId === 'string'
                              ? (item.pricingDetails.masterCatalogueId as string)
                              : null
                          }
                          quotationId={quotation._id}
                          itemIndex={index}
                        />

                        {/* Markup */}
                        <div
                          // Gains the ring the other amber cells already had —
                          // it was a bare tint, so the one amber state a
                          // reviewer is least likely to notice was also the
                          // faintest. Same level, same paint, one call.
                          className={`box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-[10px] border-r border-b border-solid border-[#D7DBD9] ${markupStale ? flagCellClass({ level: 'warn', reason: '' }) : 'bg-white'}`}
                          title={markupStale ? markupTooltip : undefined}
                        >
                          <div className="font-['Public_Sans'] font-normal text-xs leading-[16px] flex items-center text-center text-slate-900">
                            <EditableCell
                              value={setMarkupPct}
                              type="number"
                              onSave={async (newValue) => {
                                // Confirm before a markup edit rewrites the quotation's selling prices.
                                if (
                                  Number(newValue) !== Number(setMarkupPct) &&
                                  !(await confirm({
                                    title: 'Are you sure you want to change the quotation?',
                                    summary: hasCost
                                      ? `Markup on this line becomes ${newValue}%. Its selling price is recalculated from the cost.`
                                      : `Markup on this line becomes ${newValue}%. It applies once the line has a cost.`,
                                    confirmLabel: 'Change markup',
                                  }))
                                ) {
                                  // Cancelled — leave the quotation unchanged. EditableCell's
                                  // re-seed effect runs when editing ends and restores the cell
                                  // from the (unchanged) prop, so the typed value does not linger.
                                  return;
                                }
                                await updateQuotationItem(index, 'markup', newValue);
                              }}
                              className="text-center w-full"
                              suffix="%"
                              validate={markupEditProblem}
                              tooltipText={markupTooltip}
                            />
                          </div>
                        </div>

                        {/* Unit Selling Price */}
                        <div className="box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-[10px] bg-white border-r border-b border-solid border-[#D7DBD9]">
                          <div className="font-['Public_Sans'] font-normal text-xs leading-[16px] flex items-center text-center text-slate-900">
                            <span className={`relative group ${item.pricingSource && item.pricingSource !== 'not_found' ? 'cursor-help' : ''}`}>
                              {USD_LABEL}{dispUnitSelling.toFixed(2)}
                              {item.pricingSource && item.pricingSource !== 'not_found' && (
                                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-10">
                                  Source: {formatPricingSource(item.pricingSource)}
                                  <span className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></span>
                                </span>
                              )}
                            </span>
                          </div>
                        </div>

                        {/* Total Selling Price */}
                        <div className="box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-[10px] bg-white border-r border-b border-solid border-[#D7DBD9]">
                          <div className="font-['Public_Sans'] font-normal text-xs leading-[16px] flex items-center text-center text-slate-900">
                            <span className={`relative group ${item.pricingSource && item.pricingSource !== 'not_found' ? 'cursor-help' : ''}`}>
                              {USD_LABEL}{dispTotalSelling.toFixed(2)}
                              {item.pricingSource && item.pricingSource !== 'not_found' && (
                                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-10">
                                  Source: {formatPricingSource(item.pricingSource)}
                                  <span className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></span>
                                </span>
                              )}
                            </span>
                          </div>
                        </div>

                        {/* Unit Selling Price (TTD) */}
                        <div className="box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-[10px] bg-white border-r border-b border-solid border-[#D7DBD9]">
                          <div className="font-['Public_Sans'] font-normal text-xs leading-[16px] flex items-center text-center text-slate-900">
                            {(() => {
                              const unitSellingTtd = usdToTtd(dispUnitSelling, ttdToUsdRate);
                              return <span>{unitSellingTtd != null ? `${TTD_LABEL}${unitSellingTtd.toFixed(2)}` : '—'}</span>;
                            })()}
                          </div>
                        </div>

                        {/* Total Cost Price (TTD) — see the header note above for why it is
                            here and why only its FILL is red. Fed the SAME figure the US$
                            Total Cost Price cell renders, the owner's line cost (`lineProfitUsd.cost`),
                            so the two columns cannot come to different answers about one line.
                            A dash, not the US$ number, when no rate has loaded (`usdToTtd`). */}
                        <div className="box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-[10px] bg-[#FDECEA] border-r border-b border-solid border-[#D7DBD9]">
                          <div className="font-['Public_Sans'] font-normal text-xs leading-[16px] flex items-center text-center text-slate-900">
                            {(() => {
                              const totalCostUsd = lineProfitUsd ? lineProfitUsd.cost : ceilPrice(item.totalPrice || 0);
                              // Qty × the UNIT TT$ above, through the same owner, so a
                              // pinned line's total is its pinned price times its count
                              // and not a second conversion of the US$ total.
                              const lineTotalTtd = totalCostTtd(item, ttdToUsdRate);
                              return (
                                <span
                                  className="cursor-help"
                                  title={`Qty × Unit Cost Price in Trinidad dollars, at the quotation's locked rate. ${USD_LABEL}${exactCostText(totalCostUsd)}`}
                                >
                                  {lineTotalTtd != null ? `${TTD_LABEL}${lineTotalTtd.toFixed(2)}` : '—'}
                                </span>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Total Selling Price (TTD) */}
                        <div className="box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-[10px] bg-white border-r border-b border-solid border-[#D7DBD9]">
                          <div className="font-['Public_Sans'] font-normal text-xs leading-[16px] flex items-center text-center text-slate-900">
                            {(() => {
                              const totalSellingTtd = usdToTtd(dispTotalSelling, ttdToUsdRate);
                              return <span>{totalSellingTtd != null ? `${TTD_LABEL}${totalSellingTtd.toFixed(2)}` : '—'}</span>;
                            })()}
                          </div>
                        </div>

                        {/* Competitor's Price */}
                        <div className="box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-[10px] bg-white border-r border-b border-solid border-[#D7DBD9]">
                          <div className="font-['Public_Sans'] font-normal text-xs leading-[16px] flex items-center text-center text-slate-900">
                            <EditableCell
                              value={competitorPriceOf(item) > 0 ? competitorPriceOf(item).toFixed(2) : 'Click to add'}
                              type="number"
                              onSave={async (newValue) => {
                                const numValue = typeof newValue === 'number' ? newValue : parseFloat(String(newValue)) || 0;
                                await handleCompetitorPriceChange(index, numValue);
                              }}
                              className="text-center w-full"
                              prefix={competitorPriceOf(item) > 0 ? USD_LABEL : ''}
                              money
                            />
                          </div>
                        </div>

                        {/* Item Profit */}
                        <div className="box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-[10px] bg-white border-r border-b border-solid border-[#D7DBD9]">
                          <div className="font-['Public_Sans'] font-normal text-xs leading-[18px] flex items-center text-center text-slate-900">
                            {/* This line's share of the quote, from the one owner: its selling
                                price AFTER its share of the discount, less its cost. The column
                                used to read `selling − cost` and ignore the discount entirely, so
                                it could not be added up to the Profit in the summary box.
                                Competitor's Price is a comparison field and never feeds this
                                (ClickUp 86d3hpttw). */}
                            <ItemProfitCell usd={lineProfitUsd} ttd={lineProfitTtd} basis="cost" />
                          </div>
                        </div>

                        {/* No Total Profit cell — see the header. The quote's
                            own profit is the Pricing Summary's job; repeating
                            it on every line only competed with Item Profit,
                            which is the one profit that varies by row. */}

                        {/* Internal Remarks */}
                        <div className="box-border flex flex-row items-center min-w-0 overflow-hidden py-[4px] px-[8px] gap-[10px] bg-white border-r border-b border-solid border-[#D7DBD9]">
                          <div className="w-full min-w-0 font-['Public_Sans'] font-normal text-xs leading-[16px] text-slate-900">
                            <LineNoteCell
                              item={item}
                              onSave={(cleanValue) => updateQuotationItem(index, 'internalRemarks', cleanValue)}
                            />
                          </div>
                        </div>

                        {/* More Details */}
                        <div className="box-border flex flex-row items-center min-w-0 overflow-hidden py-[4px] px-[8px] gap-[10px] bg-white border-r border-b border-solid border-[#D7DBD9]">
                          <div className="w-full min-w-0 font-['Public_Sans'] font-normal text-xs leading-[16px] text-slate-900">
                            <EditableCell
                              value={item.moreDetails && item.moreDetails.trim() !== "" ? item.moreDetails : "Click to add details"}
                              onSave={(newValue) => {
                                const cleanValue = newValue === "Click to add details" ? "" : newValue;
                                return updateQuotationItem(index, 'moreDetails', cleanValue);
                              }}
                              truncate
                              className="w-full text-left min-h-[20px] p-0.5"
                            />
                          </div>
                        </div>

                        {/* Profit against selling price — this line's gross margin: profit ÷
                            final selling price, the SAME per-line `marginPercent` the
                            Pricing Summary's "Profit against selling price" row totals from
                            (`resolveQuotationTotals`, BUSINESS_RULES#quotation-line-rate).
                            Deliberately NOT profit ÷ cost — that is Profit against cost price,
                            the realised markup,
                            a different number by design (prod ticket USC-BUG-0038 item
                            6). A percentage with nothing to divide by reads "—", never
                            0%, which would read as "priced at no margin". */}
                        <div className={`box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-[10px] bg-white border-r border-b border-solid border-[#D7DBD9] ${isLastRow ? 'rounded-br-md' : ''}`}>
                          <div className="font-['Public_Sans'] font-normal text-xs leading-[18px] flex items-center text-center text-slate-900">
                            <ItemProfitCell usd={lineProfitUsd} ttd={lineProfitTtd} basis="selling" />
                          </div>
                        </div>

                        {/* Competitor Markup % (D97): competitor price against OUR current unit cost,
                            from the one owner (utils/competitorSummary). Red when under the Settings
                            threshold; with no loaded threshold it is shown plain - never a guessed one. */}
                        {(() => {
                          const cmp = competitorMarkupPercent(item);
                          const below = isBelowCompetitorWarn(cmp, competitorWarnPercent);
                          return (
                            <div
                              data-testid="competitor-markup-cell"
                              data-below={below ? 'true' : 'false'}
                              className={`box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-[10px] border-r border-b border-solid border-[#D7DBD9] ${below ? 'bg-[#FDECEA] border-[#F3C6C0]' : 'bg-white'}`}
                              title={competitorWarnPercent != null ? `IFS/competitor price vs our current cost. Below ${competitorWarnPercent}% is flagged.` : 'IFS/competitor price vs our current cost.'}
                            >
                              <div className="font-['Public_Sans'] font-normal text-xs leading-[16px] text-center text-slate-900">
                                {cmp == null ? '—' : `${cmp.toFixed(1)}%`}
                              </div>
                            </div>
                          );
                        })()}
                        </QuotationGridRowCells>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center justify-end px-4 py-2 bg-white border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowAddItemModal(true)}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50 hover:border-slate-400 transition-colors"
                  title="Add a new item to this quotation"
                >
                  <span className="text-base leading-none">+</span> Add Item
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Tables - Sticky at Bottom (hidden in focus mode) */}
        {/* Extra right padding reserves the bottom-right corner for the FloatingAssistant
            (fixed bottom-6 right-6) so the Pricing Summary toggle isn't hidden behind it.
            (ClickUp 86d3t58ku) */}
        {!focusMode && (
        <div className="sticky bottom-0 bg-brand-bg border-t border-slate-200 pl-8 pr-24 py-2 shadow-lg z-10">
          {quotation && (() => {
            // Unichand's summary, from the one owner (see `quotationTotals` above):
            // profit is AFTER the discount and margin is over the FINAL selling price.
            const totalSellingPrice = quotationTotals.sellingBeforeDiscount;
            const totalCostPrice = quotationTotals.totalCost;
            const profit = quotationTotals.profit;
            // The SAME profit said in TT$: the TT$ selling less the TT$ cost this
            // page already shows, not the US$ profit converted a second time
            // (USC-BUG-0022 — see `quotationProfitTtd`). Both places that state
            // profit in TT$ read this one value.
            // `resolveProfitTtd` also states the TT$ percentages, from the TT$ figures.
            const profitTtdFigures = resolveProfitTtd(quotation.items, quotationTotals.netSellingPrice, ttdToUsdRate);
            const profitTtd = profitTtdFigures ? profitTtdFigures.profit : null;
            // Profit over COST — the rate this quote realised. Deliberately NOT the same
            // number as "Profit against selling price" (margin), which is over the final selling price; both are shown
            // and neither may be printed under the other's label. Both are the TT$
            // percentages (`shownProfitPercents`), printed beside US$ and TT$ alike.
            const shownPercents = shownProfitPercents(
              { ...quotationTotals, cost: quotationTotals.totalCost },
              profitTtdFigures
            );
            const profitPercentage = shownPercents.ofCost;
            const marginPercentage = shownPercents.ofSelling;
            const finalPrice = quotationTotals.finalPrice;

            // Competitor's summary. The "a line with no competitor price falls back to ours"
            // rule, and the reason an all-zero column totals zero instead of mirroring
            // Unichand's, live in resolveCompetitorSummary — read it before changing either.
            const {
              totalPrice: competitorTotalPrice,
              profit: competitorProfit,
              profitPercentage: competitorProfitPercentageUsd,
              marginPercentage: competitorMarginPercentageUsd,
              finalPrice: competitorFinalPrice,
              netSellingPrice: competitorNetSelling,
            } = resolveCompetitorSummary(quotation.items, {
              defaultMarkupPercent: markupPercentage,
              totalCostPrice,
              discountPercentage: competitorDiscountPercentage,
              ...chargesFromDoc({
                transportationCharges: competitorTransportationCharges,
                additionalCharges: competitorAdditionalCharges,
                customExpenses: quotation.competitorCustomExpenses,
              }),
            });

            // Always show competitor's total box
            const hasCompetitorPrices = true;
            // The competitor's TT$ profit, said the same way as ours: TT$ selling
            // less OUR TT$ cost, through the one owner. Null (no competitor price
            // anywhere) leaves the row at its plain conversion of a zero profit.
            const competitorProfitTtdFigures =
              competitorNetSelling == null
                ? null
                : resolveProfitTtd(quotation.items, competitorNetSelling, ttdToUsdRate);
            const competitorShownPercents = shownProfitPercents(
              {
                profitPercentOfCost: competitorProfitPercentageUsd,
                marginPercent: competitorMarginPercentageUsd,
                cost: totalCostPrice,
                netSellingPrice: competitorNetSelling ?? 0,
              },
              competitorProfitTtdFigures
            );
            const competitorProfitPercentage = competitorShownPercents.ofCost;
            const competitorMarginPercentage = competitorShownPercents.ofSelling;
            // The custom-expense rows each box shows: a "Customs Expense" row until one is stored (D159.1).
            const shownCompetitorExpenses = shownCustomExpenses(quotation.competitorCustomExpenses);
            const shownOurExpenses = shownCustomExpenses(quotation.customExpenses);

            return (
              <div>
                {/* Header with Toggle Button */}
                <div className="flex items-center justify-between px-0 py-2">
                  <div className="flex items-center flex-wrap gap-2 min-w-0">
                    <div className="text-slate-900 font-['Public_Sans'] font-semibold text-[13px] mr-1">Pricing Summary</div>
                    {/* The summary opens COLLAPSED, so a figure that only exists inside it is
                        a figure nobody reads. These three are the ones worth reading without
                        expanding anything — what the quote costs, what it sells for, and what
                        is left — led by the currency the business actually banks in (TT$),
                        with the US$ figure shown beside it rather than only on hover
                        (client, 2026-09-26).
                        Every one is the SAME value its row states below (`totalCost`,
                        `sellingBeforeDiscount`, `profit` off `quotationTotals`), and each
                        states its TT$ exactly once — the bar and the box read the same
                        number, and Profit (TT$) is Total Selling (TT$) less Total Cost
                        (TT$) by construction rather than by luck (USC-BUG-0022). Total Cost
                        carries the grid column's red tint because it is the same figure the
                        column totals; Profit keeps the box's own blue/orange sign colouring,
                        because a loss reading red-because-it-is-cost would be unreadable.
                        Hidden on the narrowest screens, where the header has no room. Two of
                        the three are still stated in the box below (Total Price, Profit);
                        Total Cost is NOT a row there, so below `sm` this chip is the only
                        place the quote's total cost is stated as a TOTAL — the grid's
                        "Total Cost Price (TTD)" column states it per line. Raised with the
                        owner on ClickUp 86d478wb9; adding a cost row to the box is a layout
                        call (the Competitor box beside it has no cost of its own), so it is
                        deliberately not done here rather than guessed at. */}
                    {(() => {
                      // `ttd` overrides the conversion for a row that has a better
                      // answer than one: Total Cost sums each line's own TT$
                      // (`sumCostTtd`), so a quotation priced off TT$ catalogue rows
                      // states their total exactly instead of restating the US$ sum
                      // and landing a cent or two above the column it heads.
                      const chips: { label: string; usd: number; ttd?: number | null; tint: string; title: string }[] = [
                        {
                          label: 'Total Cost',
                          usd: totalCostPrice,
                          ttd: sumCostTtd(quotation.items, ttdToUsdRate),
                          tint: 'bg-[#FDECEA] border-[#F3C6C0] text-slate-900',
                          title: 'What these lines cost USC to buy',
                        },
                        {
                          label: 'Total Selling',
                          usd: totalSellingPrice,
                          tint: 'bg-white border-[#D7DBD9] text-slate-900',
                          title: 'What the lines sell for before any discount — the Total Price row below',
                        },
                        {
                          label: 'Profit',
                          usd: profit,
                          ttd: profitTtd,
                          tint: `bg-white border-[#D7DBD9] ${profit >= 0 ? 'text-primary-500' : 'text-[#CC4B13]'}`,
                          title: 'Final selling less cost, after the discount',
                        },
                      ];
                      return chips.map(({ label, usd, ttd: ttdOverride, tint, title }) => {
                        const ttd = ttdOverride !== undefined ? ttdOverride : usdToTtd(usd, ttdToUsdRate);
                        return (
                          <span
                            key={label}
                            className={`hidden sm:inline-flex items-center gap-1.5 px-2 py-[2px] rounded-md border font-['Public_Sans'] text-xs whitespace-nowrap cursor-help ${tint}`}
                            title={`${title}. TT$ at the quotation's locked rate; US$ beside it.`}
                          >
                            <span className="font-medium text-slate-600">{label}</span>
                            <span className="font-semibold">
                              {ttd != null ? `${TTD_LABEL}${ttd.toFixed(2)}` : `${TTD_LABEL} —`}
                            </span>
                            {/* US$ visible, not hover-only (client 2026-09-26). Muted,
                                except a loss: then it inherits the chip's loss colour
                                like the TT$ figure, so a loss never reads grey. */}
                            <span className={usd < 0 ? undefined : 'text-slate-500'}>{USD_LABEL}{usd.toFixed(2)}</span>
                          </span>
                        );
                      });
                    })()}
                  </div>
                  <button
                    onClick={() => setSummaryCollapsed(!summaryCollapsed)}
                    className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-gray-300 transition-colors"
                    title={summaryCollapsed ? 'Expand summary' : 'Collapse summary'}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={`transition-transform ${summaryCollapsed ? '-rotate-90' : ''}`}
                    >
                      <path
                        d="M10.0001 12.4788C9.88897 12.4788 9.7848 12.4615 9.68758 12.4268C9.59036 12.392 9.50008 12.333 9.41675 12.2497L5.58341 8.41634C5.43064 8.26356 5.35425 8.06912 5.35425 7.83301C5.35425 7.5969 5.43064 7.40245 5.58341 7.24967C5.73619 7.0969 5.93064 7.02051 6.16675 7.02051C6.40286 7.02051 6.5973 7.0969 6.75008 7.24967L10.0001 10.4997L13.2501 7.24967C13.4029 7.0969 13.5973 7.02051 13.8334 7.02051C14.0695 7.02051 14.264 7.0969 14.4167 7.24967C14.5695 7.40245 14.6459 7.5969 14.6459 7.83301C14.6459 8.06912 14.5695 8.26356 14.4167 8.41634L10.5834 12.2497C10.5001 12.333 10.4098 12.392 10.3126 12.4268C10.2154 12.4615 10.1112 12.4788 10.0001 12.4788Z"
                        fill="#222222"
                      />
                    </svg>
                  </button>
                </div>

                {/* Summary Content - Collapsible */}
                {!summaryCollapsed && (
                  <div className="flex flex-col gap-3 animate-in fade-in duration-200">
                  <div className="flex justify-end gap-3">
                    {/* Competitor's Total Box - independent from Unichand's */}
                    {hasCompetitorPrices && (
                      <div className="flex flex-col" style={{ position: 'relative' }}>
                        <div className="text-slate-900 font-['Public_Sans'] font-semibold text-[13px] mb-1">Competitor's Total</div>

                        <div className="flex flex-col w-full max-w-[432px] min-w-0" style={{ maxHeight: '168px', overflowY: 'auto' }}>
                          {/* Row: Total Price */}
                          <div className="group flex flex-row min-h-[28px]">
                            <div className="w-6 flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ borderWidth: '1px 0 0 1px', borderStyle: 'solid', borderColor: '#D7DBD9' }}>
                              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddCompetitorCustomExpense(0); }} className="opacity-0 group-hover:opacity-100 flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#D7E4DD] hover:bg-[#c5d9cc] transition-opacity" aria-label="Add row below" title="Add custom expense below">
                                <svg width="10.5" height="10.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 5H13V11H19V13H13V19H11V13H5V11H11V5Z" fill="#222222" /></svg>
                              </button>
                            </div>
                            <div className="flex justify-center items-center w-[126px] min-w-0 shrink overflow-hidden bg-[#F9FAF9] rounded-tl-md" style={{ padding: '4px 8px', borderWidth: '1px 1px 0 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}><span className="text-slate-900 font-['Public_Sans'] font-medium text-xs truncate">Total Price</span></div>
                            <div className="flex justify-center items-center flex-1 min-w-0 overflow-hidden bg-white" style={{ padding: '4px 8px', borderWidth: '1px 1px 0 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}><span className="text-slate-900 font-['Public_Sans'] text-xs truncate">{USD_LABEL}{competitorTotalPrice.toFixed(2)}</span></div>
                            <SummaryTtdCell usd={competitorTotalPrice} ttdToUsd={ttdToUsdRate} first rounded="tr" />
                          </div>
                          {/* Partition: clear separator between Total Price and Discount */}
                          <div className="group flex flex-row min-h-[28px] border-t-2 border-slate-300">
                            <div className="w-6 flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ borderWidth: '0 0 1px 1px', borderStyle: 'solid', borderColor: '#D7DBD9' }}>
                              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddCompetitorCustomExpense(0); }} className="opacity-0 group-hover:opacity-100 flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#D7E4DD] hover:bg-[#c5d9cc] transition-opacity" aria-label="Add row below" title="Add custom expense below">
                                <svg width="10.5" height="10.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 5H13V11H19V13H13V19H11V13H5V11H11V5Z" fill="#222222" /></svg>
                              </button>
                            </div>
                            <div className="flex justify-center items-center w-[126px] min-w-0 shrink overflow-hidden bg-[#F9FAF9]" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}><span className="text-slate-900 font-['Public_Sans'] font-medium text-xs truncate">Discount</span></div>
                            <div className="flex justify-center items-center flex-1 min-w-0 overflow-hidden bg-white" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}>
                              <EditableCell value={competitorDiscountPercentage} type="number" onSave={async (v) => { const n = typeof v === 'number' ? v : parseFloat(String(v)) || 0; const c = Math.max(0, Math.min(100, n)); setCompetitorDiscountPercentage(c); await updateQuotationField('competitorDiscountPercentage', c); }} className="text-slate-900 font-['Public_Sans'] text-center text-xs" suffix="%" />
                            </div>
                            <SummaryTtdCell usd={null} ttdToUsd={ttdToUsdRate} title="A percentage has no currency" />
                          </div>
                          <div className="group flex flex-row min-h-[28px]">
                            <div className="w-6 flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ borderWidth: '0 0 1px 1px', borderStyle: 'solid', borderColor: '#D7DBD9' }}>
                              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddCompetitorCustomExpense(0); }} className="opacity-0 group-hover:opacity-100 flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#D7E4DD] hover:bg-[#c5d9cc] transition-opacity" aria-label="Add row below" title="Add custom expense below">
                                <svg width="10.5" height="10.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 5H13V11H19V13H13V19H11V13H5V11H11V5Z" fill="#222222" /></svg>
                              </button>
                            </div>
                            <div className="flex justify-center items-center w-[126px] min-w-0 shrink overflow-hidden bg-[#F9FAF9]" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}><span className="text-slate-900 font-['Public_Sans'] font-medium text-xs text-center leading-tight" title="Profit as a % of the cost price (the markup realised, after the discount)">Profit against cost price</span></div>
                            <div className={`flex justify-center items-center flex-1 min-w-0 overflow-hidden bg-white font-['Public_Sans'] text-xs ${competitorProfit >= 0 ? 'text-primary-500' : 'text-[#CC4B13]'}`} style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}><span className="truncate" title={`${USD_LABEL}${competitorProfit.toFixed(2)} — profit against cost price ${competitorProfitPercentage.toFixed(1)}%`}>{USD_LABEL}{competitorProfit.toFixed(2)} ({competitorProfitPercentage.toFixed(1)}%)</span></div>
                            <SummaryTtdCell usd={competitorProfit} {...(competitorProfitTtdFigures ? { ttd: competitorProfitTtdFigures.profit, percent: competitorProfitTtdFigures.profitPercentOfCost } : {})} ttdToUsd={ttdToUsdRate} className={competitorProfit >= 0 ? 'text-primary-500' : 'text-[#CC4B13]'} title="TT$ profit ÷ TT$ cost" />
                          </div>
                          <div className="group flex flex-row min-h-[28px]">
                            <div className="w-6 flex-shrink-0 overflow-hidden" style={{ borderWidth: '0 0 1px 1px', borderStyle: 'solid', borderColor: '#D7DBD9' }} />
                            <div className="flex justify-center items-center w-[126px] min-w-0 shrink overflow-hidden bg-[#F9FAF9]" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}><span className="text-slate-900 font-['Public_Sans'] font-semibold text-xs text-center leading-tight" title="Profit as a % of the final selling price (gross margin)">Profit against selling price</span></div>
                            <div className={`flex justify-center items-center flex-1 min-w-0 overflow-hidden bg-white font-['Public_Sans'] font-semibold text-xs ${competitorMarginPercentage >= 0 ? 'text-primary-500' : 'text-[#CC4B13]'}`} style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}><span className="truncate" title="Profit ÷ selling price">{competitorMarginPercentage.toFixed(1)}%</span></div>
                            <SummaryTtdCell usd={null} percent={competitorProfitTtdFigures?.marginPercent} ttdToUsd={ttdToUsdRate} className={`font-semibold ${(competitorProfitTtdFigures?.marginPercent ?? 0) >= 0 ? 'text-primary-500' : 'text-[#CC4B13]'}`} title="TT$ profit ÷ TT$ final selling price" />
                          </div>
                          {shownCompetitorExpenses.rows.map((exp, idx) => (
                            <div key={`c-row-${idx}`} className="group flex flex-row min-h-[28px]">
                              <div className="w-6 flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ borderWidth: '0 0 1px 1px', borderStyle: 'solid', borderColor: '#D7DBD9' }}>
                                <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddCompetitorCustomExpense(idx + 1); }} className="opacity-0 group-hover:opacity-100 flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#D7E4DD] hover:bg-[#c5d9cc] transition-opacity" aria-label="Add row below" title="Add custom expense below">
                                  <svg width="10.5" height="10.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 5H13V11H19V13H13V19H11V13H5V11H11V5Z" fill="#222222" /></svg>
                                </button>
                              </div>
                              <div className="flex justify-center items-center w-[126px] min-w-0 shrink overflow-hidden bg-[#F9FAF9]" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}>
                                <EditableCell value={exp.label} type="text" onSave={async (v) => { await handleUpdateCompetitorCustomExpense(idx, { label: String(v) }); }} truncate className="text-slate-900 font-['Public_Sans'] font-medium text-center text-xs w-full max-w-full" />
                              </div>
                              <div className="flex justify-center items-center flex-1 min-w-0 overflow-hidden bg-white gap-2" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}>
                                <EditableCell value={exp.amount || 0} type="number" onSave={async (v) => { await handleUpdateCompetitorCustomExpense(idx, { amount: typeof v === 'number' ? v : parseFloat(String(v)) || 0 }); }} className="text-slate-900 font-['Public_Sans'] text-center text-xs" prefix={USD_LABEL} />
                                {!shownCompetitorExpenses.unstored && (
                                  <button type="button" onClick={async () => { if (await confirm({ title: 'Remove this custom expense?', tone: 'error', confirmLabel: 'Remove' })) handleRemoveCompetitorCustomExpense(idx); }} title="Remove" className="text-red-600 hover:text-red-800 shrink-0" aria-label="Remove">✕</button>
                                )}
                              </div>
                              <SummaryTtdCell usd={exp.amount || 0} ttdToUsd={ttdToUsdRate} />
                            </div>
                          ))}
                          <div className="group flex flex-row min-h-[28px]">
                            <div className="w-6 flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ borderWidth: '0 0 1px 1px', borderStyle: 'solid', borderColor: '#D7DBD9' }}>
                              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddCompetitorCustomExpense(shownCompetitorExpenses.rows.length); }} className="opacity-0 group-hover:opacity-100 flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#D7E4DD] hover:bg-[#c5d9cc] transition-opacity" aria-label="Add row below" title="Add custom expense below">
                                <svg width="10.5" height="10.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 5H13V11H19V13H13V19H11V13H5V11H11V5Z" fill="#222222" /></svg>
                              </button>
                            </div>
                            <div className="flex justify-center items-center w-[126px] min-w-0 shrink overflow-hidden bg-[#F9FAF9]" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}><span className="text-slate-900 font-['Public_Sans'] font-medium text-xs truncate">Additional Charges</span></div>
                            <div className="flex justify-center items-center flex-1 min-w-0 overflow-hidden bg-white" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}>
                              <EditableCell value={competitorAdditionalCharges} type="number" onSave={async (v) => { const n = typeof v === 'number' ? v : parseFloat(String(v)) || 0; setCompetitorAdditionalCharges(Math.max(0, n)); await updateQuotationField('competitorAdditionalCharges', Math.max(0, n)); }} className="text-slate-900 font-['Public_Sans'] text-center text-xs" prefix={USD_LABEL} />
                            </div>
                            <SummaryTtdCell usd={competitorAdditionalCharges} ttdToUsd={ttdToUsdRate} />
                          </div>
                          <div className="group flex flex-row min-h-[28px]">
                            <div className="w-6 flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ borderWidth: '0 0 1px 1px', borderStyle: 'solid', borderColor: '#D7DBD9', borderRadius: '0 0 0 6px' }}>
                              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddCompetitorCustomExpense(shownCompetitorExpenses.rows.length); }} className="opacity-0 group-hover:opacity-100 flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#D7E4DD] hover:bg-[#c5d9cc] transition-opacity" aria-label="Add row below" title="Add custom expense below">
                                <svg width="10.5" height="10.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 5H13V11H19V13H13V19H11V13H5V11H11V5Z" fill="#222222" /></svg>
                              </button>
                            </div>
                            <div className="flex justify-center items-center w-[126px] min-w-0 shrink overflow-hidden bg-[#F9FAF9]" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9', borderRadius: '0 0 0 6px' }}><span className="text-slate-900 font-['Public_Sans'] font-medium text-xs truncate">Final Price</span></div>
                            <div className="flex justify-center items-center flex-1 min-w-0 overflow-hidden bg-white" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}><span className="text-slate-900 font-['Public_Sans'] text-xs truncate">{USD_LABEL}{competitorFinalPrice.toFixed(2)}</span></div>
                            <SummaryTtdCell usd={competitorFinalPrice} ttdToUsd={ttdToUsdRate} rounded="br" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Unichand's Total Box - independent from Competitor's; plus on hover per row */}
                    <div className="flex flex-col">
                      <div className="text-slate-900 font-['Public_Sans'] font-semibold text-[13px] mb-1">Our Total</div>

                      <div className="flex flex-col w-full max-w-[432px] min-w-0" style={{ maxHeight: '168px', overflowY: 'auto' }}>
                        <div className="group flex flex-row min-h-[28px]">
                          <div className="w-6 flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ borderWidth: '1px 0 0 1px', borderStyle: 'solid', borderColor: '#D7DBD9' }}>
                            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddCustomExpense(0); }} className="opacity-0 group-hover:opacity-100 flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#D7E4DD] hover:bg-[#c5d9cc] transition-opacity" aria-label="Add row below" title="Add custom expense below">
                              <svg width="10.5" height="10.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 5H13V11H19V13H13V19H11V13H5V11H11V5Z" fill="#222222" /></svg>
                            </button>
                          </div>
                          <div className="flex justify-center items-center w-[126px] min-w-0 shrink overflow-hidden bg-[#F9FAF9] rounded-tl-md" style={{ padding: '4px 8px', borderWidth: '1px 1px 0 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}><span className="text-slate-900 font-['Public_Sans'] font-medium text-xs truncate">Total Price</span></div>
                          <div className="flex justify-center items-center flex-1 min-w-0 overflow-hidden bg-white" style={{ padding: '4px 8px', borderWidth: '1px 1px 0 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}><span className="text-slate-900 font-['Public_Sans'] text-xs truncate">{USD_LABEL}{totalSellingPrice.toFixed(2)}</span></div>
                          <SummaryTtdCell usd={totalSellingPrice} ttdToUsd={ttdToUsdRate} first rounded="tr" />
                        </div>
                        {/* Partition: clear separator between Total Price and Discount */}
                        <div className="group flex flex-row min-h-[28px] border-t-2 border-slate-300">
                          <div className="w-6 flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ borderWidth: '0 0 1px 1px', borderStyle: 'solid', borderColor: '#D7DBD9' }}>
                            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddCustomExpense(0); }} className="opacity-0 group-hover:opacity-100 flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#D7E4DD] hover:bg-[#c5d9cc] transition-opacity" aria-label="Add row below" title="Add custom expense below">
                              <svg width="10.5" height="10.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 5H13V11H19V13H13V19H11V13H5V11H11V5Z" fill="#222222" /></svg>
                            </button>
                          </div>
                          <div className="flex justify-center items-center w-[126px] min-w-0 shrink overflow-hidden bg-[#F9FAF9]" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}><span className="text-slate-900 font-['Public_Sans'] font-medium text-xs truncate">Discount</span></div>
                          <div className="flex justify-center items-center flex-1 min-w-0 overflow-hidden bg-white" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}>
                            <EditableCell value={discountPercentage} type="number" onSave={async (v) => { const n = typeof v === 'number' ? v : parseFloat(String(v)) || 0; const c = Math.max(0, Math.min(100, n)); setDiscountPercentage(c); await updateQuotationField('discountPercentage', c); }} className="text-slate-900 font-['Public_Sans'] text-center text-xs" suffix="%" />
                          </div>
                          <SummaryTtdCell usd={null} ttdToUsd={ttdToUsdRate} title="A percentage has no currency" />
                        </div>
                        <div className="group flex flex-row min-h-[28px]">
                          <div className="w-6 flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ borderWidth: '0 0 1px 1px', borderStyle: 'solid', borderColor: '#D7DBD9' }}>
                            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddCustomExpense(0); }} className="opacity-0 group-hover:opacity-100 flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#D7E4DD] hover:bg-[#c5d9cc] transition-opacity" aria-label="Add row below" title="Add custom expense below">
                              <svg width="10.5" height="10.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 5H13V11H19V13H13V19H11V13H5V11H11V5Z" fill="#222222" /></svg>
                            </button>
                          </div>
                          <div className="flex justify-center items-center w-[126px] min-w-0 shrink overflow-hidden bg-[#F9FAF9]" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}><span className="text-slate-900 font-['Public_Sans'] font-medium text-xs text-center leading-tight" title="Profit as a % of the cost price (the markup realised, after the discount)">Profit against cost price</span></div>
                          <div className={`flex justify-center items-center flex-1 min-w-0 overflow-hidden bg-white font-['Public_Sans'] text-xs ${profit >= 0 ? 'text-primary-500' : 'text-[#CC4B13]'}`} style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}><span className="truncate" title={`${USD_LABEL}${profit.toFixed(2)} — profit against cost price ${profitPercentage.toFixed(1)}%`}>{USD_LABEL}{profit.toFixed(2)} ({profitPercentage.toFixed(1)}%)</span></div>
                          <SummaryTtdCell usd={profit} ttd={profitTtd} percent={profitTtdFigures?.profitPercentOfCost} ttdToUsd={ttdToUsdRate} className={profit >= 0 ? 'text-primary-500' : 'text-[#CC4B13]'} title="TT$ profit ÷ TT$ cost" />
                        </div>
                        <div className="group flex flex-row min-h-[28px]">
                          <div className="w-6 flex-shrink-0 overflow-hidden" style={{ borderWidth: '0 0 1px 1px', borderStyle: 'solid', borderColor: '#D7DBD9' }} />
                          <div className="flex justify-center items-center w-[126px] min-w-0 shrink overflow-hidden bg-[#F9FAF9]" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}><span className="text-slate-900 font-['Public_Sans'] font-semibold text-xs text-center leading-tight" title="Profit as a % of the final selling price (gross margin)">Profit against selling price</span></div>
                          <div className={`flex justify-center items-center flex-1 min-w-0 overflow-hidden bg-white font-['Public_Sans'] font-semibold text-xs ${marginPercentage >= 0 ? 'text-primary-500' : 'text-[#CC4B13]'}`} style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}><span className="truncate" title="Profit ÷ selling price">{marginPercentage.toFixed(1)}%</span></div>
                          <SummaryTtdCell usd={null} percent={profitTtdFigures?.marginPercent} ttdToUsd={ttdToUsdRate} className={`font-semibold ${(profitTtdFigures?.marginPercent ?? 0) >= 0 ? 'text-primary-500' : 'text-[#CC4B13]'}`} title="TT$ profit ÷ TT$ final selling price" />
                        </div>
                        {/* Only when "Spread charges into prices" has built charges into the
                            prices (client meeting 2026-09-25): how much, and the profit without it.
                            Internal screen only. The Profit rows above stay as they are. */}
                        <AbsorbedChargesRows absorbed={quotation.absorbedCharges} totals={quotationTotals} profitTtd={profitTtdFigures} ttdToUsd={ttdToUsdRate} />
                        {shownOurExpenses.rows.map((exp, idx) => (
                          <div key={`u-row-${idx}`} className="group flex flex-row min-h-[28px]">
                            <div className="w-6 flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ borderWidth: '0 0 1px 1px', borderStyle: 'solid', borderColor: '#D7DBD9' }}>
                              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddCustomExpense(idx + 1); }} className="opacity-0 group-hover:opacity-100 flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#D7E4DD] hover:bg-[#c5d9cc] transition-opacity" aria-label="Add row below" title="Add custom expense below">
                                <svg width="10.5" height="10.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 5H13V11H19V13H13V19H11V13H5V11H11V5Z" fill="#222222" /></svg>
                              </button>
                            </div>
                            <div className="flex justify-center items-center w-[126px] min-w-0 shrink overflow-hidden bg-[#F9FAF9]" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}>
                              <EditableCell value={exp.label} type="text" onSave={async (v) => { await handleUpdateCustomExpense(idx, { label: String(v) }); }} truncate className="text-slate-900 font-['Public_Sans'] font-medium text-center text-xs w-full max-w-full" />
                            </div>
                            <div className="flex justify-center items-center flex-1 min-w-0 overflow-hidden bg-white gap-2" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}>
                              <EditableCell value={exp.amount || 0} type="number" onSave={async (v) => { await handleUpdateCustomExpense(idx, { amount: typeof v === 'number' ? v : parseFloat(String(v)) || 0 }); }} className="text-slate-900 font-['Public_Sans'] text-center text-xs" prefix={USD_LABEL} />
                              {!shownOurExpenses.unstored && (
                                <button type="button" onClick={async () => { if (await confirm({ title: 'Remove this custom expense?', tone: 'error', confirmLabel: 'Remove' })) handleRemoveCustomExpense(idx); }} title="Remove" className="text-red-600 hover:text-red-800 shrink-0" aria-label="Remove">✕</button>
                              )}
                            </div>
                            <SummaryTtdCell usd={exp.amount || 0} ttdToUsd={ttdToUsdRate} />
                          </div>
                        ))}
                        <div className="group flex flex-row min-h-[28px]">
                          <div className="w-6 flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ borderWidth: '0 0 1px 1px', borderStyle: 'solid', borderColor: '#D7DBD9' }}>
                            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddCustomExpense(shownOurExpenses.rows.length); }} className="opacity-0 group-hover:opacity-100 flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#D7E4DD] hover:bg-[#c5d9cc] transition-opacity" aria-label="Add row below" title="Add custom expense below">
                              <svg width="10.5" height="10.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 5H13V11H19V13H13V19H11V13H5V11H11V5Z" fill="#222222" /></svg>
                            </button>
                          </div>
                          <div className="flex justify-center items-center w-[126px] min-w-0 shrink overflow-hidden bg-[#F9FAF9]" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}><span className="text-slate-900 font-['Public_Sans'] font-medium text-xs truncate">Additional Charges</span></div>
                          <div className="flex justify-center items-center flex-1 min-w-0 overflow-hidden bg-white" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}>
                            <EditableCell value={additionalCharges} type="number" onSave={async (v) => { const n = typeof v === 'number' ? v : parseFloat(String(v)) || 0; setAdditionalCharges(Math.max(0, n)); await updateQuotationField('additionalCharges', Math.max(0, n)); }} className="text-slate-900 font-['Public_Sans'] text-center text-xs" prefix={USD_LABEL} />
                          </div>
                          <SummaryTtdCell usd={additionalCharges} ttdToUsd={ttdToUsdRate} />
                        </div>
                        <div className="group flex flex-row min-h-[28px]">
                          <div className="w-6 flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ borderWidth: '0 0 1px 1px', borderStyle: 'solid', borderColor: '#D7DBD9', borderRadius: '0 0 0 6px' }}>
                            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddCustomExpense(shownOurExpenses.rows.length); }} className="opacity-0 group-hover:opacity-100 flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#D7E4DD] hover:bg-[#c5d9cc] transition-opacity" aria-label="Add row below" title="Add custom expense below">
                              <svg width="10.5" height="10.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 5H13V11H19V13H13V19H11V13H5V11H11V5Z" fill="#222222" /></svg>
                            </button>
                          </div>
                          <div className="flex justify-center items-center w-[126px] min-w-0 shrink overflow-hidden bg-[#F9FAF9]" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9', borderRadius: '0 0 0 6px' }}><span className="text-slate-900 font-['Public_Sans'] font-medium text-xs truncate">Final Price</span></div>
                          <div className="flex justify-center items-center flex-1 min-w-0 overflow-hidden bg-white" style={{ padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' }}><span className="text-slate-900 font-['Public_Sans'] text-xs truncate">{USD_LABEL}{finalPrice.toFixed(2)}</span></div>
                          <SummaryTtdCell usd={finalPrice} ttdToUsd={ttdToUsdRate} rounded="br" />
                        </div>
                      </div>
                    </div>
                  </div>
                    {(() => {
                      // D170.5 — see `runChargeSpread`. Greyed out with the reason as its title.
                      const locked = spreadLockedReason(quotation.status, !!confirmedOrder);
                      const spreadBlocked = locked ?? (quotationTotals.charges <= 0 ? 'There are no charges to spread.' : null);
                      return (
                        <div className="flex justify-end items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => void runChargeSpread('spread')}
                            loading={absorbingCharges}
                            disabled={!!spreadBlocked}
                            title={spreadBlocked ?? 'Build these charges into the line selling prices by line value; the Final Price does not change'}
                          >
                            Spread charges into prices
                          </Button>
                          {quotation.chargeSpread && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => void runChargeSpread('undo')}
                              loading={absorbingCharges}
                              disabled={!!locked}
                              title={locked ?? 'Put the charge rows and line prices back as they were before the spread'}
                            >
                              Undo spread
                            </Button>
                          )}
                          <span className="font-['Public_Sans'] text-sm leading-[22px] text-slate-600">
                            Builds Additional Charges / Custom Expenses into the line prices by line value; the Final Price does not change.
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        )}
      </div>

      {/* Client View Popup */}
      {quotation && (
        <ClientViewPopup
          quotation={quotation}
          vesselName={vesselName}
          isOpen={showClientView}
          onClose={() => setShowClientView(false)}
          discountPercentage={discountPercentage}
          markupPercentage={markupPercentage}
          totals={quotationTotals}
          onSendClick={() => {
            setShowClientView(false);
            openSendQuote();
          }}
        />
      )}

      {/* Send Quotation Modal */}
      {quotation && (
        <SendQuotationModal
          isOpen={showSendModal}
          onClose={() => setShowSendModal(false)}
          quotation={quotation}
          vesselName={vesselName}
          onSend={handleSend}
        />
      )}

      {/* RFQ-unit-mismatch nudge (owner decision 2026-09-24): shown by
          `openSendQuote` instead of opening SendQuotationModal directly, only
          while there is at least one mismatched line. "Send anyway" opens
          SendQuotationModal exactly as `openSendQuote` would have with no
          mismatches; "Go fix" only scrolls (to the next mismatched line each press), it never touches a line itself. */}
      <RfqUnitMismatchDialog
        isOpen={unitMismatchDialogLines !== null}
        entries={unitMismatchDialogLines || []}
        lastGoneTo={lastUnitFixLine}
        reviewEntries={reviewDialogLines || []}
        onClose={() => {
          setUnitMismatchDialogLines(null);
          setReviewDialogLines(null);
        }}
        onGoFix={() => {
          const mismatches = unitMismatchDialogLines || [];
          const firstReview = reviewDialogLines?.[0];
          setUnitMismatchDialogLines(null);
          setReviewDialogLines(null);
          if (mismatches.length === 0) {
            if (firstReview) scrollToQuotationLine(firstReview.lineNumber);
            return;
          }
          const next = nextMismatchLine(mismatches, lastUnitFixLine);
          if (next != null) {
            setLastUnitFixLine(next);
            scrollToQuotationLine(next);
          }
        }}
        onSendAnyway={() => {
          setUnitMismatchDialogLines(null);
          setReviewDialogLines(null);
          setShowSendModal(true);
        }}
      />

      {/* Item lookup — the merged catalogue+supplier panel. Opened from the
          row's magnifier only (owner decisions 2026-09-24 moved the Supplier
          cell to its own read-only `SupplierDetailsCard` instead — see
          `SupplierCell`'s header comment); which line stays here, same as the
          two popups this replaces both kept it here before the merge. */}
      <ItemLookupPanel
        isOpen={lookupPanelOpenIndex !== null}
        onClose={() => setLookupPanelOpenIndex(null)}
        onSelectCatalogue={handleSearchSelect}
        onSelectSupplier={(option, searchTerm) => {
          if (lookupPanelOpenIndex !== null) handleSupplierSelect(lookupPanelOpenIndex, option, searchTerm);
        }}
        quotationUsdToTtd={ttdToUsdRate ? 1 / ttdToUsdRate : null}
        vatRatePercent={vatRatePercent}
        lineUom={lookupPanelOpenIndex !== null ? quotation.items[lookupPanelOpenIndex]?.uom : null}
        // Which ONE row the line was actually priced from — never the vendor
        // name (owner, 2026-09-24: see `ItemLookupPanel`'s own doc comment on
        // this prop and `itemLookupRows.pricedFromIdentity`).
        pricingDetails={lookupPanelOpenIndex !== null ? quotation.items[lookupPanelOpenIndex]?.pricingDetails : null}
        quotationId={quotation._id}
        triggerRef={lookupTriggerRef as React.RefObject<HTMLElement>}
        // Open on the line's own item, already searched. The magnifier is only
        // ever pressed FROM a row, and the thing being looked for is the name
        // printed two inches to its right — so asking the user to retype it
        // was the whole of the friction.
        //
        // `itemName` before `itemDescription`: the catalogue's own names come
        // from `cleanItemName`, so the cleaned name is what the server's `$text`
        // index actually holds. That search is OR-across-terms ranked by
        // textScore, so a long name still returns its best matches rather than
        // nothing.
        initialQuery={lookupPanelOpenIndex !== null ? lineCatalogueName(quotation.items[lookupPanelOpenIndex]) : ''}
        onAddToCatalogue={handleAddToCatalogueFromLookup}
      />

      {/* D107: the same popup, opened straight from a vendor-less line's cost cell (or right
          after a manual Add Item) - the vendor is required, and the save prices the line. */}
      {catalogueAddIndex !== null && quotation.items[catalogueAddIndex] && (
        <AddLineToCatalogueModal
          itemName={lineCatalogueName(quotation.items[catalogueAddIndex])}
          uom={quotation.items[catalogueAddIndex].uom}
          onClose={() => setCatalogueAddIndex(null)}
          onSubmit={handleAddToCatalogueFromLookup}
        />
      )}

      {/* While a pick from the panel above is being applied/saved (owner
          decisions 2026-09-24): a sibling overlay, not a wrapper, because
          `ItemLookupPanel` renders its own `fixed inset-0` in place rather
          than through a portal — a higher z-index sitting after it in the
          tree covers it exactly, with no change to that file. Blocks clicks
          (`pointer-events-auto`) so a second pick cannot be started while
          this one is in flight, on top of the `savingPickIndex` guards in
          `handleSearchSelect` / `handleSupplierSelect` themselves. */}
      {/* Not tied to the lookup panel being open: every VAT-answer route
          closes the panel first, and the pick it then applies still needs
          the in-flight signal. */}
      {savingPickIndex !== null && (
        <div
          className="fixed inset-0 z-[75] flex items-center justify-center bg-slate-900/20 pointer-events-auto"
          aria-live="polite"
          aria-label="Applying the picked price…"
        >
          <div className="flex items-center gap-2 rounded-md bg-white px-4 py-2.5 text-xs font-medium text-slate-700 shadow-lg">
            <span
              className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600"
              aria-hidden="true"
            />
            Applying the new price…
          </div>
        </div>
      )}

      {/* "Does this item have VAT?" — one popup, rendered once, the same
          "one open at a time across the whole table" shape as the lookup
          panel above it. THREE states can open it, never more than one at
          once (each entry point only ever opens its own): `pendingCataloguePick`
          / `pendingSupplierVatPick` (a same-session pick not yet written) or
          `vatPrompt` (a line already written — the "VAT?" badge). Whichever
          pending state is set takes the Yes/No handlers; `vatPrompt`
          is the fallback once neither pending state is. Only `vatPrompt` (a
          saved line, closing writes nothing) can be cancelled; a pending pick
          has no Cancel, X, Escape or backdrop (D101.1). */}
      <CatalogueVatPrompt
        isOpen={vatPrompt !== null || pendingCataloguePick !== null || pendingSupplierVatPick !== null}
        itemName={
          pendingCataloguePick?.item.item_name ||
          pendingSupplierVatPick?.supplierData.description ||
          vatPrompt?.itemName ||
          ''
        }
        vatRatePercent={vatRatePercent}
        saving={savingVatPrompt}
        onYes={() =>
          pendingCataloguePick
            ? handlePendingCataloguePickAnswer(true)
            : pendingSupplierVatPick
              ? handlePendingSupplierVatPickAnswer(true)
              : handleCatalogueVatAnswer(true)
        }
        onNo={() =>
          pendingCataloguePick
            ? handlePendingCataloguePickAnswer(false)
            : pendingSupplierVatPick
              ? handlePendingSupplierVatPickAnswer(false)
              : handleCatalogueVatAnswer(false)
        }
        onCancel={pendingCataloguePick || pendingSupplierVatPick ? undefined : handleCatalogueVatCancel}
      />

      {/* Add Item Modal */}
      <AddQuotationItemModal
        isOpen={showAddItemModal}
        onClose={() => setShowAddItemModal(false)}
        onAdd={handleAddManualItem}
        defaultMarkupPct={markupPercentage}
      />

      {(quotation.revisionNumber ?? 0) > 0 && (
        <RevisionCompareModal
          isOpen={showCompareModal}
          onClose={() => setShowCompareModal(false)}
          quotationId={quotation._id}
          defaultMarkupPct={markupPercentage}
        />
      )}

      {/* Confirm Order failure. `ErrorModal` is the pattern the rest of the app
          already uses for a refused action (RoleManagement, UserManagement);
          success goes to the toast provider. Neither is new. */}
      <ErrorModal
        isOpen={confirmOrderError !== null}
        title="Could not confirm order"
        message={confirmOrderError || ''}
        onClose={() => setConfirmOrderError(null)}
      />

      {/* Send refused before the form opened (D105.2, D105.7: no, US$0.00 or a negative selling price). The same dialog the
          page uses for a refused Confirm order, so the lines to fix stay on screen until read. */}
      <ErrorModal
        isOpen={sendRefusal !== null}
        title="Could not send quotation"
        message={sendRefusal || ''}
        onClose={() => setSendRefusal(null)}
      />

      {/* Freezing the quotation files its unpriced lines into the Master
          Catalogue. When some of them will not file, that is reported here
          rather than left in the server log — the status change itself
          succeeded, so nothing else on this screen would say so. */}
      <ErrorModal
        isOpen={catalogueHarvestError !== null}
        title="Some items were not added to the Master Catalogue"
        message={catalogueHarvestError || ''}
        onClose={() => setCatalogueHarvestError(null)}
      />

    </div>
  );
};
